import * as THREE from 'three';

// ── helpers ──────────────────────────────────────────────────────────────────
function hexRgb(hex) {
  const n = parseInt((hex || '#888').replace('#', ''), 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
}
function rgbStr(r, g, b) {
  return `rgb(${Math.min(255,Math.max(0,r|0))},${Math.min(255,Math.max(0,g|0))},${Math.min(255,Math.max(0,b|0))})`;
}
function jitter(hex, amt) {
  const { r, g, b } = hexRgb(hex);
  const v = (Math.random() - 0.5) * amt;
  return rgbStr(r + v, g + v, b + v);
}
function tex(canvas, repeatX, repeatY) {
  const t = new THREE.CanvasTexture(canvas);
  t.wrapS = t.wrapT = THREE.RepeatWrapping;
  t.repeat.set(repeatX, repeatY);
  return t;
}

// Paints a realistic NATURAL-STONE surface inside the rectangle [x,y,w,h]
// (assumes the caller has already clipped to the stone's shape): cloudy tonal
// mottling + mineral speckle, plus a style-specific detail —
//   'riven'   = slate cleavage streaks (also used for bluestone/flagstone),
//   'tumbled' = travertine/limestone pitting (little dissolved holes),
//   'sand'    = sandstone sedimentary banding.
// Shared by the random-rectangular (opus) and crazy-paving stone generators.
function paintStoneFace(ctx, x, y, w, h, base, style) {
  const { r: br, g: bg, b: bb } = hexRgb(base);
  const area = w * h;
  const clouds = Math.max(4, Math.round(area / 2600));
  for (let i = 0; i < clouds; i++) {
    const gx = x + Math.random() * w, gy = y + Math.random() * h;
    const grad = ctx.createRadialGradient(gx, gy, 0, gx, gy, 8 + Math.random() * Math.min(w, h) * 0.55);
    const v = (Math.random() - 0.5) * (style === 'riven' ? 42 : 26);
    grad.addColorStop(0, `rgba(${br + v | 0},${bg + v | 0},${bb + v | 0},0.28)`);
    grad.addColorStop(1, 'rgba(0,0,0,0)');
    ctx.fillStyle = grad; ctx.fillRect(x - 12, y - 12, w + 24, h + 24);
  }
  const spk = Math.max(8, Math.round(area / 240));
  for (let i = 0; i < spk; i++) {
    const v = (Math.random() - 0.5) * (style === 'riven' ? 40 : 24);
    ctx.fillStyle = rgbStr(br + v, bg + v, bb + v); ctx.globalAlpha = 0.14;
    ctx.beginPath(); ctx.arc(x + Math.random() * w, y + Math.random() * h, 0.5 + Math.random() * 1.8, 0, Math.PI * 2); ctx.fill();
  }
  ctx.globalAlpha = 1;
  if (style === 'riven') {
    ctx.strokeStyle = rgbStr(br - 30, bg - 30, bb - 30);
    const lines = Math.max(3, Math.round(h / 14));
    for (let i = 0; i < lines; i++) {
      ctx.globalAlpha = 0.08 + Math.random() * 0.10; ctx.lineWidth = 0.4 + Math.random() * 0.9;
      const ly = y + Math.random() * h;
      ctx.beginPath(); ctx.moveTo(x, ly + (Math.random() - 0.5) * 4);
      ctx.bezierCurveTo(x + w * 0.33, ly + (Math.random() - 0.5) * 7, x + w * 0.66, ly + (Math.random() - 0.5) * 7, x + w, ly + (Math.random() - 0.5) * 5);
      ctx.stroke();
    }
  } else if (style === 'tumbled') {
    const pits = Math.round(area / 1600);
    for (let i = 0; i < pits; i++) {
      ctx.globalAlpha = 0.08 + Math.random() * 0.10;
      ctx.fillStyle = rgbStr(br - 24, bg - 24, bb - 24);
      ctx.beginPath(); ctx.ellipse(x + Math.random() * w, y + Math.random() * h, 1 + Math.random() * 3, 0.8 + Math.random() * 2, Math.random() * Math.PI, 0, Math.PI * 2); ctx.fill();
    }
  } else if (style === 'sand') {
    ctx.strokeStyle = rgbStr(br - 18, bg - 20, bb - 22);
    const lines = Math.round(h / 10);
    for (let i = 0; i < lines; i++) {
      ctx.globalAlpha = 0.05 + Math.random() * 0.07; ctx.lineWidth = 0.5 + Math.random();
      const ly = y + Math.random() * h;
      ctx.beginPath(); ctx.moveTo(x, ly); ctx.lineTo(x + w, ly + (Math.random() - 0.5) * 3); ctx.stroke();
    }
  }
  ctx.globalAlpha = 1;
}

// ── CUSTOM FINISH TEXTURE ─────────────────────────────────────────────────────
// Each finish gets a real microstructure texture with the custom colour baked in.
// Mesh uses color='#ffffff' (since texture is truthy); texture carries the albedo.
function makeCustomFinishTexture(mat) {
  const S = 512;
  const canvas = document.createElement('canvas');
  canvas.width = canvas.height = S;
  const ctx = canvas.getContext('2d');
  const base = mat.baseColor || mat.color || '#d4c4a8';
  const { r: br, g: bg, b: bb } = hexRgb(base);
  ctx.fillStyle = base;
  ctx.fillRect(0, 0, S, S);
  const finish = mat.finish || 'matte';

  if (finish === 'matte') {
    // Plaster micro-grain
    for (let i = 0; i < 9000; i++) {
      const v = (Math.random() - 0.5) * 10;
      ctx.fillStyle = rgbStr(br + v, bg + v, bb + v);
      ctx.globalAlpha = 0.07;
      ctx.fillRect(Math.random() * S, Math.random() * S, 1, 1);
    }
    ctx.globalAlpha = 1;
    return { texture: tex(canvas, 4, 4), roughness: 0.96, metalness: 0 };
  }

  if (finish === 'satin') {
    for (let i = 0; i < 6000; i++) {
      const v = (Math.random() - 0.5) * 12;
      ctx.fillStyle = rgbStr(br + v, bg + v, bb + v);
      ctx.globalAlpha = 0.07;
      ctx.fillRect(Math.random() * S, Math.random() * S, 1, 1);
    }
    ctx.globalAlpha = 1;
    const sh = ctx.createLinearGradient(0, S * 0.15, 0, S * 0.85);
    sh.addColorStop(0, 'rgba(255,255,255,0)');
    sh.addColorStop(0.5, 'rgba(255,255,255,0.06)');
    sh.addColorStop(1, 'rgba(255,255,255,0)');
    ctx.fillStyle = sh; ctx.fillRect(0, 0, S, S);
    return { texture: tex(canvas, 3, 3), roughness: 0.55, metalness: 0 };
  }

  if (finish === 'semi_gloss') {
    for (let i = 0; i < 3000; i++) {
      const v = (Math.random() - 0.5) * 7;
      ctx.fillStyle = rgbStr(br + v, bg + v, bb + v);
      ctx.globalAlpha = 0.05;
      ctx.fillRect(Math.random() * S, Math.random() * S, 1, 1);
    }
    ctx.globalAlpha = 1;
    return { texture: tex(canvas, 3, 3), roughness: 0.24, metalness: 0.02 };
  }

  if (finish === 'gloss') {
    for (let i = 0; i < 800; i++) {
      const v = (Math.random() - 0.5) * 4;
      ctx.fillStyle = rgbStr(br + v, bg + v, bb + v);
      ctx.globalAlpha = 0.04;
      ctx.fillRect(Math.random() * S, Math.random() * S, 1, 1);
    }
    ctx.globalAlpha = 1;
    return { texture: tex(canvas, 2, 2), roughness: 0.04, metalness: 0.04 };
  }

  if (finish === 'metallic') {
    for (let y = 0; y < S; y++) {
      if (Math.random() > 0.45) continue;
      const v = (Math.random() - 0.5) * 18;
      ctx.strokeStyle = rgbStr(br + v, bg + v, bb + v);
      ctx.globalAlpha = 0.15 + Math.random() * 0.20;
      ctx.lineWidth = 0.5;
      ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(S, y); ctx.stroke();
    }
    ctx.globalAlpha = 1;
    return { texture: tex(canvas, 2, 2), roughness: 0.18, metalness: 0.82 };
  }

  if (finish === 'brushed_metal') {
    for (let y = 0; y < S; y++) {
      const v = (Math.random() - 0.5) * 24;
      ctx.strokeStyle = rgbStr(br + v, bg + v, bb + v);
      ctx.globalAlpha = 0.12 + Math.random() * 0.26;
      ctx.lineWidth = 0.6 + Math.random() * 0.5;
      ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(S, y); ctx.stroke();
    }
    ctx.globalAlpha = 1;
    return { texture: tex(canvas, 2, 2), roughness: 0.30, metalness: 0.88 };
  }

  if (finish === 'velvet') {
    for (let y = 0; y < S; y += 3) {
      ctx.fillStyle = jitter(base, 22); ctx.globalAlpha = 0.62;
      ctx.fillRect(0, y, S, 2);
      ctx.fillStyle = 'rgba(0,0,0,1)'; ctx.globalAlpha = 0.13;
      ctx.fillRect(0, y + 2, S, 1);
    }
    for (let i = 0; i < 4500; i++) {
      ctx.fillStyle = jitter(base, 30); ctx.globalAlpha = 0.28 + Math.random() * 0.45;
      ctx.beginPath(); ctx.arc(Math.random() * S, Math.random() * S, 0.5 + Math.random() * 1.3, 0, Math.PI * 2); ctx.fill();
    }
    ctx.globalAlpha = 1;
    return { texture: tex(canvas, 4, 4), roughness: 0.98, metalness: 0 };
  }

  if (finish === 'pearl') {
    for (let i = 0; i < 3000; i++) {
      const v = (Math.random() - 0.5) * 8;
      ctx.fillStyle = rgbStr(br + v, bg + v, bb + v);
      ctx.globalAlpha = 0.06;
      ctx.fillRect(Math.random() * S, Math.random() * S, 1, 1);
    }
    const iris = ctx.createLinearGradient(0, 0, S, S);
    iris.addColorStop(0, 'rgba(255,255,255,0)');
    iris.addColorStop(0.35, 'rgba(255,255,255,0.08)');
    iris.addColorStop(0.65, `rgba(${Math.min(255, br + 40)},${Math.min(255, bg + 20)},${Math.min(255, bb + 55)},0.10)`);
    iris.addColorStop(1, 'rgba(255,255,255,0)');
    ctx.globalAlpha = 1; ctx.fillStyle = iris; ctx.fillRect(0, 0, S, S);
    return { texture: tex(canvas, 3, 3), roughness: 0.34, metalness: 0.22 };
  }

  if (finish === 'concrete') {
    for (let i = 0; i < 700; i++) {
      ctx.fillStyle = `rgba(0,0,0,${Math.random() * 0.030})`;
      ctx.fillRect(Math.random() * S, Math.random() * S, 15 + Math.random() * 42, 15 + Math.random() * 42);
    }
    for (let i = 0; i < 200; i++) {
      ctx.fillStyle = `rgba(255,255,255,${Math.random() * 0.022})`;
      ctx.fillRect(Math.random() * S, Math.random() * S, 8 + Math.random() * 25, 8 + Math.random() * 25);
    }
    for (let i = 0; i < 600; i++) {
      const v = (Math.random() - 0.5) * 18;
      ctx.fillStyle = rgbStr(br + v, bg + v, bb + v);
      ctx.globalAlpha = 0.18;
      ctx.beginPath(); ctx.arc(Math.random() * S, Math.random() * S, 0.5 + Math.random() * 2.0, 0, Math.PI * 2); ctx.fill();
    }
    ctx.globalAlpha = 1;
    ctx.strokeStyle = 'rgba(0,0,0,0.055)'; ctx.lineWidth = 1.2;
    for (let y2 = 72; y2 < S; y2 += 72 + Math.random() * 32) {
      ctx.beginPath(); ctx.moveTo(0, y2); ctx.lineTo(S, y2); ctx.stroke();
    }
    return { texture: tex(canvas, 3, 3), roughness: 0.96, metalness: 0 };
  }

  // fallback - subtle noise
  for (let i = 0; i < 4000; i++) {
    const v = (Math.random() - 0.5) * 9;
    ctx.fillStyle = rgbStr(br + v, bg + v, bb + v);
    ctx.globalAlpha = 0.07;
    ctx.fillRect(Math.random() * S, Math.random() * S, 1, 1);
  }
  ctx.globalAlpha = 1;
  return { texture: tex(canvas, 4, 4), roughness: 0.90, metalness: 0 };
}

// ── WINDOW OUTDOOR VIEW TEXTURE ──────────────────────────────────────────────
// Paints a procedural garden scene visible through window glass.
// Per-time-of-day palettes for the outdoor garden view seen through windows.
// Each entry drives the sky gradient, tree/foliage tint, ground tone, and
// which celestial extras (sun glow / sunset disc / moon+stars) get drawn.
const WINDOW_VIEW_PALETTES = {
  morning: {
    sky: ['#7fa9d6', '#aacbe2', '#ffe9c4'],
    haze: 'rgba(255,236,200,0.22)',
    treeLine: ['#3a5024', '#44602c'],
    shrubLeaf: '#4d6840', shrubBloom: '#eef4ee', shrubHighlight: '#f8fbf4',
    canopyGolds: ['#c99a1c','#d8ab28','#cfa020','#e4c236','#d2a828','#c09018','#e0c840','#eddc60'],
    canopyHighlight: '#fff0a0',
    bgTrees: ['#44602c', '#4f6c34', '#3c542a', '#466230', '#516c38'],
    groundTop: '#6a9840', groundMid: '#7cac4c', groundBottom: '#8ebc5c',
    grassSpeckle: ['#6a9c44', '#94cc66'],
    sunColor: '#fff6d8', sunGlow: 'rgba(255,244,200,',
  },
  evening: {
    sky: ['#2c3462', '#c95b3e', '#ffb15a'],
    haze: 'rgba(255,150,80,0.28)',
    treeLine: ['#241a28', '#2e2030'],
    shrubLeaf: '#3a2c34', shrubBloom: '#caa8a0', shrubHighlight: '#e8c8b0',
    canopyGolds: ['#a8461c','#c25620','#b03c18','#d86a28','#c25e24','#983414','#d87c30','#e89848'],
    canopyHighlight: '#ffcf7e',
    bgTrees: ['#241a28', '#2c2030', '#201824', '#28202c', '#302430'],
    groundTop: '#5a3a2c', groundMid: '#7a4e34', groundBottom: '#9a683e',
    grassSpeckle: ['#7a4e30', '#b87c44'],
    sunColor: '#ffcf8a', sunGlow: 'rgba(255,150,70,',
  },
  night: {
    sky: ['#01030c', '#050a1e', '#0d1630'],
    haze: 'rgba(40,60,110,0.18)',
    treeLine: ['#050a08', '#080f0c'],
    shrubLeaf: '#0a120e', shrubBloom: '#1a2a28', shrubHighlight: '#2a3e3a',
    canopyGolds: ['#141e14','#182418','#101a10','#1c2a1c','#162016','#0e1810','#1a281a','#202e20'],
    canopyHighlight: '#2c3c2c',
    bgTrees: ['#050a08', '#070d0a', '#040806', '#060c09', '#080e0b'],
    groundTop: '#040806', groundMid: '#070c08', groundBottom: '#0a100a',
    grassSpeckle: ['#0a120c', '#101a12'],
    sunColor: '#e8eeff', sunGlow: 'rgba(200,215,255,',
  },
};

export function makeWindowViewTexture(timeOfDay = 'morning') {
  const pal = WINDOW_VIEW_PALETTES[timeOfDay] || WINDOW_VIEW_PALETTES.morning;
  const isNight = timeOfDay === 'night';
  const isEvening = timeOfDay === 'evening';
  const W = 512, H = 660;
  const canvas = document.createElement('canvas');
  canvas.width = W; canvas.height = H;
  const ctx = canvas.getContext('2d');

  // ── SKY ────────────────────────────────────────────────────────────────────
  const sky = ctx.createLinearGradient(0, 0, 0, H * 0.58);
  sky.addColorStop(0,    pal.sky[0]);
  sky.addColorStop(0.5,  pal.sky[1]);
  sky.addColorStop(1,    pal.sky[2]);
  ctx.fillStyle = sky;
  ctx.fillRect(0, 0, W, H * 0.58);

  // ── NIGHT: stars + moon ──────────────────────────────────────────────────
  if (isNight) {
    for (let i = 0; i < 160; i++) {
      const x = Math.random() * W, y = Math.random() * H * 0.5;
      const r = Math.random() < 0.12 ? 1.4 + Math.random() * 1.2 : 0.5 + Math.random() * 0.7;
      ctx.globalAlpha = 0.35 + Math.random() * 0.55;
      ctx.fillStyle = Math.random() < 0.2 ? '#cfe0ff' : '#ffffff';
      ctx.beginPath(); ctx.arc(x, y, r, 0, Math.PI * 2); ctx.fill();
    }
    ctx.globalAlpha = 1;
    // Moon with soft halo
    const mx = W * 0.72, my = H * 0.16, mr = 34;
    const halo = ctx.createRadialGradient(mx, my, 0, mx, my, mr * 4.2);
    halo.addColorStop(0, 'rgba(220,230,255,0.35)');
    halo.addColorStop(0.35, 'rgba(200,215,255,0.12)');
    halo.addColorStop(1, 'rgba(200,215,255,0)');
    ctx.fillStyle = halo; ctx.fillRect(mx - mr * 4.2, my - mr * 4.2, mr * 8.4, mr * 8.4);
    ctx.fillStyle = '#f2f4fa'; ctx.beginPath(); ctx.arc(mx, my, mr, 0, Math.PI * 2); ctx.fill();
    ctx.fillStyle = 'rgba(190,200,220,0.35)';
    for (const [cx2, cy2, cr] of [[mx-10,my-6,7],[mx+8,my+10,5],[mx-4,my+13,4]]) {
      ctx.beginPath(); ctx.arc(cx2, cy2, cr, 0, Math.PI * 2); ctx.fill();
    }
  } else {
    // ── MORNING / EVENING: glowing sun low on the horizon ────────────────────
    const sx = timeOfDay === 'morning' ? W * 0.24 : W * 0.78;
    const sy = H * (timeOfDay === 'morning' ? 0.40 : 0.34);
    const sr = isEvening ? 46 : 30;
    const glow = ctx.createRadialGradient(sx, sy, 0, sx, sy, sr * (isEvening ? 5 : 3.6));
    glow.addColorStop(0, pal.sunGlow + '0.55)');
    glow.addColorStop(0.4, pal.sunGlow + '0.22)');
    glow.addColorStop(1, pal.sunGlow + '0)');
    ctx.fillStyle = glow; ctx.fillRect(sx - sr * 5, sy - sr * 5, sr * 10, sr * 10);
    ctx.fillStyle = pal.sunColor;
    ctx.globalAlpha = 0.92;
    ctx.beginPath(); ctx.arc(sx, sy, sr, 0, Math.PI * 2); ctx.fill();
    ctx.globalAlpha = 1;
  }

  // helper: paint a foliage blob
  const blob = (cx, cy, rx, ry, color, alpha) => {
    ctx.fillStyle = color; ctx.globalAlpha = alpha;
    ctx.beginPath(); ctx.ellipse(cx, cy, rx, ry, 0, 0, Math.PI * 2); ctx.fill();
  };

  // ── DISTANT TREE-LINE ────────────────────────────────────────────────────
  for (let i = 0; i < 14; i++) {
    const x = i * 42 - 10, cy = H * 0.42 + Math.sin(i * 1.8) * 14;
    blob(x, cy, 36, 48, pal.treeLine[0], 0.82);
    blob(x + 20, cy - 10, 24, 34, pal.treeLine[1], 0.76);
  }

  // ── LEFT SHRUB ────────────────────────────────────────────────────────────
  const sX = W * 0.17, sY = H * 0.64;
  for (let i = 0; i < 10; i++) blob(sX+(Math.random()-.5)*55, sY+(Math.random()-.5)*38, 24+Math.random()*16, 18+Math.random()*12, pal.shrubLeaf, 0.65+Math.random()*.2);
  for (let i = 0; i < 22; i++) blob(sX+(Math.random()-.5)*72, sY-18+(Math.random()-.5)*52, 16+Math.random()*24, 12+Math.random()*18, pal.shrubBloom, 0.70+Math.random()*.22);
  for (let i = 0; i < 16; i++) blob(sX+(Math.random()-.5)*60, sY-10+(Math.random()-.5)*44, 10+Math.random()*18, 8+Math.random()*14, pal.shrubHighlight, 0.75+Math.random()*.20);

  // ── MAIN TREE (centre-right) ──────────────────────────────────────────────
  const tX = W * 0.60;
  ctx.fillStyle = isNight ? '#030503' : '#58402e'; ctx.globalAlpha = 0.88;
  ctx.beginPath();
  ctx.moveTo(tX - 8, H * 0.76); ctx.lineTo(tX + 8, H * 0.76);
  ctx.lineTo(tX + 4, H * 0.16); ctx.lineTo(tX - 4, H * 0.16);
  ctx.closePath(); ctx.fill();
  ctx.strokeStyle = isNight ? '#030503' : '#52382a'; ctx.lineWidth = 3.5;
  for (let b = 0; b < 6; b++) {
    const by = H * (0.18 + b * 0.07), side = b % 2 === 0 ? 1 : -1;
    ctx.globalAlpha = 0.75; ctx.beginPath();
    ctx.moveTo(tX, by);
    ctx.quadraticCurveTo(tX + side * 36, by - 18, tX + side * 72, by - 14);
    ctx.stroke();
  }
  ctx.globalAlpha = 1;
  const golds = pal.canopyGolds;
  for (let i = 0; i < 55; i++) {
    const c = golds[Math.floor(Math.random() * golds.length)];
    blob(tX + (Math.random()-.5)*W*.46, H*.07+Math.random()*H*.44, 20+Math.random()*54, 16+Math.random()*42, c, 0.60+Math.random()*.34);
  }
  for (let i = 0; i < 18; i++) {
    blob(tX+(Math.random()-.5)*W*.38, H*.09+Math.random()*H*.34, 10+Math.random()*28, 8+Math.random()*20, pal.canopyHighlight, isNight ? 0.10+Math.random()*.08 : 0.28+Math.random()*.22);
  }
  ctx.globalAlpha = 1;

  // ── BACKGROUND TREES ──────────────────────────────────────────────────────
  const bgT = pal.bgTrees;
  for (const [cx, cy, rx, ry, col, al] of [
    [W*0.82, H*0.35, 50, 66, bgT[0], 0.88],
    [W*0.88, H*0.30, 38, 52, bgT[1], 0.82],
    [W*0.94, H*0.38, 30, 44, bgT[2], 0.78],
    [W*0.06, H*0.36, 38, 52, bgT[3], 0.82],
    [W*0.12, H*0.32, 30, 44, bgT[4], 0.76],
  ]) blob(cx, cy, rx, ry, col, al);

  // ── NIGHT: a few distant lit windows peeking through the tree-line ────────
  if (isNight) {
    for (let i = 0; i < 6; i++) {
      const x = W * (0.05 + Math.random() * 0.9), y = H * (0.30 + Math.random() * 0.14);
      ctx.globalAlpha = 0.55 + Math.random() * 0.35;
      ctx.fillStyle = Math.random() < 0.6 ? '#ffcf7a' : '#ffe6b0';
      ctx.fillRect(x, y, 5 + Math.random() * 4, 6 + Math.random() * 5);
    }
    ctx.globalAlpha = 1;
  }

  // ── LAWN / GROUND ───────────────────────────────────────────────────────────
  const ground = ctx.createLinearGradient(0, H * 0.66, 0, H);
  ground.addColorStop(0,   pal.groundTop);
  ground.addColorStop(0.25, pal.groundMid);
  ground.addColorStop(0.65, pal.groundBottom);
  ground.addColorStop(1,   pal.groundBottom);
  ctx.fillStyle = ground;
  ctx.fillRect(0, H * 0.66, W, H);
  for (let i = 0; i < 90; i++) {
    ctx.fillStyle = Math.random() > .5 ? pal.grassSpeckle[0] : pal.grassSpeckle[1];
    ctx.globalAlpha = isNight ? 0.10 : 0.16;
    ctx.fillRect(Math.random()*W, H*.67+Math.random()*H*.33, Math.random()*55, Math.random()*7);
  }
  ctx.globalAlpha = 1;

  // ── DEPTH HAZE (horizon softener) ──────────────────────────────────────────
  const haze = ctx.createLinearGradient(0, H * 0.28, 0, H * 0.58);
  haze.addColorStop(0, pal.haze.replace(/[\d.]+\)$/, '0)'));
  haze.addColorStop(1, pal.haze);
  ctx.fillStyle = haze; ctx.fillRect(0, 0, W, H * 0.58);

  const t = new THREE.CanvasTexture(canvas);
  return t;
}

// ─── WINDOW-VIEW ANIMATED SPRITES ──────────────────────────────────────────────
// Small transparent-background billboards layered in front of the static
// window-view backdrop and animated (position/rotation only, drawn once) via
// useFrame in ThreeDRoom.jsx — a driving car, a walking person, a swaying
// bush. Kept intentionally simple/iconic (small canvas, few shapes) since
// they're seen tiny and distant through a window.

export function makeCarSpriteTexture(color = '#b03a2e', timeOfDay = 'morning') {
  const W = 96, H = 48;
  const canvas = document.createElement('canvas');
  canvas.width = W; canvas.height = H;
  const ctx = canvas.getContext('2d');
  const isNight = timeOfDay === 'night';
  const dark = (() => {
    const n = parseInt(color.replace('#', ''), 16);
    const r = Math.max(0, ((n >> 16) & 255) - 40), g = Math.max(0, ((n >> 8) & 255) - 40), b = Math.max(0, (n & 255) - 40);
    return `rgb(${r},${g},${b})`;
  })();
  // Lower body
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.moveTo(6, 34);
  ctx.quadraticCurveTo(2, 34, 4, 28);
  ctx.lineTo(14, 26);
  ctx.quadraticCurveTo(20, 14, 34, 13);
  ctx.lineTo(66, 13);
  ctx.quadraticCurveTo(80, 14, 86, 26);
  ctx.lineTo(92, 28);
  ctx.quadraticCurveTo(94, 34, 90, 34);
  ctx.closePath();
  ctx.fill();
  // Cabin/window band
  ctx.fillStyle = isNight ? 'rgba(200,220,255,0.55)' : 'rgba(160,190,210,0.85)';
  ctx.beginPath();
  ctx.moveTo(24, 25);
  ctx.quadraticCurveTo(30, 16, 38, 15);
  ctx.lineTo(62, 15);
  ctx.quadraticCurveTo(70, 16, 76, 25);
  ctx.closePath();
  ctx.fill();
  ctx.strokeStyle = dark; ctx.lineWidth = 1.2;
  ctx.beginPath(); ctx.moveTo(50, 15); ctx.lineTo(50, 25); ctx.stroke();
  // Wheels
  for (const wx of [22, 74]) {
    ctx.fillStyle = '#151515';
    ctx.beginPath(); ctx.arc(wx, 34, 8, 0, Math.PI * 2); ctx.fill();
    ctx.fillStyle = '#565656';
    ctx.beginPath(); ctx.arc(wx, 34, 3.2, 0, Math.PI * 2); ctx.fill();
  }
  // Lights — glow at night
  ctx.fillStyle = isNight ? '#fff6c8' : '#f0e8c0';
  ctx.beginPath(); ctx.ellipse(90, 29, 2.4, 1.8, 0, 0, Math.PI * 2); ctx.fill();
  if (isNight) {
    const glow = ctx.createRadialGradient(90, 29, 0, 90, 29, 10);
    glow.addColorStop(0, 'rgba(255,246,200,0.55)'); glow.addColorStop(1, 'rgba(255,246,200,0)');
    ctx.fillStyle = glow; ctx.beginPath(); ctx.arc(90, 29, 10, 0, Math.PI * 2); ctx.fill();
  }
  ctx.fillStyle = '#a01818';
  ctx.beginPath(); ctx.ellipse(5, 29, 2, 1.6, 0, 0, Math.PI * 2); ctx.fill();

  const t = new THREE.CanvasTexture(canvas);
  t.premultiplyAlpha = false;
  return t;
}

export function makePersonSpriteTexture(timeOfDay = 'morning') {
  const W = 22, H = 56;
  const canvas = document.createElement('canvas');
  canvas.width = W; canvas.height = H;
  const ctx = canvas.getContext('2d');
  const isNight = timeOfDay === 'night';
  const body = isNight ? '#12141c' : '#2a2620';
  ctx.fillStyle = body;
  // Head
  ctx.beginPath(); ctx.arc(W / 2, 8, 6, 0, Math.PI * 2); ctx.fill();
  // Torso
  ctx.beginPath();
  ctx.moveTo(W/2 - 6, 16); ctx.lineTo(W/2 + 6, 16);
  ctx.lineTo(W/2 + 4, 34); ctx.lineTo(W/2 - 4, 34);
  ctx.closePath(); ctx.fill();
  // Arms (slightly asymmetric for a mid-stride look)
  ctx.strokeStyle = body; ctx.lineWidth = 3; ctx.lineCap = 'round';
  ctx.beginPath(); ctx.moveTo(W/2 - 5, 19); ctx.lineTo(W/2 - 9, 30); ctx.stroke();
  ctx.beginPath(); ctx.moveTo(W/2 + 5, 19); ctx.lineTo(W/2 + 8, 29); ctx.stroke();
  // Legs, mid-stride
  ctx.beginPath(); ctx.moveTo(W/2 - 3, 34); ctx.lineTo(W/2 - 8, 54); ctx.stroke();
  ctx.beginPath(); ctx.moveTo(W/2 + 3, 34); ctx.lineTo(W/2 + 6, 54); ctx.stroke();
  const t = new THREE.CanvasTexture(canvas);
  t.premultiplyAlpha = false;
  return t;
}

export function makeSwayBushTexture(leafColor = '#4d6840', timeOfDay = 'morning') {
  const W = 96, H = 84;
  const canvas = document.createElement('canvas');
  canvas.width = W; canvas.height = H;
  const ctx = canvas.getContext('2d');
  const isNight = timeOfDay === 'night';
  const col = isNight ? '#0a120e' : leafColor;
  for (let i = 0; i < 26; i++) {
    const cx2 = W/2 + (Math.random()-0.5)*W*0.7, cy2 = H*0.65 + (Math.random()-0.5)*H*0.55;
    const rx = 10 + Math.random()*16, ry = 8 + Math.random()*12;
    ctx.fillStyle = col; ctx.globalAlpha = 0.55 + Math.random()*0.35;
    ctx.beginPath(); ctx.ellipse(cx2, cy2, rx, ry, Math.random()*Math.PI, 0, Math.PI*2); ctx.fill();
  }
  ctx.globalAlpha = 1;
  const t = new THREE.CanvasTexture(canvas);
  t.premultiplyAlpha = false;
  return t;
}

// Seeded PRNG (mulberry32) so the same painting id always generates the same
// artwork instead of re-rolling on every re-render.
function mulberry32(seed) {
  let a = seed >>> 0;
  return function () {
    a |= 0; a = (a + 0x6D2B79F5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// Procedural abstract gallery artwork for the "Painting" wall-decor item —
// a different (but stable, per opening id) composition each time so a room
// with several paintings doesn't look like the same picture repeated.
export function makePaintingArtTexture(seed = 1) {
  const W = 512, H = 384;
  const canvas = document.createElement('canvas');
  canvas.width = W; canvas.height = H;
  const ctx = canvas.getContext('2d');
  const rnd = mulberry32(seed * 97 + 13);

  const PALETTES = [
    ['#e8dcc4', '#c17a4e', '#8a3d2c', '#3a2a1e', '#d4a24a'],
    ['#dce6e0', '#5a8a78', '#2c5048', '#1a2e2a', '#c4a24a'],
    ['#e4dce8', '#8a6ab0', '#4a3878', '#2a1e40', '#d488a0'],
    ['#e8e0d0', '#3a6890', '#1e3a54', '#c8903c', '#e8e8e0'],
    ['#f0e4d4', '#b04838', '#6a1e18', '#e0a840', '#2a2420'],
  ];
  const palette = PALETTES[Math.floor(rnd() * PALETTES.length)];

  // Background wash
  const bg = ctx.createLinearGradient(0, 0, W, H);
  bg.addColorStop(0, palette[0]);
  bg.addColorStop(1, jitter(palette[0], 18));
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, W, H);

  // Abstract layered brush-stroke blobs
  const blob = (cx, cy, rx, ry, rot, color, alpha) => {
    ctx.save();
    ctx.translate(cx, cy); ctx.rotate(rot);
    ctx.fillStyle = color; ctx.globalAlpha = alpha;
    ctx.beginPath(); ctx.ellipse(0, 0, rx, ry, 0, 0, Math.PI * 2); ctx.fill();
    ctx.restore();
  };
  const strokeCount = 6 + Math.floor(rnd() * 5);
  for (let i = 0; i < strokeCount; i++) {
    const col = palette[1 + Math.floor(rnd() * (palette.length - 1))];
    blob(
      rnd() * W, rnd() * H,
      40 + rnd() * 120, 18 + rnd() * 60,
      rnd() * Math.PI,
      col, 0.35 + rnd() * 0.45,
    );
  }
  ctx.globalAlpha = 1;

  // A few crisp geometric accents (line or ring) for a "gallery abstract" feel
  const accentCount = 2 + Math.floor(rnd() * 3);
  for (let i = 0; i < accentCount; i++) {
    ctx.strokeStyle = palette[palette.length - 1];
    ctx.lineWidth = 1.5 + rnd() * 2.5;
    ctx.globalAlpha = 0.5 + rnd() * 0.4;
    if (rnd() > 0.5) {
      ctx.beginPath();
      ctx.arc(rnd() * W, rnd() * H, 20 + rnd() * 70, 0, Math.PI * 2);
      ctx.stroke();
    } else {
      ctx.beginPath();
      ctx.moveTo(rnd() * W, rnd() * H);
      ctx.lineTo(rnd() * W, rnd() * H);
      ctx.stroke();
    }
  }
  ctx.globalAlpha = 1;

  // Subtle canvas-weave texture (fine noise) so it doesn't read as flat-vector
  const imgData = ctx.getImageData(0, 0, W, H);
  const d = imgData.data;
  for (let i = 0; i < d.length; i += 4) {
    const n = (rnd() - 0.5) * 10;
    d[i] += n; d[i + 1] += n; d[i + 2] += n;
  }
  ctx.putImageData(imgData, 0, 0);

  return new THREE.CanvasTexture(canvas);
}

// ── FLOOR TEXTURES ────────────────────────────────────────────────────────────
export function makeFloorTexture(mat) {
  if (mat?.__custom) return makeCustomFinishTexture(mat);

  const S = 1024;
  const canvas = document.createElement('canvas');
  canvas.width = canvas.height = S;
  const ctx = canvas.getContext('2d');
  const base = mat?.baseColor || '#d4c4a8';
  const { r: br, g: bg, b: bb } = hexRgb(base);

  ctx.fillStyle = base;
  ctx.fillRect(0, 0, S, S);

  if (!mat) return { texture: tex(canvas, 4, 4), roughness: 0.85, metalness: 0 };

  const id = mat.id;

  // ── LAMINATE PLANKS ──────────────────────────────────────────────────────
  // Deliberately DIFFERENT from solid wood so the two read apart at a glance:
  // a staggered brick-laid grid of boards with pronounced BEVELLED V-grooves
  // (top + sides), a cleaner, more parallel PRINTED grain (less chaotic than
  // real timber, and no scattered knots), and a uniform top-coat SHEEN.
  // Both the default (boards along X) and the `dir:'y'` variants (boards along
  // Y) are handled here — the `_y` ids share the base id's plank width + finish.
  if (id.startsWith('lam_')) {
    const baseId = id.replace(/_y$/, '');
    // Slim, long boards (≈7:1) so they read as real laminate planks rather than
    // short stubby squares.
    const LAM_PW = { lam_natural:80, lam_golden:78, lam_rustic:82, lam_smoked:78, lam_greige:80, lam_driftwood:82, lam_grey:80, lam_lgrey:78, lam_white:82, lam_charcoal:80, lam_walnut:74, lam_walnut_g:74 };
    const LAM_ROUGH = { lam_natural:0.55, lam_golden:0.40, lam_rustic:0.58, lam_smoked:0.48, lam_greige:0.54, lam_driftwood:0.56, lam_grey:0.52, lam_lgrey:0.52, lam_white:0.56, lam_charcoal:0.50, lam_walnut:0.42, lam_walnut_g:0.12 };
    const pw = LAM_PW[baseId] || 78, pl = 540, gc = mat.grainColor;
    const nc = Math.ceil(S / pw) + 1;
    const lr = LAM_ROUGH[baseId] ?? 0.5;

    // A `dir:'y'` laminate simply rotates the whole plank layout 90° so the
    // boards (and their grooves) run along Y instead of X. The canvas is square,
    // so a corner rotation still fills it edge-to-edge and tiles seamlessly.
    ctx.save();
    if (mat.dir === 'y') { ctx.translate(S, 0); ctx.rotate(Math.PI / 2); }

    // Board faces — printed décor → subtle, fairly UNIFORM board-to-board tone
    // (solid wood jitters much more).
    for (let i = 0; i < nc; i++) { ctx.fillStyle = jitter(base, 9); ctx.fillRect(i * pw, 0, pw, S); }

    // Printed grain: long, mostly-parallel streaks + a couple of soft cathedral
    // arcs per board, clipped to each board so it reads as a printed plank.
    for (let i = 0; i < nc; i++) {
      const px = i * pw;
      ctx.save(); ctx.beginPath(); ctx.rect(px, 0, pw, S); ctx.clip();
      ctx.strokeStyle = gc;
      for (let j = 0; j < 24; j++) {
        const x = px + (j / 24) * pw + (Math.random() - 0.5) * 3;
        ctx.globalAlpha = 0.05 + Math.random() * 0.09; ctx.lineWidth = 0.4 + Math.random() * 1.1;
        ctx.beginPath(); ctx.moveTo(x, 0);
        ctx.bezierCurveTo(x + (Math.random() - 0.5) * 7, S * 0.34, x + (Math.random() - 0.5) * 7, S * 0.66, x + (Math.random() - 0.5) * 5, S);
        ctx.stroke();
      }
      for (let a = 0; a < 2; a++) {
        const cx = px + pw * (0.3 + Math.random() * 0.4), cy = Math.random() * S;
        ctx.globalAlpha = 0.05 + Math.random() * 0.05; ctx.lineWidth = 0.6;
        for (let k = 0; k < 5; k++) {
          ctx.beginPath(); ctx.moveTo(cx, cy - 36 - k * 8);
          ctx.quadraticCurveTo(cx - (9 + k * 6), cy, cx, cy + 36 + k * 8); ctx.stroke();
        }
      }
      ctx.restore();
    }
    ctx.globalAlpha = 1;

    // Staggered end-joints (brick-lay): a horizontal bevel groove per board,
    // offset column-to-column so joints never line up — the classic laminate lay.
    for (let i = 0; i < nc; i++) {
      const px = i * pw, off = (i % 3) * (pl / 3);
      for (let y = off; y < S + pl; y += pl) {
        ctx.fillStyle = 'rgba(0,0,0,0.30)'; ctx.fillRect(px, y - 1.5, pw, 3);
        ctx.fillStyle = 'rgba(255,255,255,0.16)'; ctx.fillRect(px, y + 1.5, pw, 1);
      }
    }

    // Pronounced bevelled V-grooves between boards — the strongest laminate tell.
    for (let i = 0; i <= nc; i++) {
      const px = i * pw;
      ctx.fillStyle = 'rgba(0,0,0,0.32)'; ctx.fillRect(px - 1.5, 0, 3, S);
      ctx.fillStyle = 'rgba(255,255,255,0.20)'; ctx.fillRect(px + 1.5, 0, 1.2, S);
      ctx.fillStyle = 'rgba(0,0,0,0.12)'; ctx.fillRect(px - 3, 0, 1.5, S);
    }

    // NOTE: no baked sheen gradient here on purpose. A directional gradient
    // across the texture tiles into visible light/dark bands (the "3 shades"
    // seam every repeat). The wear-layer gloss instead comes entirely from the
    // material's roughness/metalness (real environment reflection), which stays
    // uniform however many times the texture repeats.
    ctx.restore();
    return { texture: tex(canvas, 3, 3), roughness: lr, metalness: lr < 0.2 ? 0.08 : 0.03 };
  }

  // ── WOOD PLANKS ──────────────────────────────────────────────────────────
  const WOOD_IDS = ['oak','walnut','maple','dark_oak','limed_oak','pine','walnut_am','teak','ash','whitewash','ebony','grey_oak','hickory','wenge','mahogany','white_oak','smoked_oak','fumed_oak'];
  if (WOOD_IDS.includes(id)) {
    // Wide plank widths — modern floors use broad boards, so the new oaks are wider.
    const pwMap = { oak:72, walnut:60, maple:84, dark_oak:58, limed_oak:70, pine:88, walnut_am:62, teak:68, ash:80, whitewash:76, ebony:55, grey_oak:72, hickory:78, wenge:56, mahogany:66, white_oak:96, smoked_oak:92, fumed_oak:88 };
    const pw = pwMap[id] || 70;
    const nc = Math.ceil(S / pw) + 2;
    const gc = mat.grainColor;
    const { r: gr, g: gg, b: gb } = hexRgb(gc);

    for (let i = 0; i < nc; i++) {
      ctx.fillStyle = jitter(base, id === 'pine' ? 26 : id === 'ash' ? 18 : id === 'hickory' ? 34 : 22);
      ctx.fillRect(i * pw, 0, pw - 2, S);
      ctx.fillStyle = 'rgba(255,255,255,0.18)';
      ctx.fillRect(i * pw, 0, 2, S);
      ctx.fillStyle = 'rgba(0,0,0,0.16)';
      ctx.fillRect(i * pw + pw - 3, 0, 2, S);
    }

    // Organic wavy grain lines
    for (let j = 0; j < 120; j++) {
      const y0 = (j / 120) * S;
      ctx.strokeStyle = gc;
      ctx.globalAlpha = 0.04 + Math.random() * 0.11;
      ctx.lineWidth = 0.3 + Math.random() * 1.0;
      ctx.beginPath();
      ctx.moveTo(0, y0 + (Math.random() - 0.5) * 6);
      ctx.bezierCurveTo(
        S * 0.2, y0 + (Math.random() - 0.5) * 18,
        S * 0.5, y0 + (Math.random() - 0.5) * 22,
        S * 0.75, y0 + (Math.random() - 0.5) * 16
      );
      ctx.bezierCurveTo(
        S * 0.85, y0 + (Math.random() - 0.5) * 10,
        S * 0.95, y0 + (Math.random() - 0.5) * 6,
        S, y0 + (Math.random() - 0.5) * 4
      );
      ctx.stroke();
    }

    // Plank dividers
    ctx.globalAlpha = 0.44; ctx.fillStyle = gc;
    for (let i = 0; i < nc; i++) ctx.fillRect(i * pw - 1, 0, 2, S);

    // End-grain butt joints
    for (let i = 0; i < nc; i++) {
      const ey = 80 + Math.random() * (S - 160);
      ctx.globalAlpha = 0.14; ctx.fillRect(i * pw, ey - 1, pw - 2, 3);
      ctx.globalAlpha = 0.07; ctx.fillRect(i * pw, ey + 2, pw - 2, 12);
    }

    // Knots
    for (let k = 0; k < 5; k++) {
      const kx = Math.random() * S, ky = Math.random() * S;
      const kr = 5 + Math.random() * 14;
      ctx.globalAlpha = 0.22;
      ctx.fillStyle = rgbStr(gr - 18, gg - 18, gb - 18);
      ctx.beginPath(); ctx.ellipse(kx, ky, kr, kr * 0.65, Math.random() * Math.PI, 0, Math.PI * 2); ctx.fill();
      ctx.globalAlpha = 0.26;
      ctx.fillStyle = rgbStr(gr - 40, gg - 40, gb - 40);
      ctx.beginPath(); ctx.ellipse(kx, ky, kr * 0.42, kr * 0.3, Math.random() * Math.PI, 0, Math.PI * 2); ctx.fill();
      ctx.globalAlpha = 0.10;
      ctx.strokeStyle = gc; ctx.lineWidth = 1.2;
      ctx.beginPath(); ctx.ellipse(kx, ky, kr * 1.3, kr, Math.random() * Math.PI, 0, Math.PI * 2); ctx.stroke();
    }
    ctx.globalAlpha = 1;

    if (id === 'limed_oak' || id === 'whitewash') {
      ctx.fillStyle = id === 'whitewash' ? 'rgba(255,255,255,0.44)' : 'rgba(255,255,255,0.34)';
      ctx.fillRect(0, 0, S, S);
    }
    if (id === 'ebony') {
      ctx.fillStyle = 'rgba(0,0,0,0.18)';
      ctx.fillRect(0, 0, S, S);
    }

    const roughMap = { oak:0.70, walnut:0.58, maple:0.65, dark_oak:0.56, limed_oak:0.76, pine:0.74, walnut_am:0.55, teak:0.60, ash:0.72, whitewash:0.80, ebony:0.48, grey_oak:0.68, hickory:0.72, wenge:0.50, mahogany:0.54, white_oak:0.72, smoked_oak:0.66, fumed_oak:0.60 };
    return { texture: tex(canvas, 3, 3), roughness: roughMap[id] ?? 0.68, metalness: 0 };
  }

  // ── HERRINGBONE ──────────────────────────────────────────────────────────
  if (id === 'herringbone') {
    const bw = 22, bh = 11, gc = mat.grainColor;
    ctx.fillStyle = base; ctx.fillRect(0, 0, S, S);
    for (let y = -S; y < S * 2; y += bh * 4) {
      for (let x = -S; x < S * 2; x += bw * 2) {
        for (const [tx, ty, rot] of [[x+bw*.5, y+bh*.5, -Math.PI/4],[x+bw*1.5, y+bh*2.5, Math.PI/4]]) {
          ctx.fillStyle = jitter(base, 14);
          ctx.save(); ctx.translate(tx, ty); ctx.rotate(rot);
          ctx.fillRect(-bw/2, -bh/2, bw-1, bh-1);
          ctx.fillStyle = 'rgba(255,255,255,0.16)'; ctx.fillRect(-bw/2, -bh/2, bw-1, 1.5);
          ctx.fillStyle = 'rgba(0,0,0,0.14)';       ctx.fillRect(-bw/2, bh/2-2.5, bw-1, 1.5);
          ctx.restore();
        }
      }
    }
    ctx.strokeStyle = gc;
    for (let j = 0; j < 60; j++) {
      const y = (j/60)*S; ctx.globalAlpha = 0.05; ctx.lineWidth = 0.4;
      ctx.beginPath(); ctx.moveTo(0,y); ctx.lineTo(S,y+(Math.random()-0.5)*8); ctx.stroke();
    }
    ctx.globalAlpha = 1;
    return { texture: tex(canvas, 3, 3), roughness: 0.66, metalness: 0 };
  }

  // ── CHEVRON ──────────────────────────────────────────────────────────────
  if (id === 'chevron') {
    const bw = 20, bh = 8, gc = mat.grainColor;
    for (let row = -2; row < S / bh + 4; row++) {
      for (let col = -2; col < S / bw + 4; col++) {
        const even = row % 2 === 0;
        ctx.fillStyle = jitter(base, 16);
        ctx.save();
        ctx.translate(col * bw + (even ? 0 : bw * 0.5), row * bh);
        ctx.rotate(even ? Math.PI/4 : -Math.PI/4);
        ctx.fillRect(0, -bh/2, bw-1, bh-1);
        ctx.fillStyle = 'rgba(255,255,255,0.14)'; ctx.fillRect(0, -bh/2, bw-1, 1.5);
        ctx.fillStyle = 'rgba(0,0,0,0.12)';       ctx.fillRect(0, bh/2-2.5, bw-1, 1.5);
        ctx.restore();
      }
    }
    ctx.globalAlpha = 0.05; ctx.strokeStyle = mat.grainColor; ctx.lineWidth = 0.4;
    for (let j = 0; j < 60; j++) { const y=(j/60)*S; ctx.beginPath(); ctx.moveTo(0,y); ctx.lineTo(S,y); ctx.stroke(); }
    ctx.globalAlpha = 1;
    return { texture: tex(canvas, 3, 3), roughness: 0.64, metalness: 0 };
  }

  // ── PARQUET ──────────────────────────────────────────────────────────────
  if (id === 'parquet') {
    const bs = 52, ps = 12, gc = mat.grainColor;
    ctx.fillStyle = base; ctx.fillRect(0, 0, S, S);
    for (let r = -1; r < S/bs+1; r++) {
      for (let c = -1; c < S/bs+1; c++) {
        const horiz = (r+c)%2===0, bx = c*bs, by = r*bs;
        ctx.save(); ctx.beginPath(); ctx.rect(bx, by, bs, bs); ctx.clip();
        for (let p = 0; p < bs; p += ps) {
          ctx.fillStyle = jitter(base, 16);
          if (horiz) ctx.fillRect(bx, by+p, bs, ps-1);
          else       ctx.fillRect(bx+p, by, ps-1, bs);
          ctx.fillStyle = 'rgba(255,255,255,0.12)';
          if (horiz) ctx.fillRect(bx, by+p, bs, 2);
          else       ctx.fillRect(bx+p, by, 2, bs);
        }
        ctx.strokeStyle = gc; ctx.globalAlpha = 0.28; ctx.lineWidth = 1;
        ctx.strokeRect(bx+0.5, by+0.5, bs-1, bs-1);
        ctx.globalAlpha = 1; ctx.restore();
      }
    }
    return { texture: tex(canvas, 3, 3), roughness: 0.62, metalness: 0 };
  }

  // ── BASKETWEAVE ──────────────────────────────────────────────────────────
  if (id === 'basketweave') {
    const bs = 44, ps = 10, gc = mat.grainColor;
    ctx.fillStyle = base; ctx.fillRect(0, 0, S, S);
    // 2x2 basket blocks: each block alternates a pair of horizontal slats
    // interleaved with a pair of vertical slats from the neighbouring block.
    for (let r = -1; r < S / bs + 1; r++) {
      for (let c = -1; c < S / bs + 1; c++) {
        const horiz = (r + c) % 2 === 0;
        const bx = c * bs, by = r * bs;
        ctx.save(); ctx.beginPath(); ctx.rect(bx, by, bs, bs); ctx.clip();
        for (let p = 0; p < bs; p += ps) {
          ctx.fillStyle = jitter(base, 18);
          if (horiz) ctx.fillRect(bx, by + p, bs, ps - 1.5);
          else       ctx.fillRect(bx + p, by, ps - 1.5, bs);
          ctx.fillStyle = 'rgba(255,255,255,0.16)';
          if (horiz) ctx.fillRect(bx, by + p, bs, 1.5);
          else       ctx.fillRect(bx + p, by, 1.5, bs);
          ctx.fillStyle = 'rgba(0,0,0,0.14)';
          if (horiz) ctx.fillRect(bx, by + p + ps - 2.5, bs, 1.5);
          else       ctx.fillRect(bx + p + ps - 2.5, by, 1.5, bs);
        }
        // Woven shadow where this block dips under its neighbours
        ctx.fillStyle = 'rgba(0,0,0,0.10)';
        if (horiz) { ctx.fillRect(bx, by, 3, bs); ctx.fillRect(bx + bs - 3, by, 3, bs); }
        else       { ctx.fillRect(bx, by, bs, 3); ctx.fillRect(bx, by + bs - 3, bs, 3); }
        ctx.restore();
      }
    }
    ctx.strokeStyle = gc; ctx.globalAlpha = 0.3; ctx.lineWidth = 1;
    for (let r = -1; r < S / bs + 1; r++)
      for (let c = -1; c < S / bs + 1; c++)
        ctx.strokeRect(c * bs + 0.5, r * bs + 0.5, bs - 1, bs - 1);
    ctx.globalAlpha = 1;
    return { texture: tex(canvas, 3, 3), roughness: 0.60, metalness: 0 };
  }

  // ── BAMBOO ───────────────────────────────────────────────────────────────
  if (id === 'bamboo') {
    const sh = 18;
    for (let r = 0; r < S/sh+1; r++) {
      ctx.fillStyle = jitter(base, 12);
      ctx.fillRect(0, r*sh, S, sh-2);
      ctx.fillStyle = 'rgba(255,255,255,0.18)'; ctx.fillRect(0, r*sh, S, 2);
      ctx.fillStyle = mat.grainColor; ctx.globalAlpha = 0.32;
      ctx.fillRect(0, r*sh+sh-2, S, 2); ctx.globalAlpha = 1;
    }
    ctx.strokeStyle = mat.grainColor;
    for (let x = 55; x < S; x += 55+Math.random()*25) {
      ctx.globalAlpha = 0.14; ctx.lineWidth = 1.2;
      ctx.beginPath(); ctx.moveTo(x,0); ctx.lineTo(x,S); ctx.stroke();
    }
    ctx.globalAlpha = 1;
    return { texture: tex(canvas, 3, 3), roughness: 0.72, metalness: 0 };
  }

  // ── NATURAL STONE — RANDOM RECTANGULAR (opus / ashlar) ────────────────────
  // Different-sized rectangular flags fitted together (a greedy merged grid, so
  // you get a natural mix of 1×1, 2×1, 1×2, 2×2… stones), each with a real riven
  // /tumbled/sandy surface and recessed grout — the travertine/limestone/
  // sandstone/slate/bluestone look from the reference photos.
  const STONE_RECT_IDS = ['stn_travertine','stn_silver_trav','stn_limestone','stn_sandstone','stn_slate','stn_slate_bl','stn_bluestone'];
  if (STONE_RECT_IDS.includes(id)) {
    const style = mat.stoneStyle || 'tumbled';
    const cs = mat.cell || 148, g = mat.grout || 4;
    const grout = mat.groutColor || rgbStr(br - 30, bg - 32, bb - 34);
    const cols = Math.ceil(S / cs) + 1, rows = Math.ceil(S / cs) + 1;
    const occ = Array.from({ length: rows }, () => new Array(cols).fill(false));
    const fits = (r, c, tw, th) => { for (let i = 0; i < th; i++) for (let j = 0; j < tw; j++) if (r + i >= rows || c + j >= cols || occ[r + i][c + j]) return false; return true; };
    const mark = (r, c, tw, th) => { for (let i = 0; i < th; i++) for (let j = 0; j < tw; j++) occ[r + i][c + j] = true; };
    const SIZES = [[1, 1], [2, 1], [1, 2], [2, 2], [3, 1], [1, 3], [2, 1], [1, 1]];

    ctx.fillStyle = grout; ctx.fillRect(0, 0, S, S);
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        if (occ[r][c]) continue;
        let sw = 1, sh = 1;
        const bag = SIZES.slice().sort(() => Math.random() - 0.5);
        for (const [tw, th] of bag) { if (fits(r, c, tw, th)) { sw = tw; sh = th; break; } }
        mark(r, c, sw, sh);
        const x = c * cs + g / 2, y = r * cs + g / 2, w = sw * cs - g, h = sh * cs - g;
        ctx.save(); ctx.beginPath(); ctx.rect(x, y, w, h); ctx.clip();
        ctx.fillStyle = jitter(base, mat.toneVary ?? 16); ctx.fillRect(x, y, w, h);
        paintStoneFace(ctx, x, y, w, h, base, style);
        ctx.fillStyle = 'rgba(255,255,255,0.10)'; ctx.fillRect(x, y, w, 2); ctx.fillRect(x, y, 2, h);
        ctx.fillStyle = 'rgba(0,0,0,0.18)'; ctx.fillRect(x, y + h - 2, w, 2); ctx.fillRect(x + w - 2, y, 2, h);
        ctx.restore();
      }
    }
    const rough = mat.rough ?? 0.55;
    return { texture: tex(canvas, 2, 2), roughness: rough, metalness: rough < 0.3 ? 0.05 : 0 };
  }

  // ── NATURAL STONE — CRAZY PAVING (irregular flagstone) ────────────────────
  // Irregular flags via a jittered point grid (edges kept straight so it tiles),
  // each an irregular polygon with recessed grout — the random crazy-paving look.
  const STONE_CRAZY_IDS = ['stn_flagstone','stn_flag_autumn'];
  if (STONE_CRAZY_IDS.includes(id)) {
    const style = mat.stoneStyle || 'riven';
    const grout = mat.groutColor || rgbStr(br - 34, bg - 36, bb - 38);
    ctx.fillStyle = grout; ctx.fillRect(0, 0, S, S);
    const n = mat.cellN || 6, cs = S / n, g = mat.grout || 6;
    const pts = [];
    for (let r = 0; r <= n; r++) { pts[r] = []; for (let c = 0; c <= n; c++) {
      const jx = (c === 0 || c === n) ? 0 : (Math.random() - 0.5) * cs * 0.62;
      const jy = (r === 0 || r === n) ? 0 : (Math.random() - 0.5) * cs * 0.62;
      pts[r][c] = { x: c * cs + jx, y: r * cs + jy };
    } }
    for (let r = 0; r < n; r++) { for (let c = 0; c < n; c++) {
      const p = [pts[r][c], pts[r][c + 1], pts[r + 1][c + 1], pts[r + 1][c]];
      const cx = (p[0].x + p[1].x + p[2].x + p[3].x) / 4, cy = (p[0].y + p[1].y + p[2].y + p[3].y) / 4;
      const q = p.map(pt => { const dx = cx - pt.x, dy = cy - pt.y, d = Math.hypot(dx, dy) || 1; return { x: pt.x + dx / d * g, y: pt.y + dy / d * g }; });
      const path = () => { ctx.beginPath(); ctx.moveTo(q[0].x, q[0].y); for (let k = 1; k < 4; k++) ctx.lineTo(q[k].x, q[k].y); ctx.closePath(); };
      ctx.save(); path(); ctx.clip();
      const bx = Math.min(q[0].x, q[1].x, q[2].x, q[3].x), by = Math.min(q[0].y, q[1].y, q[2].y, q[3].y);
      const bw = Math.max(q[0].x, q[1].x, q[2].x, q[3].x) - bx, bh = Math.max(q[0].y, q[1].y, q[2].y, q[3].y) - by;
      ctx.fillStyle = jitter(base, mat.toneVary ?? 26); ctx.fillRect(bx, by, bw, bh);
      paintStoneFace(ctx, bx, by, bw, bh, base, style);
      ctx.restore();
      path(); ctx.strokeStyle = 'rgba(255,255,255,0.10)'; ctx.lineWidth = 1.5; ctx.stroke();
      path(); ctx.strokeStyle = 'rgba(0,0,0,0.16)'; ctx.lineWidth = 0.8; ctx.stroke();
    } }
    return { texture: tex(canvas, 2, 2), roughness: mat.rough ?? 0.58, metalness: 0 };
  }

  // ── PORCELAIN TILES (large-format marble & stone effect) ──────────────────
  // A grid of large tiles, each with its OWN veining/mottling clipped to the
  // tile (like real cut porcelain), thin recessed grout between them, and a
  // polished sheen. tileStyle = 'marble' | 'concrete' | 'stone'.
  const PORCELAIN_IDS = ['porc_carrara','porc_statuario','porc_grey','porc_black','porc_cream','porc_cloud','porc_slate','porc_sand',
    'porc_carrara_sq','porc_grey_sq','porc_stone_sq','porc_sand_sq','porc_mosaic'];
  if (PORCELAIN_IDS.includes(id)) {
    const style = mat.tileStyle || 'marble';
    const vc = mat.veins || '#b6babf';
    const lum = br * 0.299 + bg * 0.587 + bb * 0.114;
    // Grout: a touch darker than the tile (or lighter, on very dark tiles).
    const grout = mat.groutColor || (lum < 72 ? rgbStr(br + 24, bg + 24, bb + 26) : rgbStr(br - 24, bg - 26, bb - 28));
    // tilesN = squares per texture (must divide 1024 so grout aligns at the
    // seamless repeat): 2 = large format, 4 = small square grid, 8 = mosaic.
    const TILES = mat.tilesN || 2, tsz = S / TILES;
    const g = TILES >= 8 ? 3 : (TILES >= 4 ? 4 : 5); // thinner grout on denser grids

    ctx.fillStyle = base; ctx.fillRect(0, 0, S, S);

    for (let ty = 0; ty < TILES; ty++) {
      for (let tx = 0; tx < TILES; tx++) {
        const x = tx * tsz, y = ty * tsz;
        ctx.save();
        ctx.beginPath(); ctx.rect(x, y, tsz, tsz); ctx.clip();
        // Slight per-tile tone shift so adjacent tiles read as separately cut.
        ctx.fillStyle = jitter(base, style === 'marble' ? 6 : 12);
        ctx.fillRect(x, y, tsz, tsz);

        if (style === 'marble') {
          for (let i = 0; i < 12; i++) { // cloudy ground
            const gx = x + Math.random() * tsz, gy = y + Math.random() * tsz;
            const grad = ctx.createRadialGradient(gx, gy, 0, gx, gy, 40 + Math.random() * 110);
            const v = (Math.random() - 0.5) * 14;
            grad.addColorStop(0, `rgba(${br + v | 0},${bg + v | 0},${bb + v | 0},0.18)`); grad.addColorStop(1, 'rgba(0,0,0,0)');
            ctx.fillStyle = grad; ctx.fillRect(x - 40, y - 40, tsz + 80, tsz + 80);
          }
          ctx.strokeStyle = vc; // major diagonal veins
          const nv = 3 + Math.floor(Math.random() * 3);
          for (let i = 0; i < nv; i++) {
            ctx.globalAlpha = 0.12 + Math.random() * 0.24; ctx.lineWidth = 0.6 + Math.random() * 2.8;
            const dir = (Math.random() * 0.6 + 0.2) * Math.PI * (Math.random() < 0.5 ? 1 : -1);
            const x0 = x + Math.random() * tsz, y0 = y + Math.random() * tsz;
            const dx = Math.cos(dir) * tsz * 1.3, dy = Math.sin(dir) * tsz * 1.3;
            ctx.beginPath(); ctx.moveTo(x0 - dx * 0.3, y0 - dy * 0.3);
            ctx.bezierCurveTo(x0 + dx * 0.1 + (Math.random() - 0.5) * 60, y0 + dy * 0.1 + (Math.random() - 0.5) * 60,
              x0 + dx * 0.5 + (Math.random() - 0.5) * 70, y0 + dy * 0.5 + (Math.random() - 0.5) * 70,
              x0 + dx * 0.9, y0 + dy * 0.9);
            ctx.stroke();
          }
          for (let i = 0; i < 9; i++) { // hairline veins
            ctx.globalAlpha = 0.05 + Math.random() * 0.10; ctx.lineWidth = 0.3 + Math.random() * 0.7;
            const x0 = x + Math.random() * tsz, y0 = y + Math.random() * tsz;
            ctx.beginPath(); ctx.moveTo(x0, y0);
            ctx.bezierCurveTo(x0 + (Math.random() - 0.5) * tsz * 0.6, y0 + (Math.random() - 0.5) * tsz * 0.6, x0 + (Math.random() - 0.5) * tsz * 0.9, y0 + (Math.random() - 0.5) * tsz * 0.9, x0 + (Math.random() - 0.5) * tsz * 1.2, y0 + (Math.random() - 0.5) * tsz * 1.2);
            ctx.stroke();
          }
          ctx.strokeStyle = 'rgba(255,255,255,0.16)'; // faint shimmer beside veins
          for (let i = 0; i < 4; i++) {
            ctx.globalAlpha = 0.05 + Math.random() * 0.06; ctx.lineWidth = 0.4;
            const x0 = x + Math.random() * tsz, y0 = y + Math.random() * tsz;
            ctx.beginPath(); ctx.moveTo(x0, y0);
            ctx.bezierCurveTo(x0 + (Math.random() - 0.5) * 90, y0 + (Math.random() - 0.5) * 90, x0 + (Math.random() - 0.5) * 150, y0 + (Math.random() - 0.5) * 150, x0 + (Math.random() - 0.5) * 240, y0 + (Math.random() - 0.5) * 240);
            ctx.stroke();
          }
        } else if (style === 'concrete') {
          for (let i = 0; i < 70; i++) {
            const gx = x + Math.random() * tsz, gy = y + Math.random() * tsz;
            const grad = ctx.createRadialGradient(gx, gy, 0, gx, gy, 20 + Math.random() * 70);
            const v = (Math.random() - 0.5) * 12;
            grad.addColorStop(0, `rgba(${br + v | 0},${bg + v | 0},${bb + v | 0},0.14)`); grad.addColorStop(1, 'rgba(0,0,0,0)');
            ctx.fillStyle = grad; ctx.fillRect(x - 20, y - 20, tsz + 40, tsz + 40);
          }
          for (let i = 0; i < 220; i++) {
            const v = (Math.random() - 0.5) * 10;
            ctx.fillStyle = rgbStr(br + v, bg + v, bb + v); ctx.globalAlpha = 0.09;
            ctx.beginPath(); ctx.arc(x + Math.random() * tsz, y + Math.random() * tsz, 0.5 + Math.random() * 1.6, 0, Math.PI * 2); ctx.fill();
          }
        } else { // stone
          for (let i = 0; i < 44; i++) {
            const gx = x + Math.random() * tsz, gy = y + Math.random() * tsz;
            const grad = ctx.createRadialGradient(gx, gy, 0, gx, gy, 12 + Math.random() * 46);
            const v = (Math.random() - 0.5) * 38;
            grad.addColorStop(0, `rgba(${br + v | 0},${bg + v | 0},${bb + v | 0},0.30)`); grad.addColorStop(1, 'rgba(0,0,0,0)');
            ctx.fillStyle = grad; ctx.fillRect(x - 20, y - 20, tsz + 40, tsz + 40);
          }
          for (let i = 0; i < 420; i++) {
            const v = (Math.random() - 0.5) * 44;
            ctx.fillStyle = rgbStr(br + v, bg + v, bb + v); ctx.globalAlpha = 0.14;
            ctx.beginPath(); ctx.arc(x + Math.random() * tsz, y + Math.random() * tsz, 0.6 + Math.random() * 2.2, 0, Math.PI * 2); ctx.fill();
          }
          ctx.strokeStyle = rgbStr(br - 34, bg - 34, bb - 34);
          for (let i = 0; i < 5; i++) {
            ctx.globalAlpha = 0.08 + Math.random() * 0.08; ctx.lineWidth = 0.4 + Math.random();
            const x0 = x + Math.random() * tsz, y0 = y + Math.random() * tsz;
            ctx.beginPath(); ctx.moveTo(x0, y0); ctx.lineTo(x0 + (Math.random() - 0.5) * tsz * 0.9, y0 + (Math.random() - 0.5) * tsz * 0.9); ctx.stroke();
          }
        }

        if (mat.polished) { // soft diagonal sheen per tile
          const gm = ctx.createLinearGradient(x, y, x + tsz * 0.85, y + tsz * 0.55);
          gm.addColorStop(0, 'rgba(255,255,255,0.12)'); gm.addColorStop(0.45, 'rgba(255,255,255,0.03)'); gm.addColorStop(1, 'rgba(255,255,255,0)');
          ctx.fillStyle = gm; ctx.globalAlpha = 1; ctx.fillRect(x, y, tsz, tsz);
        }
        ctx.globalAlpha = 1; ctx.restore();
      }
    }

    // Grout grid — a recessed groove (grout fill + dark centre + light bevel).
    for (let i = 0; i <= TILES; i++) {
      const p = i * tsz;
      ctx.globalAlpha = 1; ctx.fillStyle = grout;
      ctx.fillRect(p - g, 0, g * 2, S); ctx.fillRect(0, p - g, S, g * 2);
      ctx.fillStyle = 'rgba(0,0,0,0.22)';
      ctx.fillRect(p - 1, 0, 2, S); ctx.fillRect(0, p - 1, S, 2);
      ctx.fillStyle = 'rgba(255,255,255,0.10)';
      ctx.fillRect(p + g, 0, 1.5, S); ctx.fillRect(0, p + g, S, 1.5);
      ctx.fillRect(p - g - 1.5, 0, 1.5, S); ctx.fillRect(0, p - g - 1.5, S, 1.5);
    }
    ctx.globalAlpha = 1;

    const rough = mat.rough ?? (mat.polished ? 0.07 : 0.5);
    return { texture: tex(canvas, 2, 2), roughness: rough, metalness: mat.polished ? 0.05 : 0 };
  }

  // ── MARBLE ───────────────────────────────────────────────────────────────
  if (['marble_w','marble_g','marble_bl','emperador','calacatta'].includes(id)) {
    const vc = mat.veins;
    // Cloudy crystalline background
    for (let i = 0; i < 120; i++) {
      const gx = Math.random()*S, gy = Math.random()*S;
      const grad = ctx.createRadialGradient(gx, gy, 0, gx, gy, 50+Math.random()*160);
      const v = (Math.random()-0.5)*18;
      grad.addColorStop(0, `rgba(${br+v|0},${bg+v|0},${bb+v|0},0.22)`);
      grad.addColorStop(1, 'rgba(0,0,0,0)');
      ctx.fillStyle = grad; ctx.fillRect(gx-180,gy-180,360,360);
    }
    // Major veins - long dramatic sweeps
    ctx.strokeStyle = vc;
    for (let i = 0; i < 14; i++) {
      ctx.globalAlpha = 0.14 + Math.random()*0.26;
      ctx.lineWidth = 0.6 + Math.random()*3.2;
      const x0 = Math.random()*S, y0 = Math.random()*S;
      ctx.beginPath(); ctx.moveTo(x0, y0);
      ctx.bezierCurveTo(
        x0+Math.random()*300-150, y0+Math.random()*300-150,
        x0+Math.random()*500-250, y0+Math.random()*500-250,
        x0+Math.random()*800-400, y0+Math.random()*800-400
      );
      ctx.stroke();
    }
    // Fine hairline veins
    for (let i = 0; i < 30; i++) {
      ctx.globalAlpha = 0.05 + Math.random()*0.14;
      ctx.lineWidth = 0.3 + Math.random()*0.8;
      const x0 = Math.random()*S, y0 = Math.random()*S;
      ctx.beginPath(); ctx.moveTo(x0, y0);
      ctx.bezierCurveTo(x0+Math.random()*120-60, y0+Math.random()*120-60, x0+Math.random()*240-120, y0+Math.random()*240-120, x0+Math.random()*400-200, y0+Math.random()*400-200);
      ctx.stroke();
    }
    // Vein shimmer
    ctx.strokeStyle = 'rgba(255,255,255,0.22)';
    for (let i = 0; i < 8; i++) {
      ctx.globalAlpha = 0.06+Math.random()*0.08; ctx.lineWidth = 0.5;
      const x0=Math.random()*S, y0=Math.random()*S;
      ctx.beginPath(); ctx.moveTo(x0,y0);
      ctx.bezierCurveTo(x0+Math.random()*130-65, y0+Math.random()*90-45, x0+Math.random()*220-110, y0+Math.random()*170-85, x0+Math.random()*360-180, y0+Math.random()*300-150);
      ctx.stroke();
    }
    ctx.globalAlpha = 1;
    const rough = id === 'marble_bl' ? 0.07 : id === 'emperador' ? 0.09 : 0.05;
    return { texture: tex(canvas, 2, 2), roughness: rough, metalness: 0.06 };
  }

  // ── GRANITE (speckled crystal) ────────────────────────────────────────────
  if (['granite_w','granite_bl'].includes(id)) {
    const vc = mat.veins;
    // Mineral crystals
    for (let i = 0; i < 2400; i++) {
      const size = 1 + Math.random() * 9;
      const v = (Math.random() - 0.5) * 45;
      ctx.fillStyle = rgbStr(br + v, bg + v, bb + v);
      ctx.globalAlpha = 0.45 + Math.random() * 0.55;
      ctx.save();
      ctx.translate(Math.random() * S, Math.random() * S);
      ctx.rotate(Math.random() * Math.PI);
      ctx.fillRect(-size / 2, -size * 0.3, size, size * 0.6);
      ctx.restore();
    }
    // Fine speckle
    for (let i = 0; i < 6000; i++) {
      const v = (Math.random() - 0.5) * 32;
      ctx.fillStyle = rgbStr(br + v, bg + v, bb + v);
      ctx.globalAlpha = 0.28;
      ctx.beginPath(); ctx.arc(Math.random()*S, Math.random()*S, 0.5+Math.random()*2.2, 0, Math.PI*2); ctx.fill();
    }
    // Subtle veins
    ctx.strokeStyle = vc;
    for (let i = 0; i < 6; i++) {
      ctx.globalAlpha = 0.08+Math.random()*0.10; ctx.lineWidth = 0.5+Math.random()*1.5;
      const x0=Math.random()*S, y0=Math.random()*S;
      ctx.beginPath(); ctx.moveTo(x0,y0);
      ctx.bezierCurveTo(x0+Math.random()*200-100, y0+Math.random()*200-100, x0+Math.random()*350-175, y0+Math.random()*350-175, x0+Math.random()*550-275, y0+Math.random()*550-275);
      ctx.stroke();
    }
    ctx.globalAlpha = 1;
    return { texture: tex(canvas, 2, 2), roughness: id === 'granite_bl' ? 0.18 : 0.20, metalness: 0.06 };
  }

  // ── ONYX ─────────────────────────────────────────────────────────────────
  if (id === 'onyx') {
    const vc = mat.veins;
    const vcRgb = hexRgb(vc);
    for (let i = 0; i < 90; i++) {
      const gx=Math.random()*S, gy=Math.random()*S;
      const grad = ctx.createRadialGradient(gx,gy,0,gx,gy,55+Math.random()*130);
      const v = (Math.random()-0.5)*14;
      grad.addColorStop(0,`rgba(${br+v|0},${bg+v|0},${bb+v|0},0.24)`);
      grad.addColorStop(1,'rgba(0,0,0,0)');
      ctx.fillStyle=grad; ctx.fillRect(gx-150,gy-150,300,300);
    }
    // Dramatic glowing veins
    ctx.strokeStyle = vc;
    for (let i = 0; i < 16; i++) {
      ctx.globalAlpha = 0.22+Math.random()*0.32; ctx.lineWidth = 0.8+Math.random()*3.0;
      const x0=Math.random()*S, y0=Math.random()*S;
      ctx.beginPath(); ctx.moveTo(x0,y0);
      ctx.bezierCurveTo(x0+Math.random()*260-130, y0+Math.random()*260-130, x0+Math.random()*380-190, y0+Math.random()*380-190, x0+Math.random()*650-325, y0+Math.random()*650-325);
      ctx.stroke();
    }
    // Vein glow highlight
    ctx.strokeStyle = rgbStr(Math.min(255,vcRgb.r+35),Math.min(255,vcRgb.g+35),Math.min(255,vcRgb.b+35));
    for (let i = 0; i < 8; i++) {
      ctx.globalAlpha = 0.10+Math.random()*0.10; ctx.lineWidth = 0.4;
      const x0=Math.random()*S, y0=Math.random()*S;
      ctx.beginPath(); ctx.moveTo(x0,y0);
      ctx.bezierCurveTo(x0+Math.random()*110-55, y0+Math.random()*110-55, x0+Math.random()*220-110, y0+Math.random()*220-110, x0+Math.random()*420-210, y0+Math.random()*420-210);
      ctx.stroke();
    }
    ctx.globalAlpha = 1;
    return { texture: tex(canvas, 2, 2), roughness: 0.05, metalness: 0.08 };
  }

  // ── LIMESTONE ────────────────────────────────────────────────────────────
  if (id === 'limestone') {
    // Fine sedimentary layering
    for (let i = 0; i < 400; i++) {
      ctx.fillStyle = jitter(base, 14); ctx.globalAlpha = 0.14;
      const w = 30+Math.random()*100;
      ctx.fillRect(Math.random()*S, Math.random()*S, w, 2+Math.random()*5);
    }
    for (let i = 0; i < 2500; i++) {
      const v = (Math.random()-0.5)*18;
      ctx.fillStyle = rgbStr(br+v,bg+v,bb+v); ctx.globalAlpha = 0.10;
      ctx.fillRect(Math.random()*S, Math.random()*S, 1+Math.random()*3, 1+Math.random()*3);
    }
    ctx.globalAlpha = 1;
    return { texture: tex(canvas, 2, 2), roughness: 0.84, metalness: 0 };
  }

  // ── JERUSALEM STONE ───────────────────────────────────────────────────────
  if (id === 'jerusalem') {
    const gc2 = mat.veins || '#c0a870', sw=160, sh=80, g=5;
    ctx.fillStyle = gc2; ctx.fillRect(0,0,S,S);
    for (let r=0; r<S/sh+1; r++) {
      const off=(r%2)*(sw/2);
      for (let c=-1; c<S/sw+1; c++) {
        ctx.fillStyle = jitter(base,20); ctx.globalAlpha=1;
        ctx.fillRect(c*sw+off+g, r*sh+g, sw-g*2, sh-g*2);
        ctx.fillStyle='rgba(255,255,255,0.12)'; ctx.fillRect(c*sw+off+g, r*sh+g, sw-g*2, 3);
        ctx.fillStyle='rgba(0,0,0,0.10)'; ctx.fillRect(c*sw+off+g, r*sh+sh-g-3, sw-g*2, 3);
      }
    }
    for (let i=0; i<800; i++) {
      ctx.fillStyle=`rgba(0,0,0,${Math.random()*0.020})`; ctx.fillRect(Math.random()*S, Math.random()*S, Math.random()*4, Math.random()*4);
    }
    ctx.globalAlpha=1;
    return { texture: tex(canvas, 2, 2), roughness: 0.82, metalness: 0 };
  }

  // ── TRAVERTINE ───────────────────────────────────────────────────────────
  if (id === 'travertine') {
    const vc = mat.veins, tw = 200, th = 100, g = 5;
    ctx.fillStyle = mat.groutColor || '#b89060'; ctx.fillRect(0, 0, S, S);
    for (let r = 0; r < S/th+1; r++) {
      const off = (r%2)*(tw/2);
      for (let c = -1; c < S/tw+1; c++) {
        ctx.fillStyle = jitter(base, 16); ctx.globalAlpha = 1;
        ctx.fillRect(c*tw+off+g, r*th+g, tw-g*2, th-g*2);
        ctx.fillStyle = 'rgba(255,255,255,0.14)';
        ctx.fillRect(c*tw+off+g, r*th+g, tw-g*2, 3); ctx.fillRect(c*tw+off+g, r*th+g, 3, th-g*2);
        ctx.fillStyle = 'rgba(0,0,0,0.12)';
        ctx.fillRect(c*tw+off+g, r*th+th-g-3, tw-g*2, 3); ctx.fillRect(c*tw+off+tw-g-3, r*th+g, 3, th-g*2);
      }
    }
    ctx.strokeStyle = vc;
    for (let i = 0; i < 16; i++) {
      ctx.globalAlpha = 0.10+Math.random()*0.10; ctx.lineWidth = 0.5+Math.random()*1.5;
      const x0=Math.random()*S, y0=Math.random()*S;
      ctx.beginPath(); ctx.moveTo(x0,y0);
      ctx.bezierCurveTo(x0+Math.random()*100-50, y0+Math.random()*80-40, x0+Math.random()*200-100, y0+Math.random()*160-80, x0+Math.random()*360-180, y0+Math.random()*280-140);
      ctx.stroke();
    }
    for (let i = 0; i < 90; i++) {
      ctx.fillStyle = vc; ctx.globalAlpha = 0.14+Math.random()*0.12;
      ctx.beginPath(); ctx.arc(Math.random()*S, Math.random()*S, 0.8+Math.random()*2.8, 0, Math.PI*2); ctx.fill();
    }
    ctx.globalAlpha = 1;
    return { texture: tex(canvas, 2, 2), roughness: 0.42, metalness: 0 };
  }

  // ── SLATE ────────────────────────────────────────────────────────────────
  if (id === 'slate') {
    const gc2 = mat.groutColor, sw=110, sh=55, g=4;
    ctx.fillStyle = gc2; ctx.fillRect(0, 0, S, S);
    for (let r = 0; r < S/sh+1; r++) {
      const off = (r%2)*(sw/2);
      for (let c = -1; c < S/sw+1; c++) {
        ctx.fillStyle = jitter(base, 26); ctx.globalAlpha = 1;
        ctx.fillRect(c*sw+off+g, r*sh+g, sw-g*2, sh-g*2);
        ctx.strokeStyle = jitter(base, 10); ctx.globalAlpha = 0.18; ctx.lineWidth = 0.5;
        for (let l = 0; l < 8; l++) {
          const ly = r*sh+g+Math.random()*(sh-g*2);
          ctx.beginPath(); ctx.moveTo(c*sw+off+g, ly); ctx.lineTo(c*sw+off+sw-g*2, ly+(Math.random()-0.5)*5); ctx.stroke();
        }
        ctx.fillStyle = 'rgba(255,255,255,0.10)'; ctx.globalAlpha = 1;
        ctx.fillRect(c*sw+off+g, r*sh+g, sw-g*2, 2);
        ctx.fillStyle = 'rgba(0,0,0,0.16)';
        ctx.fillRect(c*sw+off+g, r*sh+sh-g-2, sw-g*2, 2); ctx.fillRect(c*sw+off+sw-g-2, r*sh+g, 2, sh-g*2);
      }
    }
    ctx.globalAlpha = 1;
    return { texture: tex(canvas, 2, 2), roughness: 0.90, metalness: 0 };
  }

  // ── CONCRETE / MICROCEMENT ───────────────────────────────────────────────
  if (id === 'concrete' || id === 'microcement') {
    for (let i = 0; i < 700; i++) {
      ctx.fillStyle = `rgba(0,0,0,${Math.random()*0.032})`; ctx.fillRect(Math.random()*S, Math.random()*S, 18+Math.random()*55, 18+Math.random()*55);
    }
    for (let i = 0; i < 250; i++) {
      ctx.fillStyle = `rgba(255,255,255,${Math.random()*0.026})`; ctx.fillRect(Math.random()*S, Math.random()*S, 10+Math.random()*32, 10+Math.random()*32);
    }
    for (let i = 0; i < 600; i++) {
      const v = (Math.random()-0.5)*22;
      ctx.fillStyle = rgbStr(br+v,bg+v,bb+v); ctx.globalAlpha = 0.28;
      ctx.beginPath(); ctx.arc(Math.random()*S, Math.random()*S, 0.7+Math.random()*2.4, 0, Math.PI*2); ctx.fill();
    }
    ctx.globalAlpha = 1;
    ctx.strokeStyle = 'rgba(0,0,0,0.07)'; ctx.lineWidth = 1.5;
    // Raw concrete keeps the poured-slab control joints; seamless microcement is
    // a continuous troweled surface, so skip the joint lines and give it a
    // softer sealed sheen instead of concrete's dead-matte finish.
    if (id !== 'microcement') {
      ctx.beginPath(); ctx.moveTo(S/2,0); ctx.lineTo(S/2,S); ctx.stroke();
      ctx.beginPath(); ctx.moveTo(0,S/2); ctx.lineTo(S,S/2); ctx.stroke();
    }
    return { texture: tex(canvas, 2, 2), roughness: id === 'microcement' ? 0.55 : 0.92, metalness: 0 };
  }

  // ── SANDSTONE ────────────────────────────────────────────────────────────
  if (id === 'sandstone') {
    const gc2 = mat.groutColor || '#b89060', sw=160, sh=80, g=5;
    ctx.fillStyle = gc2; ctx.fillRect(0, 0, S, S);
    for (let r = 0; r < S/sh+1; r++) {
      const off = (r%2)*(sw/2);
      for (let c = -1; c < S/sw+1; c++) {
        ctx.fillStyle = jitter(base, 22); ctx.globalAlpha=1;
        ctx.fillRect(c*sw+off+g, r*sh+g, sw-g*2, sh-g*2);
        ctx.fillStyle = 'rgba(255,255,255,0.12)'; ctx.fillRect(c*sw+off+g, r*sh+g, sw-g*2, 3);
        ctx.fillStyle = 'rgba(0,0,0,0.12)';       ctx.fillRect(c*sw+off+g, r*sh+sh-g-3, sw-g*2, 3);
      }
    }
    for (let i = 0; i < 700; i++) {
      ctx.fillStyle = `rgba(0,0,0,${Math.random()*0.022})`; ctx.fillRect(Math.random()*S, Math.random()*S, Math.random()*4, Math.random()*4);
    }
    ctx.globalAlpha = 1;
    return { texture: tex(canvas, 2, 2), roughness: 0.86, metalness: 0 };
  }

  // ── TERRACOTTA ───────────────────────────────────────────────────────────
  if (id === 'terracotta') {
    const tw2=64, th2=32, g=3;
    ctx.fillStyle = mat.groutColor; ctx.fillRect(0, 0, S, S);
    for (let r = 0; r < S/th2+1; r++) {
      const off = (r%2)*(tw2/2);
      for (let c = -1; c < S/tw2+1; c++) {
        ctx.fillStyle = jitter(base, 18); ctx.globalAlpha=1;
        ctx.fillRect(c*tw2+off+g/2, r*th2+g/2, tw2-g, th2-g);
        ctx.fillStyle = 'rgba(255,255,255,0.14)'; ctx.fillRect(c*tw2+off+g/2, r*th2+g/2, tw2-g, 2.5);
        ctx.fillStyle = 'rgba(0,0,0,0.14)';       ctx.fillRect(c*tw2+off+g/2, r*th2+th2-g/2-3, tw2-g, 2.5);
      }
    }
    return { texture: tex(canvas, 3, 3), roughness: 0.70, metalness: 0 };
  }

  // ── TERRAZZO ─────────────────────────────────────────────────────────────
  if (id === 'terrazzo') {
    const ac = mat.altColor;
    for (let i = 0; i < 900; i++) {
      const chipColors = [base, ac, jitter(base, 32), jitter(ac, 28), '#ffffff', '#e0e0d8'];
      ctx.fillStyle = chipColors[Math.floor(Math.random() * chipColors.length)];
      ctx.globalAlpha = 0.65 + Math.random() * 0.35;
      const size = 3 + Math.random() * 15;
      ctx.save();
      ctx.translate(Math.random() * S, Math.random() * S);
      ctx.rotate(Math.random() * Math.PI);
      const pts = 4 + Math.floor(Math.random() * 4);
      ctx.beginPath();
      for (let p = 0; p < pts; p++) {
        const angle = (p / pts) * Math.PI * 2 + Math.random() * 0.5;
        const rr = size * (0.4 + Math.random() * 0.6);
        p === 0 ? ctx.moveTo(Math.cos(angle)*rr, Math.sin(angle)*rr) : ctx.lineTo(Math.cos(angle)*rr, Math.sin(angle)*rr);
      }
      ctx.closePath(); ctx.fill();
      ctx.restore();
    }
    ctx.globalAlpha = 1;
    return { texture: tex(canvas, 2, 2), roughness: 0.20, metalness: 0.04 };
  }

  // ── CHECKERS ─────────────────────────────────────────────────────────────
  if (id === 'checker' || id === 'checker_cl') {
    const sq = 52;
    ctx.fillStyle = base; ctx.fillRect(0, 0, S, S);
    ctx.fillStyle = mat.altColor;
    for (let r = 0; r < S/sq; r++)
      for (let c = 0; c < S/sq; c++)
        if ((r+c)%2===1) ctx.fillRect(c*sq, r*sq, sq, sq);
    for (let r = 0; r < S/sq; r++) {
      for (let c = 0; c < S/sq; c++) {
        ctx.fillStyle = 'rgba(255,255,255,0.10)';
        ctx.beginPath(); ctx.moveTo(c*sq+3,r*sq+3); ctx.lineTo(c*sq+sq*0.58,r*sq+3); ctx.lineTo(c*sq+3,r*sq+sq*0.58); ctx.closePath(); ctx.fill();
        ctx.fillStyle = 'rgba(0,0,0,0.14)';
        ctx.fillRect(c*sq, r*sq+sq-2, sq, 2); ctx.fillRect(c*sq+sq-2, r*sq, 2, sq);
      }
    }
    const rough = id === 'checker' ? 0.28 : 0.40;
    return { texture: tex(canvas, 4, 4), roughness: rough, metalness: id==='checker' ? 0.06 : 0 };
  }

  // ── HEXAGONS ─────────────────────────────────────────────────────────────
  if (id === 'hex_white' || id === 'hex_black') {
    const hr = 28, hexH = Math.sqrt(3)*hr;
    ctx.fillStyle = mat.groutColor; ctx.fillRect(0, 0, S, S);
    for (let r = -1; r < S/hexH+2; r++) {
      for (let c = -1; c < S/(hr*1.5)+2; c++) {
        const cx2 = c*hr*3+(r%2===0?0:hr*1.5), cy2 = r*hexH;
        ctx.fillStyle = id==='hex_black' ? jitter(base,10) : jitter(base,7);
        ctx.beginPath();
        for (let i = 0; i < 6; i++) {
          const a = (Math.PI/3)*i-Math.PI/6;
          i===0 ? ctx.moveTo(cx2+hr*0.88*Math.cos(a), cy2+hr*0.88*Math.sin(a)) : ctx.lineTo(cx2+hr*0.88*Math.cos(a), cy2+hr*0.88*Math.sin(a));
        }
        ctx.closePath(); ctx.fill();
        ctx.fillStyle = 'rgba(255,255,255,0.14)';
        ctx.beginPath();
        ctx.moveTo(cx2, cy2-hr*0.88);
        ctx.lineTo(cx2+hr*0.76*Math.cos(Math.PI/6), cy2-hr*0.88+hr*0.5);
        ctx.lineTo(cx2-hr*0.76*Math.cos(Math.PI/6), cy2-hr*0.88+hr*0.5);
        ctx.closePath(); ctx.fill();
      }
    }
    return { texture: tex(canvas, 4, 4), roughness: id==='hex_black' ? 0.22 : 0.20, metalness: 0.06 };
  }

  // ── PENNY ROUND TILE ─────────────────────────────────────────────────────
  if (id === 'penny_tile') {
    const pr = 13, rowH = pr * 1.75;
    ctx.fillStyle = mat.groutColor; ctx.fillRect(0, 0, S, S);
    for (let r = -1; r < S / rowH + 2; r++) {
      for (let c = -1; c < S / (pr * 2) + 2; c++) {
        const cx2 = c * pr * 2 + (r % 2 === 0 ? 0 : pr), cy2 = r * rowH;
        // A handful of accent pennies scattered through an otherwise uniform field
        const isAccent = Math.random() < 0.045;
        ctx.fillStyle = isAccent ? rgbStr(br * 0.72, bg * 0.72, bb * 0.72) : jitter(base, 9);
        ctx.beginPath(); ctx.arc(cx2, cy2, pr * 0.86, 0, Math.PI * 2); ctx.fill();
        const gm = ctx.createRadialGradient(cx2 - pr*0.3, cy2 - pr*0.3, 0, cx2, cy2, pr);
        gm.addColorStop(0, 'rgba(255,255,255,0.30)');
        gm.addColorStop(0.55, 'rgba(255,255,255,0.05)');
        gm.addColorStop(1, 'rgba(0,0,0,0.10)');
        ctx.fillStyle = gm;
        ctx.beginPath(); ctx.arc(cx2, cy2, pr * 0.86, 0, Math.PI * 2); ctx.fill();
      }
    }
    return { texture: tex(canvas, 4, 4), roughness: 0.16, metalness: 0.05 };
  }

  // ── METRO TILE ───────────────────────────────────────────────────────────
  if (id === 'metro') {
    const mw=100, mh=50, g=4;
    ctx.fillStyle = mat.groutColor; ctx.fillRect(0, 0, S, S);
    for (let r = 0; r < S/mh+1; r++) {
      const off = (r%2)*(mw/2);
      for (let c = -1; c < S/mw+1; c++) {
        const gx=c*mw+off+g/2, gy=r*mh+g/2, tw2=mw-g, th2=mh-g;
        ctx.fillStyle = jitter(base, 8); ctx.globalAlpha=1;
        ctx.fillRect(gx, gy, tw2, th2);
        const gm = ctx.createLinearGradient(gx, gy, gx+tw2, gy+th2);
        gm.addColorStop(0,'rgba(255,255,255,0.24)'); gm.addColorStop(0.4,'rgba(255,255,255,0.08)'); gm.addColorStop(1,'rgba(255,255,255,0)');
        ctx.fillStyle=gm; ctx.fillRect(gx,gy,tw2,th2);
        ctx.fillStyle='rgba(0,0,0,0.12)';
        ctx.fillRect(gx, gy+th2-2, tw2, 2); ctx.fillRect(gx+tw2-2, gy, 2, th2);
      }
    }
    return { texture: tex(canvas, 3, 3), roughness: 0.12, metalness: 0.08 };
  }

  // ── CEMENT TILE ──────────────────────────────────────────────────────────
  if (id === 'cement_tile') {
    const ts=72, g=3;
    ctx.fillStyle = mat.groutColor; ctx.fillRect(0, 0, S, S);
    for (let r = 0; r < S/ts+1; r++) {
      for (let c = 0; c < S/ts+1; c++) {
        const tx2=c*ts+g, ty=r*ts+g, tw2=ts-g*2, th2=ts-g*2;
        ctx.fillStyle=jitter(base,10); ctx.globalAlpha=1; ctx.fillRect(tx2,ty,tw2,th2);
        ctx.fillStyle=mat.altColor; ctx.globalAlpha=0.58;
        ctx.fillRect(tx2+tw2*0.25, ty+th2*0.25, tw2*0.5, th2*0.5);
        ctx.globalAlpha=0.22;
        ctx.fillRect(tx2,ty,tw2,th2*0.12); ctx.fillRect(tx2,ty+th2*0.88,tw2,th2*0.12);
        ctx.fillRect(tx2,ty,tw2*0.12,th2); ctx.fillRect(tx2+tw2*0.88,ty,tw2*0.12,th2);
        ctx.fillStyle='rgba(255,255,255,0.12)'; ctx.globalAlpha=1;
        ctx.fillRect(tx2,ty,tw2,2.5); ctx.fillRect(tx2,ty,2.5,th2);
        ctx.fillStyle='rgba(0,0,0,0.10)';
        ctx.fillRect(tx2,ty+th2-2.5,tw2,2.5); ctx.fillRect(tx2+tw2-2.5,ty,2.5,th2);
      }
    }
    return { texture: tex(canvas, 4, 4), roughness: 0.78, metalness: 0 };
  }

  // ── ENCAUSTIC ────────────────────────────────────────────────────────────
  if (id === 'encaustic') {
    const ac=mat.altColor, gc3=mat.groutColor, ts=64, g=4;
    ctx.fillStyle=gc3; ctx.fillRect(0,0,S,S);
    for (let r=0; r<S/ts+1; r++) {
      for (let c=0; c<S/ts+1; c++) {
        const tx2=c*ts+g, ty=r*ts+g, tw2=ts-g*2, th2=ts-g*2;
        ctx.fillStyle=jitter(base,8); ctx.globalAlpha=1; ctx.fillRect(tx2,ty,tw2,th2);
        const cx2=tx2+tw2/2, cy2=ty+th2/2, ds=tw2*0.32;
        ctx.fillStyle=ac; ctx.globalAlpha=0.85;
        ctx.beginPath(); ctx.moveTo(cx2,cy2-ds); ctx.lineTo(cx2+ds,cy2); ctx.lineTo(cx2,cy2+ds); ctx.lineTo(cx2-ds,cy2); ctx.closePath(); ctx.fill();
        ctx.globalAlpha=0.55;
        const dts=tw2*0.12;
        for (const [dx2,dy2] of [[tx2+dts,ty+dts],[tx2+tw2-dts,ty+dts],[tx2+dts,ty+th2-dts],[tx2+tw2-dts,ty+th2-dts]]) {
          ctx.beginPath(); ctx.arc(dx2,dy2,dts*0.6,0,Math.PI*2); ctx.fill();
        }
        ctx.globalAlpha=1;
        ctx.fillStyle='rgba(255,255,255,0.10)'; ctx.fillRect(tx2,ty,tw2,2);
        ctx.fillStyle='rgba(0,0,0,0.10)'; ctx.fillRect(tx2,ty+th2-2,tw2,2);
      }
    }
    return { texture: tex(canvas, 3, 3), roughness: 0.74, metalness: 0 };
  }

  // ── ZELLIGE ──────────────────────────────────────────────────────────────
  if (id === 'zellige') {
    const gc3=mat.groutColor, ts=26, g=2;
    ctx.fillStyle=gc3; ctx.fillRect(0,0,S,S);
    for (let r=0; r<S/ts+2; r++) {
      for (let c=0; c<S/ts+2; c++) {
        const jx=(Math.random()-0.5)*2.5, jy=(Math.random()-0.5)*2.5;
        const tx2=c*ts+g+jx, ty=r*ts+g+jy, tw2=ts-g*2, th2=ts-g*2;
        ctx.fillStyle=jitter(base,20); ctx.globalAlpha=1;
        ctx.fillRect(tx2,ty,tw2,th2);
        const gm=ctx.createLinearGradient(tx2,ty,tx2,ty+th2);
        gm.addColorStop(0,'rgba(255,255,255,0.24)'); gm.addColorStop(0.5,'rgba(255,255,255,0.06)'); gm.addColorStop(1,'rgba(255,255,255,0)');
        ctx.fillStyle=gm; ctx.fillRect(tx2,ty,tw2,th2);
      }
    }
    return { texture: tex(canvas, 4, 4), roughness: 0.15, metalness: 0.06 };
  }

  // ── MOSAIC ───────────────────────────────────────────────────────────────
  if (id === 'mosaic') {
    const gc3=mat.groutColor, ac=mat.altColor, ts=13, g=2;
    ctx.fillStyle=gc3; ctx.fillRect(0,0,S,S);
    for (let r=0; r<S/ts+1; r++) {
      for (let c=0; c<S/ts+1; c++) {
        const mix=Math.random()>0.55;
        ctx.fillStyle=mix ? jitter(ac,14) : jitter(base,16);
        ctx.globalAlpha=1;
        ctx.fillRect(c*ts+g, r*ts+g, ts-g*2, ts-g*2);
        const gm=ctx.createLinearGradient(c*ts+g,r*ts+g,c*ts+ts,r*ts+ts);
        gm.addColorStop(0,'rgba(255,255,255,0.22)'); gm.addColorStop(1,'rgba(255,255,255,0)');
        ctx.fillStyle=gm; ctx.fillRect(c*ts+g,r*ts+g,ts-g*2,ts-g*2);
      }
    }
    return { texture: tex(canvas, 5, 5), roughness: 0.18, metalness: 0.06 };
  }

  // ── WOOD-LOOK TILE ───────────────────────────────────────────────────────
  if (id === 'woodlook') {
    const gc3=mat.grainColor, pw=78, g=3;
    const nc=Math.ceil(S/pw)+2;
    ctx.fillStyle='#666'; ctx.fillRect(0,0,S,S);
    for (let i=0; i<nc; i++) {
      ctx.fillStyle=jitter(base,14); ctx.globalAlpha=1;
      ctx.fillRect(i*pw+g/2, 0, pw-g, S);
      // Printed grain (subtle, regular - it's a tile)
      ctx.strokeStyle=gc3;
      for (let j=0; j<20; j++) {
        const y=(j/20)*S; ctx.globalAlpha=0.06+Math.random()*0.05; ctx.lineWidth=0.5;
        ctx.beginPath(); ctx.moveTo(i*pw+g/2,y); ctx.lineTo(i*pw+pw-g/2,y+(Math.random()-0.5)*6); ctx.stroke();
      }
      ctx.globalAlpha=1;
    }
    const gm=ctx.createLinearGradient(0,0,S,S*0.25);
    gm.addColorStop(0,'rgba(255,255,255,0.07)'); gm.addColorStop(1,'rgba(255,255,255,0)');
    ctx.fillStyle=gm; ctx.fillRect(0,0,S,S);
    return { texture: tex(canvas, 2, 2), roughness: 0.20, metalness: 0.03 };
  }

  // ── CARPET (all types) ───────────────────────────────────────────────────
  const CARPET_IDS = ['carpet_grey','carpet_beige','carpet_navy','carpet_sage','carpet_plush','carpet_choc','carpet_char','carpet_teal','carpet_bur','sisal','berber'];
  if (CARPET_IDS.includes(id)) {
    const isSisal = id === 'sisal';
    const isBerber = id === 'berber';
    // Directional pile rows
    for (let y = 0; y < S; y += isSisal ? 6 : 3) {
      ctx.fillStyle = jitter(base, isSisal ? 20 : 28); ctx.globalAlpha = 0.70;
      ctx.fillRect(0, y, S, isSisal ? 5 : 2);
      ctx.fillStyle = 'rgba(0,0,0,1)'; ctx.globalAlpha = 0.10;
      ctx.fillRect(0, y + (isSisal ? 5 : 2), S, 1);
    }
    if (isSisal) {
      // Sisal: crosshatch weave
      for (let x = 0; x < S; x += 6) {
        ctx.fillStyle = jitter(base, 18); ctx.globalAlpha = 0.35;
        ctx.fillRect(x, 0, 2, S);
      }
    } else if (isBerber) {
      // Berber: dense small flecked loops (ring shapes, not cut-pile dots) —
      // multi-tone flecks are the signature look of a real berber weave.
      for (let i = 0; i < 4200; i++) {
        const cx2 = Math.random() * S, cy2 = Math.random() * S, r = 1.4 + Math.random() * 2.0;
        const fleck = Math.random() < 0.22; // occasional darker fleck yarn
        ctx.strokeStyle = jitter(base, fleck ? 46 : 24);
        ctx.globalAlpha = 0.5 + Math.random() * 0.4;
        ctx.lineWidth = 0.8 + Math.random() * 0.6;
        ctx.beginPath(); ctx.arc(cx2, cy2, r, 0, Math.PI * 2); ctx.stroke();
      }
      ctx.globalAlpha = 0.08; ctx.fillStyle = 'rgba(0,0,0,1)';
      for (let i = 0; i < 900; i++) {
        ctx.beginPath(); ctx.arc(Math.random()*S, Math.random()*S, 2.5, 0, Math.PI*2); ctx.fill();
      }
    } else {
      // Carpet: pile dots
      for (let i = 0; i < 5500; i++) {
        ctx.fillStyle = jitter(base, 32); ctx.globalAlpha = 0.40+Math.random()*0.54;
        const cx2=Math.random()*S, cy2=Math.random()*S, r=0.6+Math.random()*1.7;
        ctx.beginPath(); ctx.arc(cx2,cy2,r,0,Math.PI*2); ctx.fill();
      }
    }
    ctx.globalAlpha = 1;
    return { texture: tex(canvas, 4, 4), roughness: isSisal ? 0.92 : isBerber ? 0.90 : 0.98, metalness: 0 };
  }

  // ── LUXURY VINYL OAK ─────────────────────────────────────────────────────
  if (id === 'luxury_oak') {
    const gc3=mat.grainColor, pw=76;
    const nc=Math.ceil(S/pw)+2;
    for (let i=0; i<nc; i++) {
      ctx.fillStyle=jitter(base,12); ctx.globalAlpha=1;
      ctx.fillRect(i*pw, 0, pw-2, S);
      ctx.fillStyle='rgba(255,255,255,0.14)'; ctx.fillRect(i*pw,0,2,S);
      ctx.fillStyle='rgba(0,0,0,0.12)'; ctx.fillRect(i*pw+pw-3,0,2,S);
    }
    ctx.strokeStyle=gc3;
    for (let j=0; j<60; j++) {
      const y=(j/60)*S; ctx.globalAlpha=0.05+Math.random()*0.06; ctx.lineWidth=0.4;
      ctx.beginPath(); ctx.moveTo(0,y); ctx.lineTo(S,y+(Math.random()-0.5)*5); ctx.stroke();
    }
    ctx.globalAlpha=0.26; ctx.fillStyle=gc3;
    for (let i=0; i<nc; i++) ctx.fillRect(i*pw-1,0,2,S);
    ctx.globalAlpha=1;
    return { texture: tex(canvas, 3, 3), roughness: 0.52, metalness: 0 };
  }

  // ── LUXURY VINYL GREY ────────────────────────────────────────────────────
  if (id === 'luxury_grey') {
    for (let i=0; i<500; i++) {
      ctx.fillStyle=jitter(base,10); ctx.globalAlpha=0.12;
      ctx.fillRect(Math.random()*S, Math.random()*S, 18+Math.random()*52, 2+Math.random()*4);
    }
    ctx.globalAlpha=1;
    return { texture: tex(canvas, 3, 3), roughness: 0.48, metalness: 0 };
  }

  // ── VINYL STONE ──────────────────────────────────────────────────────────
  if (id === 'luxury_stone') {
    for (let i=0; i<60; i++) {
      const gx=Math.random()*S, gy=Math.random()*S;
      const grad=ctx.createRadialGradient(gx,gy,0,gx,gy,60+Math.random()*110);
      const v=(Math.random()-0.5)*12;
      grad.addColorStop(0,`rgba(${br+v|0},${bg+v|0},${bb+v|0},0.18)`); grad.addColorStop(1,'rgba(0,0,0,0)');
      ctx.fillStyle=grad; ctx.fillRect(gx-130,gy-130,260,260);
    }
    ctx.globalAlpha=1;
    return { texture: tex(canvas, 2, 2), roughness: 0.50, metalness: 0 };
  }

  // ── CORK ─────────────────────────────────────────────────────────────────
  if (id === 'cork_floor') {
    for (let i=0; i<700; i++) {
      const cx2=Math.random()*S, cy2=Math.random()*S;
      const rx=5+Math.random()*13, ry=4+Math.random()*10;
      const v=(Math.random()-0.5)*22;
      ctx.strokeStyle=rgbStr(br+v-12,bg+v-12,bb+v-12);
      ctx.fillStyle=rgbStr(br+v,bg+v,bb+v);
      ctx.globalAlpha=0.45+Math.random()*0.55; ctx.lineWidth=0.8+Math.random()*0.6;
      ctx.beginPath(); ctx.ellipse(cx2,cy2,rx,ry,Math.random()*Math.PI,0,Math.PI*2);
      ctx.fill(); ctx.stroke();
    }
    ctx.globalAlpha=1;
    return { texture: tex(canvas, 3, 3), roughness: 0.86, metalness: 0 };
  }

  // ── EPOXY ────────────────────────────────────────────────────────────────
  if (id === 'epoxy_grey' || id === 'epoxy_white') {
    for (let i=0; i<2500; i++) {
      const v=(Math.random()-0.5)*8;
      ctx.fillStyle=rgbStr(br+v,bg+v,bb+v); ctx.globalAlpha=0.05;
      ctx.fillRect(Math.random()*S, Math.random()*S, 2+Math.random()*10, 1);
    }
    ctx.globalAlpha=1;
    const swirl=ctx.createRadialGradient(S*0.35,S*0.4,0,S*0.35,S*0.4,S*0.75);
    swirl.addColorStop(0,'rgba(255,255,255,0.05)'); swirl.addColorStop(0.5,'rgba(255,255,255,0)'); swirl.addColorStop(1,'rgba(0,0,0,0.02)');
    ctx.fillStyle=swirl; ctx.fillRect(0,0,S,S);
    return { texture: tex(canvas, 2, 2), roughness: 0.05, metalness: 0.05 };
  }

  // fallback solid
  return { texture: tex(canvas, 4, 4), roughness: 0.85, metalness: 0 };
}

// PBR props for a chosen finish — shared by all three layers so a finish
// picked on top of a recoloured preset (see buildStyleColorOverride in
// houseSlice.js) can override just the roughness/metalness of the pattern
// texture that preset's own generator already drew.
export function finishToPBR(finish) {
  switch (finish) {
    case 'gloss':         return { roughness: 0.04, metalness: 0.04 };
    case 'semi_gloss':    return { roughness: 0.24, metalness: 0.02 };
    case 'satin':         return { roughness: 0.55, metalness: 0 };
    case 'metallic':      return { roughness: 0.18, metalness: 0.82 };
    case 'brushed_metal': return { roughness: 0.30, metalness: 0.88 };
    case 'pearl':         return { roughness: 0.34, metalness: 0.22 };
    case 'velvet':        return { roughness: 0.98, metalness: 0 };
    case 'concrete':      return { roughness: 0.96, metalness: 0 };
    default:              return { roughness: 0.92, metalness: 0 }; // matte
  }
}

export function makeWallTexture(mat) {
  if (!mat) return null;
  if (mat.__custom) {
    const S = 512;
    const cv = document.createElement('canvas');
    cv.width = cv.height = S;
    const cx = cv.getContext('2d');
    const base = mat.baseColor || mat.color || '#d4c4a8';
    const { r: br, g: bg, b: bb } = hexRgb(base);
    cx.fillStyle = base; cx.fillRect(0, 0, S, S);

    const finish = mat.finish || 'matte';

    if (finish === 'velvet') {
      // Velvet pile — horizontal rows of slightly varied hue
      for (let y = 0; y < S; y += 3) {
        cx.fillStyle = jitter(base, 18); cx.globalAlpha = 0.55;
        cx.fillRect(0, y, S, 2);
        cx.fillStyle = 'rgba(0,0,0,1)'; cx.globalAlpha = 0.10;
        cx.fillRect(0, y + 2, S, 1);
      }
      for (let i = 0; i < 3000; i++) {
        cx.fillStyle = jitter(base, 24); cx.globalAlpha = 0.22 + Math.random() * 0.3;
        cx.beginPath(); cx.arc(Math.random()*S, Math.random()*S, 0.5+Math.random()*1.2, 0, Math.PI*2); cx.fill();
      }
    } else if (finish === 'metallic' || finish === 'brushed_metal') {
      // Brushed horizontal lines
      for (let y = 0; y < S; y++) {
        const v = (Math.random() - 0.5) * (finish === 'brushed_metal' ? 28 : 20);
        cx.strokeStyle = rgbStr(br + v, bg + v, bb + v);
        cx.globalAlpha = 0.18 + Math.random() * 0.22;
        cx.lineWidth = 0.5 + Math.random() * 0.5;
        cx.beginPath(); cx.moveTo(0, y); cx.lineTo(S, y); cx.stroke();
      }
    } else if (finish === 'concrete') {
      // Concrete — large blotches + fine grain
      for (let i = 0; i < 600; i++) {
        cx.fillStyle = `rgba(0,0,0,${Math.random()*0.028})`; cx.globalAlpha = 1;
        cx.fillRect(Math.random()*S, Math.random()*S, 18+Math.random()*40, 18+Math.random()*40);
      }
      for (let i = 0; i < 400; i++) {
        cx.fillStyle = `rgba(255,255,255,${Math.random()*0.020})`; cx.globalAlpha = 1;
        cx.fillRect(Math.random()*S, Math.random()*S, 8+Math.random()*22, 8+Math.random()*22);
      }
      cx.strokeStyle = 'rgba(0,0,0,0.045)'; cx.lineWidth = 1;
      for (let y2 = 68; y2 < S; y2 += 68 + Math.random()*28) {
        cx.beginPath(); cx.moveTo(0, y2); cx.lineTo(S, y2); cx.stroke();
      }
    } else if (finish === 'gloss') {
      // GLOSS — ultra-smooth, bright scattered light reflections
      for (let i = 0; i < 300; i++) {
        const v = (Math.random() - 0.5) * 4;
        cx.fillStyle = rgbStr(br + v, bg + v, bb + v);
        cx.globalAlpha = 0.025; cx.fillRect(Math.random()*S, Math.random()*S, 1, 1);
      }
      // Bright scattered specular blobs simulate mirror-like shine
      for (let i = 0; i < 18; i++) {
        const rx = Math.random() * S, ry = Math.random() * S, rr = 30 + Math.random() * 70;
        const g = cx.createRadialGradient(rx, ry, 0, rx, ry, rr);
        g.addColorStop(0,   'rgba(255,255,255,0.55)');
        g.addColorStop(0.25,'rgba(255,255,255,0.28)');
        g.addColorStop(0.6, 'rgba(255,255,255,0.08)');
        g.addColorStop(1,   'rgba(255,255,255,0)');
        cx.fillStyle = g; cx.globalAlpha = 1; cx.fillRect(rx-rr, ry-rr, rr*2, rr*2);
      }
      // Overall brightness lift
      cx.fillStyle = 'rgba(255,255,255,0.10)'; cx.globalAlpha = 1; cx.fillRect(0,0,S,S);
      cx.globalAlpha = 1;
      const t = new THREE.CanvasTexture(cv);
      t.wrapS = t.wrapT = THREE.RepeatWrapping; t.repeat.set(1.0, 1.0);
      return { texture: t, ...finishToPBR(finish) };

    } else if (finish === 'semi_gloss') {
      // SEMI-GLOSS — fine surface with moderate sheen spots
      for (let i = 0; i < 2500; i++) {
        const v = (Math.random() - 0.5) * 9;
        cx.fillStyle = rgbStr(br + v, bg + v, bb + v);
        cx.globalAlpha = 0.045; cx.fillRect(Math.random()*S, Math.random()*S, 1, 1);
      }
      for (let i = 0; i < 9; i++) {
        const rx = Math.random() * S, ry = Math.random() * S, rr = 50 + Math.random() * 90;
        const g = cx.createRadialGradient(rx, ry, 0, rx, ry, rr);
        g.addColorStop(0,   'rgba(255,255,255,0.28)');
        g.addColorStop(0.4, 'rgba(255,255,255,0.10)');
        g.addColorStop(1,   'rgba(255,255,255,0)');
        cx.fillStyle = g; cx.globalAlpha = 1; cx.fillRect(rx-rr, ry-rr, rr*2, rr*2);
      }
      cx.fillStyle = 'rgba(255,255,255,0.045)'; cx.globalAlpha = 1; cx.fillRect(0,0,S,S);
      cx.globalAlpha = 1;
      const t = new THREE.CanvasTexture(cv);
      t.wrapS = t.wrapT = THREE.RepeatWrapping; t.repeat.set(1.5, 1.5);
      return { texture: t, ...finishToPBR(finish) };

    } else if (finish === 'satin') {
      // SATIN — medium grain with very subtle soft sheen
      for (let i = 0; i < 6000; i++) {
        const v = (Math.random() - 0.5) * 12;
        cx.fillStyle = rgbStr(br + v, bg + v, bb + v);
        cx.globalAlpha = 0.055; cx.fillRect(Math.random()*S, Math.random()*S, 1, 1);
      }
      for (let i = 0; i < 4; i++) {
        const rx = Math.random() * S, ry = Math.random() * S, rr = 90 + Math.random() * 120;
        const g = cx.createRadialGradient(rx, ry, 0, rx, ry, rr);
        g.addColorStop(0,   'rgba(255,255,255,0.13)');
        g.addColorStop(0.5, 'rgba(255,255,255,0.04)');
        g.addColorStop(1,   'rgba(255,255,255,0)');
        cx.fillStyle = g; cx.globalAlpha = 1; cx.fillRect(rx-rr, ry-rr, rr*2, rr*2);
      }
      // Subtle roller laps
      for (let x = 0; x < S; x += 68 + Math.random()*32) {
        const gm = cx.createLinearGradient(x, 0, x+34, 0);
        gm.addColorStop(0,'rgba(255,255,255,0)'); gm.addColorStop(0.5,'rgba(255,255,255,0.025)'); gm.addColorStop(1,'rgba(255,255,255,0)');
        cx.fillStyle = gm; cx.globalAlpha = 1; cx.fillRect(x, 0, 34, S);
      }
      cx.globalAlpha = 1;
      const t = new THREE.CanvasTexture(cv);
      t.wrapS = t.wrapT = THREE.RepeatWrapping; t.repeat.set(2.0, 2.0);
      return { texture: t, ...finishToPBR(finish) };

    } else if (finish === 'pearl') {
      // PEARL — smooth with iridescent micro-shimmer (soft pink/blue sparkle)
      for (let i = 0; i < 2000; i++) {
        const v = (Math.random() - 0.5) * 7;
        cx.fillStyle = rgbStr(br + v, bg + v, bb + v);
        cx.globalAlpha = 0.04; cx.fillRect(Math.random()*S, Math.random()*S, 1, 1);
      }
      // Soft overall luminance glow
      for (let i = 0; i < 6; i++) {
        const rx = Math.random()*S, ry = Math.random()*S, rr = 70+Math.random()*100;
        const g = cx.createRadialGradient(rx, ry, 0, rx, ry, rr);
        g.addColorStop(0, 'rgba(255,255,255,0.18)'); g.addColorStop(1, 'rgba(255,255,255,0)');
        cx.fillStyle = g; cx.globalAlpha = 1; cx.fillRect(rx-rr, ry-rr, rr*2, rr*2);
      }
      // Iridescent sparkle — tiny pink and blue-white dots
      for (let i = 0; i < 700; i++) {
        const hue = Math.random() > 0.5 ? `rgba(220,200,255,` : `rgba(255,210,230,`;
        cx.fillStyle = hue + (0.12 + Math.random()*0.22) + ')';
        cx.globalAlpha = 1;
        cx.beginPath(); cx.arc(Math.random()*S, Math.random()*S, 0.5+Math.random()*1.8, 0, Math.PI*2); cx.fill();
      }
      cx.fillStyle = 'rgba(255,255,255,0.06)'; cx.globalAlpha = 1; cx.fillRect(0,0,S,S);
      cx.globalAlpha = 1;
      const t = new THREE.CanvasTexture(cv);
      t.wrapS = t.wrapT = THREE.RepeatWrapping; t.repeat.set(1.5, 1.5);
      return { texture: t, ...finishToPBR(finish) };

    } else {
      // MATTE — pure solid color, zero texture or visual effects
      return { texture: null, ...finishToPBR('matte') };
    }
    cx.globalAlpha = 1;
    const t = new THREE.CanvasTexture(cv);
    t.wrapS = t.wrapT = THREE.RepeatWrapping;
    t.repeat.set(2.5, 2.5);
    return { texture: t, ...finishToPBR(mat.finish) };
  }

  const S = 512;
  const canvas = document.createElement('canvas');
  canvas.width = canvas.height = S;
  const ctx = canvas.getContext('2d');
  const base = mat.color;
  const { r: br, g: bg, b: bb } = hexRgb(base);

  // ── PLAIN PAINTED WALLS (neutral / colour) ───────────────────────────────
  // Subtle paint micro-texture so flat CGI walls become realistic painted surfaces
  if (['neutral', 'colour'].includes(mat.category)) {
    ctx.fillStyle = base; ctx.fillRect(0, 0, S, S);
    // Fine paint stipple / orange-peel only — NO directional roller-lap marks
    // (those tiled into visible vertical stripes on saturated colours).
    for (let i = 0; i < 6000; i++) {
      const v = (Math.random() - 0.5) * 9;
      ctx.fillStyle = rgbStr(br + v, bg + v, bb + v);
      ctx.globalAlpha = 0.07;
      ctx.fillRect(Math.random() * S, Math.random() * S, 1, 1);
    }
    ctx.globalAlpha = 1;
    const t = new THREE.CanvasTexture(canvas);
    t.wrapS = t.wrapT = THREE.RepeatWrapping;
    return { texture: t, roughness: 0.90, metalness: 0, repeat: [1.5, 1.5] };
  }

  // ── TEXTURED / PATTERN walls need canvas ─────────────────────────────────
  const ALL_TEXTURED = ['brick','brick_white','concrete_w','plaster','wood_panel','limewash',
    'stripe_bw','stripe_blue','stripe_sage','wainscot',
    'grasscloth','shiplap','stone_clad','cork_wall','raked_plast',
    'geometric','damask','stripe_gold','herring_wp',
    'marble_w','marble_blk','travertine','stone_wall2',
    'micro_cem','subway_tile','hex_tile','terracotta_t',
    'fluted','slatted','fabric_w','jute_wall',
    'venetian_stucco','board_batten','cinder_block','reclaimed_brick',
    'bookmatched','chevron_panel','pegboard','houndstooth',
    'tadelakt','roman_clay','clay_plaster','board_concrete','panel_3d','brushed_brass',
    'wood_slat','tambour','terrazzo_w','shou_sugi','corten','cane_web','brushed_steel'];
  if (!ALL_TEXTURED.includes(mat.id)) return null;

  // ── BRICK ─────────────────────────────────────────────────────────────────
  if (mat.id === 'brick' || mat.id === 'brick_white') {
    const bw=80, bh=36, g=5;
    ctx.fillStyle = mat.groutColor; ctx.fillRect(0, 0, S, S);
    for (let r=0; r<S/bh+1; r++) {
      const off=(r%2)*(bw/2);
      for (let c=-1; c<S/bw+1; c++) {
        ctx.fillStyle=jitter(base, mat.id==='brick'?20:10); ctx.globalAlpha=1;
        ctx.fillRect(c*bw+off+g/2, r*bh+g/2, bw-g, bh-g);
        ctx.fillStyle='rgba(255,255,255,0.08)'; ctx.fillRect(c*bw+off+g/2, r*bh+g/2, bw-g, 3);
        ctx.fillStyle='rgba(0,0,0,0.10)'; ctx.fillRect(c*bw+off+g/2, r*bh+bh-g/2-3, bw-g, 3);
      }
    }
    const t=new THREE.CanvasTexture(canvas); t.wrapS=t.wrapT=THREE.RepeatWrapping;
    return { texture:t, roughness:mat.id==='brick'?0.88:0.82, metalness:0, repeat:[0.4,0.6] };
  }

  // ── RAW CONCRETE ──────────────────────────────────────────────────────────
  if (mat.id === 'concrete_w') {
    ctx.fillStyle = base; ctx.fillRect(0, 0, S, S);
    for (let i=0; i<650; i++) {
      ctx.fillStyle=`rgba(0,0,0,${Math.random()*0.032})`; ctx.fillRect(Math.random()*S,Math.random()*S,15+Math.random()*42,15+Math.random()*42);
    }
    ctx.strokeStyle='rgba(0,0,0,0.07)';
    for (let y=60; y<S; y+=60+Math.random()*20) { ctx.lineWidth=1+Math.random(); ctx.beginPath(); ctx.moveTo(0,y); ctx.lineTo(S,y); ctx.stroke(); }
    const t=new THREE.CanvasTexture(canvas); t.wrapS=t.wrapT=THREE.RepeatWrapping;
    return { texture:t, roughness:0.94, metalness:0, repeat:[1,1] };
  }

  // ── VENETIAN PLASTER ──────────────────────────────────────────────────────
  if (mat.id === 'plaster') {
    ctx.fillStyle = base; ctx.fillRect(0, 0, S, S);
    for (let i=0; i<220; i++) {
      ctx.strokeStyle=jitter(base,14); ctx.globalAlpha=0.08+Math.random()*0.12; ctx.lineWidth=1+Math.random()*4;
      const x0=Math.random()*S, y0=Math.random()*S, len=30+Math.random()*110, ang=Math.random()*Math.PI*2;
      ctx.beginPath(); ctx.moveTo(x0,y0); ctx.lineTo(x0+Math.cos(ang)*len, y0+Math.sin(ang)*len); ctx.stroke();
    }
    ctx.globalAlpha=1;
    const t=new THREE.CanvasTexture(canvas); t.wrapS=t.wrapT=THREE.RepeatWrapping;
    return { texture:t, roughness:0.72, metalness:0.02, repeat:[1,1] };
  }

  // ── LIMEWASH ──────────────────────────────────────────────────────────────
  if (mat.id === 'limewash') {
    ctx.fillStyle = base; ctx.fillRect(0, 0, S, S);
    for (let i=0; i<180; i++) {
      ctx.strokeStyle=jitter(base,22); ctx.globalAlpha=0.06+Math.random()*0.10; ctx.lineWidth=2+Math.random()*9;
      const x0=Math.random()*S, y0=Math.random()*S, ang=(Math.random()-0.5)*0.4, len=60+Math.random()*170;
      ctx.beginPath(); ctx.moveTo(x0,y0);
      ctx.quadraticCurveTo(x0+Math.cos(ang+0.3)*len*0.5, y0+Math.sin(ang+0.3)*len*0.5, x0+Math.cos(ang)*len, y0+Math.sin(ang)*len);
      ctx.stroke();
    }
    ctx.globalAlpha=1;
    const t=new THREE.CanvasTexture(canvas); t.wrapS=t.wrapT=THREE.RepeatWrapping;
    return { texture:t, roughness:0.88, metalness:0, repeat:[1,1] };
  }

  // ── WOOD PANELLING ────────────────────────────────────────────────────────
  if (mat.id === 'wood_panel') {
    const gc3=mat.grainColor, pw=48, nc=Math.ceil(S/pw)+2;
    for (let i=0; i<nc; i++) { ctx.fillStyle=jitter(base,16); ctx.globalAlpha=1; ctx.fillRect(i*pw,0,pw-2,S); }
    ctx.strokeStyle=gc3;
    for (let j=0; j<40; j++) { const y=(j/40)*S; ctx.globalAlpha=0.04+Math.random()*0.08; ctx.lineWidth=0.4+Math.random()*0.8; ctx.beginPath(); ctx.moveTo(0,y); ctx.lineTo(S,y+(Math.random()-0.5)*8); ctx.stroke(); }
    ctx.globalAlpha=0.35; ctx.fillStyle=gc3;
    for (let i=0; i<nc; i++) ctx.fillRect(i*pw-1,0,2,S);
    ctx.globalAlpha=0.5; ctx.fillRect(0,S*0.42,S,6); ctx.globalAlpha=1;
    const t=new THREE.CanvasTexture(canvas); t.wrapS=t.wrapT=THREE.RepeatWrapping;
    return { texture:t, roughness:0.65, metalness:0, repeat:[0.5,0.5] };
  }

  // ── STRIPES ───────────────────────────────────────────────────────────────
  if (['stripe_bw','stripe_blue','stripe_sage'].includes(mat.id)) {
    ctx.fillStyle=base; ctx.fillRect(0,0,S,S);
    ctx.fillStyle=mat.stripeColor; ctx.globalAlpha=0.7;
    for (let x=0; x<S; x+=32) ctx.fillRect(x,0,12,S);
    ctx.globalAlpha=1;
    const t=new THREE.CanvasTexture(canvas); t.wrapS=t.wrapT=THREE.RepeatWrapping;
    return { texture:t, roughness:0.82, metalness:0, repeat:[0.5,0.5] };
  }

  // ── WAINSCOTING ───────────────────────────────────────────────────────────
  if (mat.id === 'wainscot') {
    const pc=mat.panelColor;
    ctx.fillStyle=base; ctx.fillRect(0,0,S,S);
    ctx.fillStyle=pc; ctx.fillRect(0,S*0.55,S,S*0.45);
    const pad=18;
    for (let col=0; col<3; col++) {
      const bx=col*(S/3)+pad, bw2=S/3-pad*2;
      ctx.strokeStyle='rgba(0,0,0,0.12)'; ctx.lineWidth=3; ctx.strokeRect(bx,S*0.58,bw2,S*0.36);
      ctx.strokeStyle='rgba(255,255,255,0.28)'; ctx.lineWidth=1.5; ctx.strokeRect(bx+3,S*0.58+3,bw2-6,S*0.36-6);
    }
    ctx.fillStyle='rgba(255,255,255,0.14)'; ctx.fillRect(0,S*0.53,S,10);
    ctx.fillStyle='rgba(0,0,0,0.10)'; ctx.fillRect(0,S*0.54,S,4);
    const t=new THREE.CanvasTexture(canvas); t.wrapS=t.wrapT=THREE.RepeatWrapping;
    return { texture:t, roughness:0.78, metalness:0, repeat:[0.35,0.5] };
  }

  // ── GRASSCLOTH ─────────────────────────────────────────────────────────────
  if (mat.id === 'grasscloth') {
    ctx.fillStyle = base; ctx.fillRect(0, 0, S, S);
    for (let x = 0; x < S; x += 3) {
      ctx.strokeStyle = jitter(base, 16); ctx.globalAlpha = 0.5 + Math.random() * 0.4;
      ctx.lineWidth = 1.5 + Math.random();
      ctx.beginPath(); ctx.moveTo(x + Math.random() * 2, 0); ctx.lineTo(x + Math.random() * 2, S); ctx.stroke();
    }
    for (let y = 0; y < S; y += 4) {
      ctx.strokeStyle = jitter(base, 8); ctx.globalAlpha = 0.12 + Math.random() * 0.10;
      ctx.lineWidth = 0.5; ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(S, y); ctx.stroke();
    }
    ctx.globalAlpha = 1;
    const t = new THREE.CanvasTexture(canvas); t.wrapS = t.wrapT = THREE.RepeatWrapping;
    return { texture: t, roughness: 0.90, metalness: 0, repeat: [1, 1] };
  }

  // ── SHIPLAP ────────────────────────────────────────────────────────────────
  if (mat.id === 'shiplap') {
    const ph = 44;
    ctx.fillStyle = '#222'; ctx.fillRect(0, 0, S, S);
    for (let row = 0; row < S / ph + 1; row++) {
      const y = row * ph;
      ctx.fillStyle = jitter(base, 12); ctx.globalAlpha = 1;
      ctx.fillRect(0, y + 3, S, ph - 4);
      ctx.fillStyle = 'rgba(255,255,255,0.12)'; ctx.fillRect(0, y + 3, S, 4);
      ctx.fillStyle = 'rgba(0,0,0,0.18)'; ctx.fillRect(0, y + ph - 6, S, 4);
      for (let i = 0; i < 14; i++) {
        ctx.strokeStyle = jitter(base, 20); ctx.globalAlpha = 0.05 + Math.random() * 0.07;
        ctx.lineWidth = 0.4 + Math.random() * 0.6;
        ctx.beginPath(); ctx.moveTo(0, y + 4 + (i / 14) * (ph - 8)); ctx.lineTo(S, y + 4 + (i / 14) * (ph - 8) + (Math.random() - 0.5) * 4); ctx.stroke();
      }
    }
    ctx.globalAlpha = 1;
    const t = new THREE.CanvasTexture(canvas); t.wrapS = t.wrapT = THREE.RepeatWrapping;
    return { texture: t, roughness: 0.72, metalness: 0, repeat: [0.5, 0.5] };
  }

  // ── STONE CLADDING ─────────────────────────────────────────────────────────
  if (mat.id === 'stone_clad') {
    ctx.fillStyle = '#706a62'; ctx.fillRect(0, 0, S, S);
    const sh = 52, sw = 80;
    for (let row = 0; row < S / sh + 1; row++) {
      const off = (row % 2) * (sw * 0.5);
      for (let col = -1; col < S / sw + 2; col++) {
        const x = col * sw + off, y = row * sh;
        const cw = sw - 5 - Math.random() * 8, ch = sh - 5 - Math.random() * 6;
        ctx.fillStyle = jitter(base, 18); ctx.globalAlpha = 1;
        ctx.fillRect(x + 3, y + 3, cw, ch);
        ctx.fillStyle = 'rgba(255,255,255,0.07)'; ctx.fillRect(x + 3, y + 3, cw, 5);
        ctx.fillStyle = 'rgba(0,0,0,0.12)'; ctx.fillRect(x + 3, y + ch - 4, cw, 5);
      }
    }
    ctx.globalAlpha = 1;
    const t = new THREE.CanvasTexture(canvas); t.wrapS = t.wrapT = THREE.RepeatWrapping;
    return { texture: t, roughness: 0.92, metalness: 0, repeat: [0.4, 0.5] };
  }

  // ── CORK WALL ──────────────────────────────────────────────────────────────
  if (mat.id === 'cork_wall') {
    ctx.fillStyle = base; ctx.fillRect(0, 0, S, S);
    for (let i = 0; i < 1800; i++) {
      const x = Math.random() * S, y = Math.random() * S;
      const rx = 4 + Math.random() * 10, ry = 3 + Math.random() * 8;
      ctx.strokeStyle = jitter(base, 22); ctx.globalAlpha = 0.28 + Math.random() * 0.30;
      ctx.lineWidth = 0.8 + Math.random(); ctx.beginPath();
      ctx.ellipse(x, y, rx, ry, Math.random() * Math.PI, 0, Math.PI * 2); ctx.stroke();
      ctx.fillStyle = jitter(base, 14); ctx.globalAlpha = 0.08;
      ctx.beginPath(); ctx.ellipse(x, y, rx * 0.5, ry * 0.5, 0, 0, Math.PI * 2); ctx.fill();
    }
    ctx.globalAlpha = 1;
    const t = new THREE.CanvasTexture(canvas); t.wrapS = t.wrapT = THREE.RepeatWrapping;
    return { texture: t, roughness: 0.96, metalness: 0, repeat: [1, 1] };
  }

  // ── RAKED PLASTER ──────────────────────────────────────────────────────────
  if (mat.id === 'raked_plast') {
    ctx.fillStyle = base; ctx.fillRect(0, 0, S, S);
    for (let y = 0; y < S; y += 2) {
      ctx.strokeStyle = jitter(base, 20); ctx.globalAlpha = 0.18 + Math.random() * 0.22;
      ctx.lineWidth = 0.8 + Math.random() * 0.8;
      const wavY = y + Math.sin(y * 0.1) * 2;
      ctx.beginPath(); ctx.moveTo(0, wavY); ctx.lineTo(S, wavY + (Math.random() - 0.5) * 3); ctx.stroke();
    }
    for (let i = 0; i < 60; i++) {
      ctx.strokeStyle = 'rgba(0,0,0,0.06)'; ctx.globalAlpha = 1; ctx.lineWidth = 1.5;
      const y = Math.random() * S;
      ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(S, y + (Math.random() - 0.5) * 6); ctx.stroke();
    }
    ctx.globalAlpha = 1;
    const t = new THREE.CanvasTexture(canvas); t.wrapS = t.wrapT = THREE.RepeatWrapping;
    return { texture: t, roughness: 0.86, metalness: 0, repeat: [1, 1] };
  }

  // ── GEOMETRIC PATTERN ──────────────────────────────────────────────────────
  if (mat.id === 'geometric') {
    const sc = mat.stripeColor || '#888';
    ctx.fillStyle = base; ctx.fillRect(0, 0, S, S);
    const gs = 48;
    ctx.strokeStyle = sc; ctx.globalAlpha = 0.55; ctx.lineWidth = 2;
    for (let row = 0; row < S / gs + 1; row++) {
      for (let col = 0; col < S / gs + 1; col++) {
        const cx2 = col * gs, cy2 = row * gs;
        if ((row + col) % 2 === 0) {
          ctx.beginPath(); ctx.moveTo(cx2, cy2); ctx.lineTo(cx2 + gs, cy2 + gs); ctx.stroke();
          ctx.beginPath(); ctx.moveTo(cx2 + gs, cy2); ctx.lineTo(cx2, cy2 + gs); ctx.stroke();
        }
        ctx.strokeRect(cx2 + 4, cy2 + 4, gs - 8, gs - 8);
      }
    }
    ctx.globalAlpha = 1;
    const t = new THREE.CanvasTexture(canvas); t.wrapS = t.wrapT = THREE.RepeatWrapping;
    return { texture: t, roughness: 0.80, metalness: 0, repeat: [0.4, 0.4] };
  }

  // ── DAMASK ─────────────────────────────────────────────────────────────────
  if (mat.id === 'damask') {
    const sc2 = mat.stripeColor || '#9a8868';
    ctx.fillStyle = base; ctx.fillRect(0, 0, S, S);
    const ds = 96;
    for (let row = 0; row < S / ds + 1; row++) {
      for (let col = 0; col < S / ds + 1; col++) {
        const cx3 = col * ds + ds / 2, cy3 = row * ds + ds / 2;
        ctx.strokeStyle = sc2; ctx.globalAlpha = 0.35; ctx.lineWidth = 1.5;
        ctx.beginPath(); ctx.arc(cx3, cy3, ds * 0.3, 0, Math.PI * 2); ctx.stroke();
        ctx.beginPath(); ctx.arc(cx3, cy3, ds * 0.18, 0, Math.PI * 2); ctx.stroke();
        for (let a = 0; a < 4; a++) {
          const ang = (a / 4) * Math.PI * 2;
          const px = cx3 + Math.cos(ang) * ds * 0.38, py = cy3 + Math.sin(ang) * ds * 0.38;
          ctx.beginPath(); ctx.arc(px, py, ds * 0.10, 0, Math.PI * 2); ctx.stroke();
        }
      }
    }
    ctx.globalAlpha = 1;
    const t = new THREE.CanvasTexture(canvas); t.wrapS = t.wrapT = THREE.RepeatWrapping;
    return { texture: t, roughness: 0.78, metalness: 0, repeat: [0.3, 0.3] };
  }

  // ── GOLD STRIPES ───────────────────────────────────────────────────────────
  if (mat.id === 'stripe_gold') {
    const sc3 = mat.stripeColor || '#c89820';
    ctx.fillStyle = base; ctx.fillRect(0, 0, S, S);
    for (let x = 0; x < S; x += 32) {
      ctx.fillStyle = sc3; ctx.globalAlpha = 0.6; ctx.fillRect(x, 0, 14, S);
      const g2 = ctx.createLinearGradient(x, 0, x + 14, 0);
      g2.addColorStop(0, 'rgba(255,255,255,0)');
      g2.addColorStop(0.3, 'rgba(255,220,80,0.25)');
      g2.addColorStop(1, 'rgba(255,255,255,0)');
      ctx.fillStyle = g2; ctx.fillRect(x, 0, 14, S);
    }
    ctx.globalAlpha = 1;
    const t = new THREE.CanvasTexture(canvas); t.wrapS = t.wrapT = THREE.RepeatWrapping;
    return { texture: t, roughness: 0.55, metalness: 0.12, repeat: [0.5, 0.5] };
  }

  // ── HERRINGBONE WALLPAPER ──────────────────────────────────────────────────
  if (mat.id === 'herring_wp') {
    const sc4 = mat.stripeColor || '#b8a888';
    ctx.fillStyle = base; ctx.fillRect(0, 0, S, S);
    const bw2 = 20, bh2 = 10;
    for (let row2 = -2; row2 < S / bh2 + 2; row2++) {
      for (let col2 = -2; col2 < S / bw2 + 2; col2++) {
        const even = (Math.floor(row2 / 2) + col2) % 2 === 0;
        ctx.save();
        ctx.translate(col2 * bw2, row2 * bh2);
        ctx.rotate(even ? Math.PI / 4 : -Math.PI / 4);
        ctx.strokeStyle = sc4; ctx.globalAlpha = 0.42; ctx.lineWidth = 1.2;
        ctx.strokeRect(-bw2 / 2, -bh2 / 2, bw2, bh2);
        ctx.restore();
      }
    }
    ctx.globalAlpha = 1;
    const t = new THREE.CanvasTexture(canvas); t.wrapS = t.wrapT = THREE.RepeatWrapping;
    return { texture: t, roughness: 0.82, metalness: 0, repeat: [0.35, 0.35] };
  }

  // ── WHITE MARBLE ───────────────────────────────────────────────────────────
  if (mat.id === 'marble_w' || mat.id === 'marble_blk') {
    const isBlk = mat.id === 'marble_blk';
    ctx.fillStyle = base; ctx.fillRect(0, 0, S, S);
    // Cloudy base variation
    for (let i = 0; i < 120; i++) {
      const x = Math.random() * S, y = Math.random() * S, r = 20 + Math.random() * 60;
      const v = (Math.random() - 0.5) * (isBlk ? 12 : 18);
      const grd = ctx.createRadialGradient(x, y, 0, x, y, r);
      grd.addColorStop(0, `rgba(${Math.max(0,br+v)},${Math.max(0,bg+v)},${Math.max(0,bb+v)},0.22)`);
      grd.addColorStop(1, 'rgba(0,0,0,0)');
      ctx.fillStyle = grd; ctx.globalAlpha = 1; ctx.fillRect(x - r, y - r, r * 2, r * 2);
    }
    // Veins
    const veinCol = isBlk ? `rgba(180,155,80,` : `rgba(${br-55},${bg-55},${bb-52},`;
    const veinColLight = isBlk ? `rgba(210,185,110,` : `rgba(${br-30},${bg-30},${bb-28},`;
    for (let v = 0; v < (isBlk ? 7 : 9); v++) {
      const x0 = Math.random() * S, y0 = -20;
      const cpx1 = x0 + (Math.random() - 0.5) * 180, cpy1 = S * 0.35;
      const cpx2 = cpx1 + (Math.random() - 0.5) * 140, cpy2 = S * 0.7;
      const x3 = cpx2 + (Math.random() - 0.5) * 140, y3 = S + 20;
      ctx.strokeStyle = veinCol + '0.75)';
      ctx.lineWidth = 0.5 + Math.random() * 1.8; ctx.globalAlpha = 0.6 + Math.random() * 0.35;
      ctx.beginPath(); ctx.moveTo(x0, y0); ctx.bezierCurveTo(cpx1, cpy1, cpx2, cpy2, x3, y3); ctx.stroke();
      ctx.strokeStyle = veinColLight + '0.4)'; ctx.lineWidth = ctx.lineWidth * 0.4; ctx.globalAlpha = 0.3;
      ctx.stroke();
      // fine branch veins
      for (let b = 0; b < 3; b++) {
        const t2 = 0.2 + Math.random() * 0.6;
        const bx = x0*(1-t2)*2 + cpx1*2*t2*(1-t2) + x3*t2*t2;
        const by = y0*(1-t2)*2 + cpy1*2*t2*(1-t2) + y3*t2*t2;
        ctx.strokeStyle = veinCol + '0.35)';
        ctx.lineWidth = 0.3 + Math.random() * 0.6; ctx.globalAlpha = 0.25;
        ctx.beginPath(); ctx.moveTo(bx, by); ctx.lineTo(bx + (Math.random()-0.5)*90, by + (Math.random()-0.5)*60); ctx.stroke();
      }
    }
    ctx.globalAlpha = 1;
    const t = new THREE.CanvasTexture(canvas); t.wrapS = t.wrapT = THREE.RepeatWrapping;
    return { texture: t, roughness: isBlk ? 0.08 : 0.10, metalness: 0.02, repeat: [0.55, 0.55] };
  }

  // ── TRAVERTINE ─────────────────────────────────────────────────────────────
  if (mat.id === 'travertine') {
    ctx.fillStyle = base; ctx.fillRect(0, 0, S, S);
    // Horizontal banding / layers
    for (let y = 0; y < S; y += 2) {
      const v = Math.sin(y * 0.035) * 12 + (Math.random() - 0.5) * 8;
      ctx.fillStyle = rgbStr(br + v, bg + v * 0.9, bb + v * 0.7);
      ctx.globalAlpha = 0.12; ctx.fillRect(0, y, S, 2);
    }
    // Travertine pores
    for (let i = 0; i < 320; i++) {
      const px = Math.random() * S, py = Math.random() * S;
      const pw2 = 2 + Math.random() * 9, ph2 = 1 + Math.random() * 4;
      ctx.fillStyle = `rgba(${Math.max(0,br-35)},${Math.max(0,bg-28)},${Math.max(0,bb-22)},1)`;
      ctx.globalAlpha = 0.25 + Math.random() * 0.35;
      ctx.beginPath(); ctx.ellipse(px, py, pw2, ph2, Math.random() * 0.4, 0, Math.PI * 2); ctx.fill();
    }
    // Calcite grain
    for (let i = 0; i < 4000; i++) {
      const v = (Math.random() - 0.5) * 20;
      ctx.fillStyle = rgbStr(br + v, bg + v * 0.85, bb + v * 0.6);
      ctx.globalAlpha = 0.055; ctx.fillRect(Math.random() * S, Math.random() * S, 1, 1);
    }
    ctx.globalAlpha = 1;
    const t = new THREE.CanvasTexture(canvas); t.wrapS = t.wrapT = THREE.RepeatWrapping;
    return { texture: t, roughness: 0.65, metalness: 0.01, repeat: [0.55, 0.55] };
  }

  // ── STACKED STONE ──────────────────────────────────────────────────────────
  if (mat.id === 'stone_wall2') {
    ctx.fillStyle = '#5a5550'; ctx.fillRect(0, 0, S, S);
    let curY = 0;
    while (curY < S) {
      const rowH = 28 + Math.random() * 22;
      let curX = (Math.random() - 0.5) * 40;
      while (curX < S) {
        const stW = 55 + Math.random() * 65;
        ctx.fillStyle = jitter(base, 24); ctx.globalAlpha = 1;
        const margin = 3 + Math.random() * 2;
        ctx.fillRect(curX + margin, curY + margin, stW - margin * 2, rowH - margin * 2);
        ctx.fillStyle = 'rgba(255,255,255,0.07)';
        ctx.fillRect(curX + margin, curY + margin, stW - margin * 2, 4);
        ctx.fillStyle = 'rgba(0,0,0,0.14)';
        ctx.fillRect(curX + margin, curY + rowH - margin - 5, stW - margin * 2, 5);
        // Stone surface detail
        for (let d = 0; d < 5; d++) {
          ctx.fillStyle = jitter(base, 16); ctx.globalAlpha = 0.08;
          ctx.fillRect(curX + margin + Math.random() * stW * 0.7, curY + margin + Math.random() * rowH * 0.6, Math.random() * 18, Math.random() * 8);
        }
        curX += stW;
      }
      curY += rowH;
    }
    ctx.globalAlpha = 1;
    const t = new THREE.CanvasTexture(canvas); t.wrapS = t.wrapT = THREE.RepeatWrapping;
    return { texture: t, roughness: 0.94, metalness: 0, repeat: [0.35, 0.45] };
  }

  // ── MICRO CEMENT ───────────────────────────────────────────────────────────
  if (mat.id === 'micro_cem') {
    ctx.fillStyle = base; ctx.fillRect(0, 0, S, S);
    // Large smooth trowel patches
    for (let i = 0; i < 60; i++) {
      const x = Math.random() * S, y = Math.random() * S;
      const w = 60 + Math.random() * 120, h = 40 + Math.random() * 80;
      const v = (Math.random() - 0.5) * 14;
      const grd = ctx.createLinearGradient(x, y, x + w, y + h);
      grd.addColorStop(0, `rgba(${br+v},${bg+v},${bb+v},0.14)`);
      grd.addColorStop(1, `rgba(${br-v},${bg-v},${bb-v},0.10)`);
      ctx.fillStyle = grd; ctx.globalAlpha = 1;
      ctx.beginPath(); ctx.ellipse(x + w/2, y + h/2, w/2, h/2, Math.random() * Math.PI, 0, Math.PI * 2); ctx.fill();
    }
    // Ultra-fine micro grain
    for (let i = 0; i < 6000; i++) {
      const v = (Math.random() - 0.5) * 8;
      ctx.fillStyle = rgbStr(br + v, bg + v, bb + v);
      ctx.globalAlpha = 0.04; ctx.fillRect(Math.random() * S, Math.random() * S, 1, 1);
    }
    // Hairline trowel edges
    ctx.strokeStyle = `rgba(${br-18},${bg-18},${bb-18},1)`; ctx.lineWidth = 0.5;
    for (let i = 0; i < 25; i++) {
      ctx.globalAlpha = 0.06 + Math.random() * 0.06;
      const x = Math.random() * S, y = Math.random() * S;
      ctx.beginPath(); ctx.moveTo(x, y); ctx.lineTo(x + (Math.random()-0.5)*200, y + (Math.random()-0.5)*80); ctx.stroke();
    }
    ctx.globalAlpha = 1;
    const t = new THREE.CanvasTexture(canvas); t.wrapS = t.wrapT = THREE.RepeatWrapping;
    return { texture: t, roughness: 0.30, metalness: 0.02, repeat: [0.8, 0.8] };
  }

  // ── SUBWAY TILES ───────────────────────────────────────────────────────────
  if (mat.id === 'subway_tile') {
    const grout = mat.groutColor || '#bfbdba';
    const tw = 76, th = 38, g3 = 4;
    ctx.fillStyle = grout; ctx.fillRect(0, 0, S, S);
    for (let row3 = 0; row3 < S / th + 1; row3++) {
      const off = (row3 % 2) * (tw / 2);
      for (let col3 = -1; col3 < S / tw + 2; col3++) {
        const tx3 = col3 * tw + off, ty3 = row3 * th;
        ctx.fillStyle = jitter(base, 5); ctx.globalAlpha = 1;
        ctx.fillRect(tx3 + g3, ty3 + g3, tw - g3 * 2, th - g3 * 2);
        // Gloss highlight
        const gl = ctx.createLinearGradient(tx3 + g3, ty3 + g3, tx3 + g3, ty3 + th / 2);
        gl.addColorStop(0, 'rgba(255,255,255,0.28)'); gl.addColorStop(1, 'rgba(255,255,255,0)');
        ctx.fillStyle = gl; ctx.fillRect(tx3 + g3, ty3 + g3, tw - g3 * 2, th / 2 - g3);
        ctx.fillStyle = 'rgba(0,0,0,0.06)'; ctx.fillRect(tx3 + g3, ty3 + th - g3 - 4, tw - g3 * 2, 4);
      }
    }
    ctx.globalAlpha = 1;
    const t = new THREE.CanvasTexture(canvas); t.wrapS = t.wrapT = THREE.RepeatWrapping;
    return { texture: t, roughness: 0.14, metalness: 0.02, repeat: [0.4, 0.55] };
  }

  // ── HEX TILES ──────────────────────────────────────────────────────────────
  if (mat.id === 'hex_tile') {
    const grout2 = mat.groutColor || '#b8b6b2';
    const R2 = 22, g4 = 2;
    const hexH = R2 * Math.sqrt(3), hexW = R2 * 2;
    ctx.fillStyle = grout2; ctx.fillRect(0, 0, S, S);
    const drawHex = (hx, hy, col4) => {
      ctx.beginPath();
      for (let i = 0; i < 6; i++) {
        const ang2 = (Math.PI / 3) * i - Math.PI / 6;
        const px = hx + (R2 - g4) * Math.cos(ang2), py = hy + (R2 - g4) * Math.sin(ang2);
        i === 0 ? ctx.moveTo(px, py) : ctx.lineTo(px, py);
      }
      ctx.closePath(); ctx.fillStyle = col4; ctx.fill();
      // Gloss sheen
      const gs2 = ctx.createRadialGradient(hx - R2*0.2, hy - R2*0.2, 0, hx, hy, R2);
      gs2.addColorStop(0, 'rgba(255,255,255,0.18)'); gs2.addColorStop(1, 'rgba(255,255,255,0)');
      ctx.fillStyle = gs2; ctx.fill();
    };
    for (let row4 = -1; row4 < S / hexH + 2; row4++) {
      for (let col4 = -1; col4 < S / hexW + 2; col4++) {
        const hx = col4 * hexW + (row4 % 2) * R2 + R2;
        const hy = row4 * hexH + R2;
        drawHex(hx, hy, jitter(base, 6));
      }
    }
    ctx.globalAlpha = 1;
    const t = new THREE.CanvasTexture(canvas); t.wrapS = t.wrapT = THREE.RepeatWrapping;
    return { texture: t, roughness: 0.16, metalness: 0.02, repeat: [0.4, 0.4] };
  }

  // ── TERRACOTTA TILE ────────────────────────────────────────────────────────
  if (mat.id === 'terracotta_t') {
    const grout3 = mat.groutColor || '#8a5e40';
    const ts = 64, g5 = 4;
    ctx.fillStyle = grout3; ctx.fillRect(0, 0, S, S);
    for (let row5 = 0; row5 < S / ts + 1; row5++) {
      for (let col5 = 0; col5 < S / ts + 1; col5++) {
        const tx5 = col5 * ts, ty5 = row5 * ts;
        ctx.fillStyle = jitter(base, 22); ctx.globalAlpha = 1;
        ctx.fillRect(tx5 + g5, ty5 + g5, ts - g5 * 2, ts - g5 * 2);
        // Natural clay variation
        for (let d2 = 0; d2 < 8; d2++) {
          ctx.fillStyle = jitter(base, 28); ctx.globalAlpha = 0.10;
          ctx.fillRect(tx5 + g5 + Math.random()*(ts-g5*2-8), ty5 + g5 + Math.random()*(ts-g5*2-5), Math.random()*20, Math.random()*10);
        }
        ctx.fillStyle = 'rgba(255,255,255,0.06)'; ctx.fillRect(tx5 + g5, ty5 + g5, ts - g5 * 2, 5);
        ctx.fillStyle = 'rgba(0,0,0,0.10)'; ctx.fillRect(tx5 + g5, ty5 + ts - g5 - 5, ts - g5 * 2, 5);
      }
    }
    ctx.globalAlpha = 1;
    const t = new THREE.CanvasTexture(canvas); t.wrapS = t.wrapT = THREE.RepeatWrapping;
    return { texture: t, roughness: 0.82, metalness: 0, repeat: [0.4, 0.4] };
  }

  // ── FLUTED PANELS ──────────────────────────────────────────────────────────
  if (mat.id === 'fluted') {
    ctx.fillStyle = base; ctx.fillRect(0, 0, S, S);
    const fw = 18;
    for (let x = 0; x < S; x += fw) {
      // Ridge body
      const rg = ctx.createLinearGradient(x, 0, x + fw, 0);
      rg.addColorStop(0,    `rgba(255,255,255,0.22)`);
      rg.addColorStop(0.35, `rgba(255,255,255,0.04)`);
      rg.addColorStop(0.65, `rgba(0,0,0,0.04)`);
      rg.addColorStop(1,    `rgba(0,0,0,0.20)`);
      ctx.fillStyle = rg; ctx.globalAlpha = 1; ctx.fillRect(x, 0, fw, S);
      // Sharp shadow groove on right edge
      ctx.fillStyle = 'rgba(0,0,0,0.28)'; ctx.fillRect(x + fw - 2, 0, 2, S);
      // Subtle highlight on left edge
      ctx.fillStyle = 'rgba(255,255,255,0.20)'; ctx.fillRect(x, 0, 2, S);
    }
    ctx.globalAlpha = 1;
    const t = new THREE.CanvasTexture(canvas); t.wrapS = t.wrapT = THREE.RepeatWrapping;
    return { texture: t, roughness: 0.55, metalness: 0, repeat: [0.45, 1] };
  }

  // ── SLATTED WOOD ───────────────────────────────────────────────────────────
  if (mat.id === 'slatted') {
    const gc2 = mat.grainColor || '#a07048';
    const slH = 22, gap = 5;
    ctx.fillStyle = '#1a1510'; ctx.fillRect(0, 0, S, S);
    for (let row6 = 0; row6 < S / (slH + gap) + 1; row6++) {
      const y = row6 * (slH + gap);
      ctx.fillStyle = jitter(base, 14); ctx.globalAlpha = 1;
      ctx.fillRect(0, y, S, slH);
      // Wood grain lines
      for (let i = 0; i < 18; i++) {
        const gy = y + (i / 18) * slH;
        ctx.strokeStyle = jitter(gc2, 18); ctx.globalAlpha = 0.06 + Math.random() * 0.09;
        ctx.lineWidth = 0.4 + Math.random() * 0.6;
        ctx.beginPath(); ctx.moveTo(0, gy); ctx.lineTo(S, gy + (Math.random()-0.5)*3); ctx.stroke();
      }
      // Top highlight, bottom shadow
      ctx.fillStyle = 'rgba(255,255,255,0.12)'; ctx.globalAlpha = 1; ctx.fillRect(0, y, S, 3);
      ctx.fillStyle = 'rgba(0,0,0,0.20)'; ctx.fillRect(0, y + slH - 3, S, 3);
    }
    ctx.globalAlpha = 1;
    const t = new THREE.CanvasTexture(canvas); t.wrapS = t.wrapT = THREE.RepeatWrapping;
    return { texture: t, roughness: 0.68, metalness: 0, repeat: [0.5, 0.45] };
  }

  // ── LINEN WEAVE ────────────────────────────────────────────────────────────
  if (mat.id === 'fabric_w') {
    ctx.fillStyle = base; ctx.fillRect(0, 0, S, S);
    const tw2 = 5;
    for (let y = 0; y < S; y += tw2) {
      for (let x = 0; x < S; x += tw2) {
        const even2 = ((x / tw2 + y / tw2) % 2 === 0);
        ctx.fillStyle = jitter(base, even2 ? 16 : 10); ctx.globalAlpha = even2 ? 0.55 : 0.35;
        ctx.fillRect(x, y, tw2, tw2);
      }
    }
    // Linen thread texture
    for (let i = 0; i < 3000; i++) {
      ctx.fillStyle = jitter(base, 20); ctx.globalAlpha = 0.08;
      ctx.fillRect(Math.random() * S, Math.random() * S, 1, 1);
    }
    ctx.globalAlpha = 1;
    const t = new THREE.CanvasTexture(canvas); t.wrapS = t.wrapT = THREE.RepeatWrapping;
    return { texture: t, roughness: 0.94, metalness: 0, repeat: [1, 1] };
  }

  // ── JUTE / SISAL ───────────────────────────────────────────────────────────
  if (mat.id === 'jute_wall') {
    ctx.fillStyle = base; ctx.fillRect(0, 0, S, S);
    for (let x = 0; x < S; x += 4) {
      ctx.strokeStyle = jitter(base, 22); ctx.globalAlpha = 0.45 + Math.random() * 0.4;
      ctx.lineWidth = 2 + Math.random(); ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x + (Math.random()-0.5)*6, S); ctx.stroke();
    }
    for (let y = 0; y < S; y += 4) {
      ctx.strokeStyle = jitter(base, 18); ctx.globalAlpha = 0.25 + Math.random() * 0.25;
      ctx.lineWidth = 1.5 + Math.random() * 0.5; ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(S, y + (Math.random()-0.5)*4); ctx.stroke();
    }
    ctx.globalAlpha = 1;
    const t = new THREE.CanvasTexture(canvas); t.wrapS = t.wrapT = THREE.RepeatWrapping;
    return { texture: t, roughness: 0.96, metalness: 0, repeat: [1, 1] };
  }

  // ── VENETIAN STUCCO ────────────────────────────────────────────────────────
  if (mat.id === 'venetian_stucco') {
    ctx.fillStyle = base; ctx.fillRect(0, 0, S, S);
    // Polished marmorino: soft cloudy colour drifts + burnished trowel sweeps
    for (let i = 0; i < 60; i++) {
      const x = Math.random() * S, y = Math.random() * S, r = 40 + Math.random() * 130;
      const v = (Math.random() - 0.5) * 16;
      const grd = ctx.createRadialGradient(x, y, 0, x, y, r);
      grd.addColorStop(0, `rgba(${Math.max(0,br+v)},${Math.max(0,bg+v)},${Math.max(0,bb+v)},0.20)`);
      grd.addColorStop(1, 'rgba(0,0,0,0)');
      ctx.fillStyle = grd; ctx.fillRect(x - r, y - r, r * 2, r * 2);
    }
    for (let i = 0; i < 90; i++) {
      ctx.strokeStyle = jitter(base, 10); ctx.globalAlpha = 0.05 + Math.random() * 0.08; ctx.lineWidth = 6 + Math.random() * 14;
      const x0 = Math.random()*S, y0 = Math.random()*S, ang = Math.random()*Math.PI*2, len = 60+Math.random()*160;
      ctx.beginPath(); ctx.moveTo(x0,y0);
      ctx.quadraticCurveTo(x0+Math.cos(ang+0.4)*len*0.5, y0+Math.sin(ang+0.4)*len*0.5, x0+Math.cos(ang)*len, y0+Math.sin(ang)*len);
      ctx.stroke();
    }
    for (let i = 0; i < 5; i++) {
      const x = Math.random()*S, y = Math.random()*S, r = 80+Math.random()*140;
      const g = ctx.createRadialGradient(x, y, 0, x, y, r);
      g.addColorStop(0, 'rgba(255,255,255,0.10)'); g.addColorStop(1, 'rgba(255,255,255,0)');
      ctx.fillStyle = g; ctx.globalAlpha = 1; ctx.fillRect(x-r, y-r, r*2, r*2);
    }
    ctx.globalAlpha = 1;
    const t = new THREE.CanvasTexture(canvas); t.wrapS = t.wrapT = THREE.RepeatWrapping;
    return { texture: t, roughness: 0.35, metalness: 0.02, repeat: [1, 1] };
  }

  // ── BOARD & BATTEN ─────────────────────────────────────────────────────────
  if (mat.id === 'board_batten') {
    const pc2 = mat.panelColor || '#d8d0bc';
    const bw3 = 90, battenW = 13;
    ctx.fillStyle = base; ctx.fillRect(0, 0, S, S);
    for (let i = 0; i < 60; i++) {
      ctx.strokeStyle = jitter(base, 8); ctx.globalAlpha = 0.05 + Math.random()*0.05; ctx.lineWidth = 0.5;
      const y = Math.random()*S; ctx.beginPath(); ctx.moveTo(0,y); ctx.lineTo(S,y+(Math.random()-0.5)*4); ctx.stroke();
    }
    ctx.globalAlpha = 1;
    for (let x = 0; x < S + bw3; x += bw3) {
      ctx.fillStyle = pc2; ctx.fillRect(x - battenW/2, 0, battenW, S);
      const g = ctx.createLinearGradient(x - battenW/2, 0, x + battenW/2, 0);
      g.addColorStop(0, 'rgba(0,0,0,0.20)'); g.addColorStop(0.3, 'rgba(255,255,255,0.30)');
      g.addColorStop(0.7, 'rgba(255,255,255,0.05)'); g.addColorStop(1, 'rgba(0,0,0,0.22)');
      ctx.fillStyle = g; ctx.fillRect(x - battenW/2, 0, battenW, S);
    }
    ctx.globalAlpha = 1;
    const t = new THREE.CanvasTexture(canvas); t.wrapS = t.wrapT = THREE.RepeatWrapping;
    return { texture: t, roughness: 0.75, metalness: 0, repeat: [0.5, 0.5] };
  }

  // ── CINDER BLOCK ────────────────────────────────────────────────────────────
  if (mat.id === 'cinder_block') {
    const grout4 = mat.groutColor || '#807c74';
    const cbw = 100, cbh = 50, g6 = 5;
    ctx.fillStyle = grout4; ctx.fillRect(0, 0, S, S);
    for (let row7 = 0; row7 < S / cbh + 1; row7++) {
      const off2 = (row7 % 2) * (cbw / 2);
      for (let col7 = -1; col7 < S / cbw + 1; col7++) {
        const bx2 = col7 * cbw + off2 + g6/2, by2 = row7 * cbh + g6/2, bw4 = cbw - g6, bh4 = cbh - g6;
        ctx.fillStyle = jitter(base, 12); ctx.globalAlpha = 1; ctx.fillRect(bx2, by2, bw4, bh4);
        for (let s2 = 0; s2 < 40; s2++) {
          ctx.fillStyle = `rgba(0,0,0,${Math.random()*0.10})`;
          ctx.fillRect(bx2 + Math.random()*bw4, by2 + Math.random()*bh4, 1.5+Math.random()*2, 1.5+Math.random()*2);
        }
        ctx.fillStyle = 'rgba(255,255,255,0.08)'; ctx.fillRect(bx2, by2, bw4, 3);
        ctx.fillStyle = 'rgba(0,0,0,0.14)'; ctx.fillRect(bx2, by2+bh4-3, bw4, 3);
      }
    }
    ctx.globalAlpha = 1;
    const t = new THREE.CanvasTexture(canvas); t.wrapS = t.wrapT = THREE.RepeatWrapping;
    return { texture: t, roughness: 0.92, metalness: 0, repeat: [0.4, 0.5] };
  }

  // ── RECLAIMED BRICK ─────────────────────────────────────────────────────────
  if (mat.id === 'reclaimed_brick') {
    const bw5 = 80, bh5 = 36, g7 = 5;
    const groutRb = mat.groutColor || '#5a4a3c';
    ctx.fillStyle = groutRb; ctx.fillRect(0, 0, S, S);
    for (let row8 = 0; row8 < S / bh5 + 1; row8++) {
      const off3 = (row8 % 2) * (bw5 / 2);
      for (let col8 = -1; col8 < S / bw5 + 1; col8++) {
        const bx3 = col8*bw5+off3+g7/2, by3 = row8*bh5+g7/2, bw6 = bw5-g7, bh6 = bh5-g7;
        ctx.fillStyle = jitter(base, 34); ctx.globalAlpha = 1;
        ctx.fillRect(bx3, by3, bw6, bh6);
        for (let d3 = 0; d3 < 3; d3++) {
          ctx.fillStyle = jitter(groutRb, 10); ctx.globalAlpha = 0.3 + Math.random()*0.3;
          ctx.fillRect(bx3 + Math.random()*bw6, by3 + Math.random()*bh6, 2+Math.random()*5, 2+Math.random()*4);
        }
        // Peeling whitewash patches — a hallmark of reclaimed brick
        if (Math.random() < 0.28) {
          ctx.fillStyle = 'rgba(240,236,228,' + (0.18+Math.random()*0.30) + ')';
          ctx.beginPath();
          ctx.ellipse(bx3+bw6*(0.3+Math.random()*0.4), by3+bh6*(0.3+Math.random()*0.4), bw6*0.25, bh6*0.35, Math.random()*Math.PI, 0, Math.PI*2);
          ctx.fill();
        }
        ctx.globalAlpha = 1;
        ctx.fillStyle = 'rgba(255,255,255,0.06)'; ctx.fillRect(bx3, by3, bw6, 2.5);
        ctx.fillStyle = 'rgba(0,0,0,0.16)'; ctx.fillRect(bx3, by3+bh6-2.5, bw6, 2.5);
      }
    }
    ctx.globalAlpha = 1;
    const t = new THREE.CanvasTexture(canvas); t.wrapS = t.wrapT = THREE.RepeatWrapping;
    return { texture: t, roughness: 0.94, metalness: 0, repeat: [0.4, 0.6] };
  }

  // ── BOOKMATCHED MARBLE ──────────────────────────────────────────────────────
  if (mat.id === 'bookmatched') {
    ctx.fillStyle = base; ctx.fillRect(0, 0, S, S);
    const halfW = S / 2;
    for (let i = 0; i < 70; i++) {
      const x = Math.random() * halfW, y = Math.random() * S, r = 20 + Math.random() * 55;
      const v = (Math.random() - 0.5) * 16;
      const grd = ctx.createRadialGradient(x, y, 0, x, y, r);
      grd.addColorStop(0, `rgba(${Math.max(0,br+v)},${Math.max(0,bg+v)},${Math.max(0,bb+v)},0.22)`);
      grd.addColorStop(1, 'rgba(0,0,0,0)');
      ctx.fillStyle = grd; ctx.fillRect(x - r, y - r, r * 2, r * 2);
    }
    const veinCol = `rgba(${Math.max(0,br-58)},${Math.max(0,bg-52)},${Math.max(0,bb-46)},`;
    for (let v = 0; v < 8; v++) {
      const x0 = Math.random() * halfW * 0.6, y0 = Math.random() * S;
      const cpx = halfW * (0.5 + Math.random() * 0.4), cpy = y0 + (Math.random()-0.5)*160;
      const x3 = halfW - Math.random() * 10, y3 = y0 + (Math.random()-0.5)*220;
      ctx.strokeStyle = veinCol + (0.55 + Math.random()*0.3) + ')';
      ctx.lineWidth = 0.6 + Math.random() * 2.2; ctx.globalAlpha = 1;
      ctx.beginPath(); ctx.moveTo(x0, y0); ctx.quadraticCurveTo(cpx, cpy, x3, y3); ctx.stroke();
    }
    ctx.globalAlpha = 1;
    // Mirror the left half onto the right half — the bookmatch seam
    ctx.save();
    ctx.translate(S, 0); ctx.scale(-1, 1);
    ctx.drawImage(canvas, 0, 0, halfW, S, 0, 0, halfW, S);
    ctx.restore();
    ctx.strokeStyle = 'rgba(0,0,0,0.10)'; ctx.lineWidth = 1.5; ctx.globalAlpha = 1;
    ctx.beginPath(); ctx.moveTo(halfW, 0); ctx.lineTo(halfW, S); ctx.stroke();
    const t = new THREE.CanvasTexture(canvas); t.wrapS = t.wrapT = THREE.RepeatWrapping;
    return { texture: t, roughness: 0.08, metalness: 0.03, repeat: [0.5, 0.5] };
  }

  // ── CHEVRON PANEL ─────────────────────────────────────────────────────────────
  if (mat.id === 'chevron_panel') {
    const gc5 = mat.grainColor || '#8a5c38';
    const bw7 = 30, bh7 = 12;
    for (let row9 = -2; row9 < S / bh7 + 4; row9++) {
      for (let col9 = -2; col9 < S / bw7 + 4; col9++) {
        const even2 = row9 % 2 === 0;
        ctx.fillStyle = jitter(base, 16);
        ctx.save();
        ctx.translate(col9*bw7 + (even2 ? 0 : bw7*0.5), row9*bh7);
        ctx.rotate(even2 ? Math.PI/4 : -Math.PI/4);
        ctx.fillRect(0, -bh7/2, bw7-1, bh7-1);
        ctx.fillStyle = 'rgba(255,255,255,0.15)'; ctx.fillRect(0, -bh7/2, bw7-1, 1.5);
        ctx.fillStyle = 'rgba(0,0,0,0.12)'; ctx.fillRect(0, bh7/2-2.5, bw7-1, 1.5);
        ctx.restore();
      }
    }
    ctx.globalAlpha = 0.06; ctx.strokeStyle = gc5; ctx.lineWidth = 0.5;
    for (let j2 = 0; j2 < 40; j2++) { const y2=(j2/40)*S; ctx.beginPath(); ctx.moveTo(0,y2); ctx.lineTo(S,y2); ctx.stroke(); }
    ctx.globalAlpha = 1;
    const t = new THREE.CanvasTexture(canvas); t.wrapS = t.wrapT = THREE.RepeatWrapping;
    return { texture: t, roughness: 0.60, metalness: 0, repeat: [0.5, 0.5] };
  }

  // ── PEGBOARD ───────────────────────────────────────────────────────────────
  if (mat.id === 'pegboard') {
    ctx.fillStyle = base; ctx.fillRect(0, 0, S, S);
    for (let i = 0; i < 40; i++) {
      ctx.strokeStyle = jitter(base, 14); ctx.globalAlpha = 0.05+Math.random()*0.06; ctx.lineWidth = 0.6;
      const y = Math.random()*S; ctx.beginPath(); ctx.moveTo(0,y); ctx.lineTo(S,y+(Math.random()-0.5)*6); ctx.stroke();
    }
    ctx.globalAlpha = 1;
    const hg = 26;
    for (let row10 = 0; row10 < S/hg+1; row10++) {
      for (let col10 = 0; col10 < S/hg+1; col10++) {
        const hx2 = col10*hg + hg/2, hy2 = row10*hg + hg/2;
        ctx.fillStyle = 'rgba(0,0,0,0.35)'; ctx.beginPath(); ctx.arc(hx2, hy2, 3.5, 0, Math.PI*2); ctx.fill();
        ctx.fillStyle = 'rgba(0,0,0,0.55)'; ctx.beginPath(); ctx.arc(hx2, hy2, 2.2, 0, Math.PI*2); ctx.fill();
        ctx.strokeStyle = 'rgba(255,255,255,0.18)'; ctx.lineWidth = 0.8;
        ctx.beginPath(); ctx.arc(hx2-0.8, hy2-0.8, 3.5, Math.PI, Math.PI*1.5); ctx.stroke();
      }
    }
    const t = new THREE.CanvasTexture(canvas); t.wrapS = t.wrapT = THREE.RepeatWrapping;
    return { texture: t, roughness: 0.62, metalness: 0, repeat: [0.7, 0.7] };
  }

  // ── HOUNDSTOOTH ──────────────────────────────────────────────────────────────
  if (mat.id === 'houndstooth') {
    const sc5 = mat.stripeColor || '#26241f';
    ctx.fillStyle = base; ctx.fillRect(0, 0, S, S);
    const g8 = 22;
    ctx.fillStyle = sc5; ctx.globalAlpha = 0.92;
    for (let row11 = -1; row11 < S/g8+2; row11++) {
      for (let col11 = -1; col11 < S/g8+2; col11++) {
        if ((row11+col11) % 2 !== 0) continue;
        const x2 = col11*g8, y2 = row11*g8;
        ctx.beginPath();
        ctx.moveTo(x2, y2);
        ctx.lineTo(x2+g8, y2);
        ctx.lineTo(x2+g8, y2+g8*0.5);
        ctx.lineTo(x2+g8*1.5, y2+g8*0.5);
        ctx.lineTo(x2+g8*1.5, y2+g8*1.5);
        ctx.lineTo(x2+g8*0.5, y2+g8*1.5);
        ctx.lineTo(x2+g8*0.5, y2+g8);
        ctx.lineTo(x2, y2+g8);
        ctx.closePath(); ctx.fill();
      }
    }
    ctx.globalAlpha = 1;
    const t = new THREE.CanvasTexture(canvas); t.wrapS = t.wrapT = THREE.RepeatWrapping;
    return { texture: t, roughness: 0.85, metalness: 0, repeat: [0.6, 0.6] };
  }

  // ── LIME-PLASTER FAMILY — Tadelakt / Roman Clay / Clay Plaster ─────────────
  // Trowelled mineral finishes: cloudy undulating tonal movement + soft curved
  // trowel sweeps + fine grain. Tadelakt gets a soft polished sheen (low
  // roughness); Roman clay and clay plaster stay matte.
  if (mat.id === 'tadelakt' || mat.id === 'roman_clay' || mat.id === 'clay_plaster') {
    ctx.fillStyle = base; ctx.fillRect(0, 0, S, S);
    const cloudV = mat.id === 'roman_clay' ? 20 : 15;
    for (let i = 0; i < 90; i++) {
      const x = Math.random() * S, y = Math.random() * S, rr = 50 + Math.random() * 150;
      const v = (Math.random() - 0.5) * cloudV;
      const grd = ctx.createRadialGradient(x, y, 0, x, y, rr);
      grd.addColorStop(0, `rgba(${br + v | 0},${bg + v | 0},${bb + v | 0},0.16)`); grd.addColorStop(1, 'rgba(0,0,0,0)');
      ctx.fillStyle = grd; ctx.globalAlpha = 1; ctx.fillRect(x - rr, y - rr, rr * 2, rr * 2);
    }
    for (let i = 0; i < 44; i++) {
      ctx.strokeStyle = jitter(base, mat.id === 'roman_clay' ? 16 : 11);
      ctx.globalAlpha = 0.05 + Math.random() * 0.06; ctx.lineWidth = 8 + Math.random() * 22;
      const x0 = Math.random() * S, y0 = Math.random() * S, ang = (Math.random() - 0.5) * 1.3, len = 80 + Math.random() * 170;
      ctx.beginPath(); ctx.moveTo(x0, y0);
      ctx.quadraticCurveTo(x0 + Math.cos(ang + 0.2) * len * 0.5, y0 + Math.sin(ang + 0.2) * len * 0.5, x0 + Math.cos(ang) * len, y0 + Math.sin(ang) * len);
      ctx.stroke();
    }
    ctx.globalAlpha = 1;
    for (let i = 0; i < 4000; i++) { const v = (Math.random() - 0.5) * 6; ctx.fillStyle = rgbStr(br + v, bg + v, bb + v); ctx.globalAlpha = 0.04; ctx.fillRect(Math.random() * S, Math.random() * S, 1, 1); }
    ctx.globalAlpha = 1;
    if (mat.id === 'tadelakt') {
      for (let i = 0; i < 10; i++) { const rx = Math.random() * S, ry = Math.random() * S, rr = 40 + Math.random() * 90; const g = ctx.createRadialGradient(rx, ry, 0, rx, ry, rr); g.addColorStop(0, 'rgba(255,255,255,0.07)'); g.addColorStop(1, 'rgba(255,255,255,0)'); ctx.fillStyle = g; ctx.fillRect(rx - rr, ry - rr, rr * 2, rr * 2); }
    }
    const t = new THREE.CanvasTexture(canvas); t.wrapS = t.wrapT = THREE.RepeatWrapping;
    const rough = mat.id === 'tadelakt' ? 0.24 : mat.id === 'clay_plaster' ? 0.80 : 0.88;
    return { texture: t, roughness: rough, metalness: mat.id === 'tadelakt' ? 0.03 : 0, repeat: [0.85, 0.85] };
  }

  // ── BOARD-FORMED CONCRETE ─────────────────────────────────────────────────
  // Poured concrete that took the imprint of timber shuttering: horizontal
  // board seams with faint grain impressions + a few tie-bolt holes.
  if (mat.id === 'board_concrete') {
    ctx.fillStyle = base; ctx.fillRect(0, 0, S, S);
    for (let i = 0; i < 600; i++) { ctx.fillStyle = `rgba(0,0,0,${Math.random() * 0.03})`; ctx.fillRect(Math.random() * S, Math.random() * S, 14 + Math.random() * 40, 14 + Math.random() * 40); }
    for (let i = 0; i < 300; i++) { ctx.fillStyle = `rgba(255,255,255,${Math.random() * 0.02})`; ctx.fillRect(Math.random() * S, Math.random() * S, 8 + Math.random() * 22, 8 + Math.random() * 22); }
    const bh = 46;
    for (let y = 0; y < S; y += bh) {
      ctx.globalAlpha = 1; ctx.fillStyle = 'rgba(0,0,0,0.14)'; ctx.fillRect(0, y, S, 2);
      ctx.fillStyle = 'rgba(255,255,255,0.06)'; ctx.fillRect(0, y + 2, S, 1);
      ctx.strokeStyle = 'rgba(0,0,0,0.05)';
      for (let j = 0; j < 5; j++) { const ly = y + 6 + Math.random() * (bh - 12); ctx.globalAlpha = 0.5; ctx.lineWidth = 0.5; ctx.beginPath(); ctx.moveTo(0, ly); ctx.lineTo(S, ly + (Math.random() - 0.5) * 4); ctx.stroke(); }
    }
    ctx.globalAlpha = 1;
    for (let i = 0; i < 6; i++) { const hx = (0.2 + Math.random() * 0.6) * S, hy = (0.15 + Math.random() * 0.7) * S; const g = ctx.createRadialGradient(hx, hy, 0, hx, hy, 6); g.addColorStop(0, 'rgba(0,0,0,0.3)'); g.addColorStop(1, 'rgba(0,0,0,0)'); ctx.fillStyle = g; ctx.beginPath(); ctx.arc(hx, hy, 6, 0, Math.PI * 2); ctx.fill(); }
    const t = new THREE.CanvasTexture(canvas); t.wrapS = t.wrapT = THREE.RepeatWrapping;
    return { texture: t, roughness: 0.90, metalness: 0, repeat: [0.7, 0.7] };
  }

  // ── 3D PANELS ─────────────────────────────────────────────────────────────
  // A grid of raised square panels on a recessed base, with bevelled edges
  // (light top/left, dark bottom/right) so they read as extruded.
  if (mat.id === 'panel_3d') {
    ctx.fillStyle = rgbStr(br - 14, bg - 14, bb - 14); ctx.fillRect(0, 0, S, S);
    const ps = 128, gap = 12, bev = 4;
    for (let ry = 0; ry < S; ry += ps) {
      for (let rx = 0; rx < S; rx += ps) {
        const x = rx + gap, y = ry + gap, w = ps - gap * 2, h = ps - gap * 2;
        ctx.fillStyle = jitter(base, 4); ctx.fillRect(x, y, w, h);
        ctx.fillStyle = 'rgba(255,255,255,0.28)'; ctx.fillRect(x, y, w, bev); ctx.fillRect(x, y, bev, h);
        ctx.fillStyle = 'rgba(0,0,0,0.28)'; ctx.fillRect(x, y + h - bev, w, bev); ctx.fillRect(x + w - bev, y, bev, h);
        ctx.fillStyle = 'rgba(0,0,0,0.12)'; ctx.fillRect(x - gap, y + h, w + gap * 2, 3);
      }
    }
    const t = new THREE.CanvasTexture(canvas); t.wrapS = t.wrapT = THREE.RepeatWrapping;
    return { texture: t, roughness: 0.68, metalness: 0, repeat: [0.5, 0.5] };
  }

  // ── BRUSHED METAL (brass / steel) ─────────────────────────────────────────
  // Metallic panel with fine horizontal brushing + a soft vertical sheen band;
  // gloss + reflectivity come from low roughness / high metalness. The tint is
  // just the material's base colour (warm for brass, cool grey for steel).
  if (mat.id === 'brushed_brass' || mat.id === 'brushed_steel') {
    ctx.fillStyle = base; ctx.fillRect(0, 0, S, S);
    for (let y = 0; y < S; y++) {
      const v = (Math.random() - 0.5) * 26;
      ctx.strokeStyle = rgbStr(br + v, bg + v * 0.9, bb + v * 0.6);
      ctx.globalAlpha = 0.16 + Math.random() * 0.2; ctx.lineWidth = 0.5 + Math.random() * 0.6;
      ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(S, y); ctx.stroke();
    }
    ctx.globalAlpha = 1;
    const g = ctx.createLinearGradient(0, 0, 0, S);
    g.addColorStop(0, 'rgba(255,255,255,0.06)'); g.addColorStop(0.5, 'rgba(0,0,0,0.05)'); g.addColorStop(1, 'rgba(255,255,255,0.05)');
    ctx.fillStyle = g; ctx.fillRect(0, 0, S, S);
    const t = new THREE.CanvasTexture(canvas); t.wrapS = t.wrapT = THREE.RepeatWrapping;
    return { texture: t, roughness: 0.34, metalness: 0.85, repeat: [1, 1] };
  }

  // ── ACOUSTIC WOOD SLAT ────────────────────────────────────────────────────
  // Vertical wood battens mounted on a dark acoustic-felt backing: rounded slats
  // (bright centre → dark edges) with wood grain, separated by shadowed gaps.
  if (mat.id === 'wood_slat') {
    ctx.fillStyle = '#191410'; ctx.fillRect(0, 0, S, S);
    const slW = 34, gap = 9, gc = mat.grainColor || '#6e4a2a';
    for (let x = 2; x < S; x += slW + gap) {
      ctx.save(); ctx.beginPath(); ctx.rect(x, 0, slW, S); ctx.clip();
      ctx.fillStyle = jitter(base, 10); ctx.fillRect(x, 0, slW, S);
      const rg = ctx.createLinearGradient(x, 0, x + slW, 0);
      rg.addColorStop(0, 'rgba(0,0,0,0.30)'); rg.addColorStop(0.3, 'rgba(255,255,255,0.12)'); rg.addColorStop(0.5, 'rgba(255,255,255,0.16)'); rg.addColorStop(0.72, 'rgba(255,255,255,0.04)'); rg.addColorStop(1, 'rgba(0,0,0,0.32)');
      ctx.fillStyle = rg; ctx.fillRect(x, 0, slW, S);
      ctx.strokeStyle = gc;
      for (let j = 0; j < 26; j++) { const gx = x + Math.random() * slW; ctx.globalAlpha = 0.05 + Math.random() * 0.08; ctx.lineWidth = 0.4 + Math.random() * 0.8; ctx.beginPath(); ctx.moveTo(gx, 0); ctx.bezierCurveTo(gx + (Math.random() - 0.5) * 4, S * 0.4, gx + (Math.random() - 0.5) * 4, S * 0.7, gx + (Math.random() - 0.5) * 3, S); ctx.stroke(); }
      ctx.globalAlpha = 1; ctx.restore();
    }
    const t = new THREE.CanvasTexture(canvas); t.wrapS = t.wrapT = THREE.RepeatWrapping;
    return { texture: t, roughness: 0.55, metalness: 0, repeat: [0.5, 1] };
  }

  // ── TAMBOUR / REEDED WOOD ─────────────────────────────────────────────────
  // Continuous rounded wood reeds (no gaps): sine-shaded flutes + wood grain.
  if (mat.id === 'tambour') {
    ctx.fillStyle = base; ctx.fillRect(0, 0, S, S);
    const fw = 20, gc = mat.grainColor || '#8a5c38';
    for (let x = 0; x < S; x += fw) {
      const rg = ctx.createLinearGradient(x, 0, x + fw, 0);
      rg.addColorStop(0, 'rgba(0,0,0,0.22)'); rg.addColorStop(0.35, 'rgba(255,255,255,0.14)'); rg.addColorStop(0.5, 'rgba(255,255,255,0.10)'); rg.addColorStop(0.72, 'rgba(255,255,255,0.02)'); rg.addColorStop(1, 'rgba(0,0,0,0.24)');
      ctx.fillStyle = rg; ctx.globalAlpha = 1; ctx.fillRect(x, 0, fw, S);
      ctx.fillStyle = 'rgba(0,0,0,0.22)'; ctx.fillRect(x + fw - 1.5, 0, 1.5, S);
    }
    ctx.strokeStyle = gc;
    for (let j = 0; j < 70; j++) { const y = Math.random() * S; ctx.globalAlpha = 0.04 + Math.random() * 0.06; ctx.lineWidth = 0.4 + Math.random() * 0.8; ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(S, y + (Math.random() - 0.5) * 6); ctx.stroke(); }
    ctx.globalAlpha = 1;
    const t = new THREE.CanvasTexture(canvas); t.wrapS = t.wrapT = THREE.RepeatWrapping;
    return { texture: t, roughness: 0.50, metalness: 0, repeat: [0.5, 1] };
  }

  // ── TERRAZZO ──────────────────────────────────────────────────────────────
  // Polished terrazzo: multi-colour aggregate chips scattered in a binder.
  if (mat.id === 'terrazzo_w') {
    ctx.fillStyle = base; ctx.fillRect(0, 0, S, S);
    const chips = ['#c0705a', '#7a8a6a', '#d8c8a8', '#4a4a52', '#e8e4dc', '#a89078', '#8a8a92'];
    for (let i = 0; i < 1400; i++) {
      ctx.fillStyle = i % 3 === 0 ? chips[Math.floor(Math.random() * chips.length)] : jitter(base, 24);
      ctx.globalAlpha = 0.6 + Math.random() * 0.4;
      const size = 3 + Math.random() * 14;
      ctx.save(); ctx.translate(Math.random() * S, Math.random() * S); ctx.rotate(Math.random() * Math.PI);
      const pts = 4 + Math.floor(Math.random() * 4); ctx.beginPath();
      for (let p = 0; p < pts; p++) { const a = (p / pts) * Math.PI * 2 + Math.random() * 0.5; const rr = size * (0.4 + Math.random() * 0.6); p === 0 ? ctx.moveTo(Math.cos(a) * rr, Math.sin(a) * rr) : ctx.lineTo(Math.cos(a) * rr, Math.sin(a) * rr); }
      ctx.closePath(); ctx.fill(); ctx.restore();
    }
    ctx.globalAlpha = 1;
    const t = new THREE.CanvasTexture(canvas); t.wrapS = t.wrapT = THREE.RepeatWrapping;
    return { texture: t, roughness: 0.28, metalness: 0.03, repeat: [0.6, 0.6] };
  }

  // ── SHOU SUGI BAN (charred wood) ──────────────────────────────────────────
  // Charred cedar planks: near-black boards with an "alligator" crackle of warm
  // grey highlight cracks + shadowed plank gaps.
  if (mat.id === 'shou_sugi') {
    const pw = 60, nc = Math.ceil(S / pw) + 1;
    ctx.fillStyle = base; ctx.fillRect(0, 0, S, S);
    for (let i = 0; i < nc; i++) {
      ctx.fillStyle = jitter(base, 14); ctx.fillRect(i * pw, 0, pw - 2, S);
      ctx.fillStyle = 'rgba(0,0,0,0.40)'; ctx.fillRect(i * pw + pw - 3, 0, 3, S);
      ctx.save(); ctx.beginPath(); ctx.rect(i * pw, 0, pw - 2, S); ctx.clip();
      for (let j = 0; j < 70; j++) {
        ctx.strokeStyle = rgbStr(br + 42, bg + 36, bb + 28);
        ctx.globalAlpha = 0.05 + Math.random() * 0.10; ctx.lineWidth = 0.5 + Math.random() * 1.2;
        const x0 = i * pw + Math.random() * pw, y0 = Math.random() * S;
        ctx.beginPath(); ctx.moveTo(x0, y0); ctx.lineTo(x0 + (Math.random() - 0.5) * 14, y0 + 8 + Math.random() * 22); ctx.stroke();
      }
      ctx.restore();
    }
    ctx.globalAlpha = 1;
    const t = new THREE.CanvasTexture(canvas); t.wrapS = t.wrapT = THREE.RepeatWrapping;
    return { texture: t, roughness: 0.50, metalness: 0.02, repeat: [0.5, 0.5] };
  }

  // ── CORTEN / WEATHERING STEEL ─────────────────────────────────────────────
  // Rusted steel plates: orange-brown patina blotches + vertical weathering
  // runoff streaks + fine grain + plate seams.
  if (mat.id === 'corten') {
    ctx.fillStyle = base; ctx.fillRect(0, 0, S, S);
    const rustCols = ['#8a4a28', '#a85e30', '#6e3a22', '#b87038', '#5a3020'];
    for (let i = 0; i < 200; i++) {
      ctx.fillStyle = rustCols[Math.floor(Math.random() * rustCols.length)];
      ctx.globalAlpha = 0.10 + Math.random() * 0.16;
      const x = Math.random() * S, y = Math.random() * S, rr = 20 + Math.random() * 90;
      ctx.beginPath(); ctx.ellipse(x, y, rr, rr * 0.7, Math.random() * Math.PI, 0, Math.PI * 2); ctx.fill();
    }
    for (let i = 0; i < 40; i++) {
      ctx.strokeStyle = rustCols[Math.floor(Math.random() * rustCols.length)];
      ctx.globalAlpha = 0.06 + Math.random() * 0.08; ctx.lineWidth = 1 + Math.random() * 4;
      const x = Math.random() * S, y0 = Math.random() * S * 0.4;
      ctx.beginPath(); ctx.moveTo(x, y0); ctx.lineTo(x + (Math.random() - 0.5) * 10, y0 + 60 + Math.random() * 180); ctx.stroke();
    }
    for (let i = 0; i < 4000; i++) { const v = (Math.random() - 0.5) * 20; ctx.fillStyle = rgbStr(br + v, bg + v * 0.8, bb + v * 0.6); ctx.globalAlpha = 0.06; ctx.fillRect(Math.random() * S, Math.random() * S, 1, 1); }
    ctx.globalAlpha = 1;
    ctx.fillStyle = 'rgba(0,0,0,0.16)'; ctx.fillRect(0, S * 0.5, S, 2); ctx.fillRect(S * 0.5, 0, 2, S);
    const t = new THREE.CanvasTexture(canvas); t.wrapS = t.wrapT = THREE.RepeatWrapping;
    return { texture: t, roughness: 0.72, metalness: 0.15, repeat: [0.6, 0.6] };
  }

  // ── CANE WEBBING (rattan) ─────────────────────────────────────────────────
  // Open octagonal cane weave: a rattan-toned field with shadowed octagon holes
  // and light/dark strand lines running through it.
  if (mat.id === 'cane_web') {
    const gapCol = rgbStr(br - 46, bg - 48, bb - 50);
    ctx.fillStyle = jitter(base, 6); ctx.fillRect(0, 0, S, S);
    const cs = 30, r = cs * 0.32;
    for (let ry = 0; ry <= S; ry += cs) {
      for (let rx = 0; rx <= S; rx += cs) {
        ctx.fillStyle = gapCol; ctx.globalAlpha = 0.9;
        ctx.beginPath();
        for (let k = 0; k < 8; k++) { const a = (k / 8) * Math.PI * 2 + Math.PI / 8; const px = rx + Math.cos(a) * r, py = ry + Math.sin(a) * r; k === 0 ? ctx.moveTo(px, py) : ctx.lineTo(px, py); }
        ctx.closePath(); ctx.fill();
      }
    }
    ctx.globalAlpha = 1;
    ctx.strokeStyle = 'rgba(255,255,255,0.10)'; ctx.lineWidth = 1;
    for (let x = 0; x <= S; x += cs) { ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, S); ctx.stroke(); }
    for (let y = 0; y <= S; y += cs) { ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(S, y); ctx.stroke(); }
    ctx.strokeStyle = 'rgba(0,0,0,0.10)';
    for (let x = cs / 2; x <= S; x += cs) { ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, S); ctx.stroke(); }
    for (let y = cs / 2; y <= S; y += cs) { ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(S, y); ctx.stroke(); }
    const t = new THREE.CanvasTexture(canvas); t.wrapS = t.wrapT = THREE.RepeatWrapping;
    return { texture: t, roughness: 0.74, metalness: 0, repeat: [0.35, 0.35] };
  }

  return null;
}

// ── CEILING TEXTURES ──────────────────────────────────────────────────────────
export function makeCeilingTexture(mat) {
  if (!mat) return null;
  if (mat.__custom) {
    const S = 512;
    const cv = document.createElement('canvas');
    cv.width = cv.height = S;
    const cx = cv.getContext('2d');
    const base = mat.baseColor || mat.color || '#f5f5f0';
    const { r: br, g: bg, b: bb } = hexRgb(base);
    cx.fillStyle = base; cx.fillRect(0, 0, S, S);
    for (let i = 0; i < 7000; i++) {
      const v = (Math.random() - 0.5) * 7;
      cx.fillStyle = rgbStr(br + v, bg + v, bb + v);
      cx.globalAlpha = 0.05;
      cx.fillRect(Math.random() * S, Math.random() * S, 1, 1);
    }
    cx.globalAlpha = 1;
    const t = new THREE.CanvasTexture(cv);
    t.wrapS = t.wrapT = THREE.RepeatWrapping;
    t.repeat.set(2, 2);
    return { texture: t, ...finishToPBR(mat.finish) };
  }

  const S = 512;
  const canvas = document.createElement('canvas');
  canvas.width = canvas.height = S;
  const ctx = canvas.getContext('2d');
  const base = mat.color;
  const { r: br, g: bg, b: bb } = hexRgb(base);
  ctx.fillStyle = base; ctx.fillRect(0, 0, S, S);

  // ── PLAIN CEILINGS ─────────────────────────────────────────────────────────
  // Subtle paint stipple so plain ceilings have a real painted look
  if (mat.category === 'plain') {
    for (let i = 0; i < 5000; i++) {
      const v = (Math.random() - 0.5) * 7;
      ctx.fillStyle = rgbStr(br + v, bg + v, bb + v);
      ctx.globalAlpha = 0.06;
      ctx.fillRect(Math.random() * S, Math.random() * S, 1, 1);
    }
    ctx.globalAlpha = 1;
    const t = new THREE.CanvasTexture(canvas);
    t.wrapS = t.wrapT = THREE.RepeatWrapping;
    return { texture: t, roughness: 0.94, metalness: 0, repeat: [2, 2] };
  }

  // ── WOOD PLANK CEILING (natural / whitewashed / walnut — colour-driven) ────
  if (mat.id === 'wood_ceil' || mat.id === 'wood_white_c' || mat.id === 'wood_dark_c') {
    const gc3=mat.grainColor, ph=56, nr=Math.ceil(S/ph)+2;
    for (let i=0; i<nr; i++) {
      ctx.fillStyle=jitter(base,16); ctx.globalAlpha=1; ctx.fillRect(0,i*ph,S,ph-2);
    }
    ctx.strokeStyle=gc3;
    for (let j=0; j<40; j++) { const x=(j/40)*S; ctx.globalAlpha=0.05+Math.random()*0.09; ctx.lineWidth=0.4+Math.random()*0.8; ctx.beginPath(); ctx.moveTo(x,0); ctx.lineTo(x+(Math.random()-0.5)*10,S); ctx.stroke(); }
    ctx.globalAlpha=0.35; ctx.fillStyle=gc3;
    for (let i=0; i<nr; i++) ctx.fillRect(0,i*ph-1,S,2);
    ctx.globalAlpha=1;
    const t=new THREE.CanvasTexture(canvas); t.wrapS=t.wrapT=THREE.RepeatWrapping;
    return { texture:t, roughness:0.75, metalness:0, repeat:[3,3] };
  }

  // ── BEADBOARD ─────────────────────────────────────────────────────────────
  if (mat.id === 'beadboard') {
    const bw=20, nc=Math.ceil(S/bw)+2;
    for (let i=0; i<nc; i++) {
      ctx.fillStyle=jitter(base,6); ctx.globalAlpha=1; ctx.fillRect(i*bw,0,bw-1,S);
      ctx.fillStyle='rgba(255,255,255,0.22)'; ctx.fillRect(i*bw+1,0,3,S);
      ctx.fillStyle='rgba(0,0,0,0.10)'; ctx.fillRect(i*bw+bw-3,0,2,S);
    }
    const t=new THREE.CanvasTexture(canvas); t.wrapS=t.wrapT=THREE.RepeatWrapping;
    return { texture:t, roughness:0.70, metalness:0, repeat:[4,4] };
  }

  // ── TEXTURED PLASTER CEILING ──────────────────────────────────────────────
  if (mat.id === 'plaster_c') {
    for (let i=0; i<320; i++) {
      ctx.strokeStyle=jitter(base,10); ctx.globalAlpha=0.06+Math.random()*0.08; ctx.lineWidth=1+Math.random()*3;
      const x0=Math.random()*S, y0=Math.random()*S, ang=Math.random()*Math.PI*2, len=20+Math.random()*65;
      ctx.beginPath(); ctx.moveTo(x0,y0); ctx.lineTo(x0+Math.cos(ang)*len, y0+Math.sin(ang)*len); ctx.stroke();
    }
    ctx.globalAlpha=1;
    const t=new THREE.CanvasTexture(canvas); t.wrapS=t.wrapT=THREE.RepeatWrapping;
    return { texture:t, roughness:0.88, metalness:0, repeat:[2,2] };
  }

  // ── COFFERED ───────────────────────────────────────────────────────────────
  if (mat.id === 'coffered') {
    const cs = 128, border = 16;
    for (let row = 0; row < S/cs+1; row++) {
      for (let col = 0; col < S/cs+1; col++) {
        const x = col*cs, y = row*cs;
        // Recessed panel — deep bevel border, lighter flat centre
        ctx.fillStyle = jitter(base, 6); ctx.globalAlpha = 1;
        ctx.fillRect(x+border, y+border, cs-border*2, cs-border*2);
        const bevel = ctx.createLinearGradient(x, y, x+border, y+border);
        bevel.addColorStop(0, 'rgba(0,0,0,0.28)'); bevel.addColorStop(1, 'rgba(0,0,0,0)');
        ctx.fillStyle = bevel; ctx.fillRect(x, y, cs, cs);
        ctx.fillStyle = 'rgba(255,255,255,0.22)'; ctx.fillRect(x, y, cs, 3); ctx.fillRect(x, y, 3, cs);
        ctx.fillStyle = 'rgba(0,0,0,0.20)';
        ctx.fillRect(x, y+cs-4, cs, 4); ctx.fillRect(x+cs-4, y, 4, cs);
        ctx.strokeStyle = 'rgba(0,0,0,0.14)'; ctx.lineWidth = 2;
        ctx.strokeRect(x+border, y+border, cs-border*2, cs-border*2);
      }
    }
    ctx.globalAlpha = 1;
    const t = new THREE.CanvasTexture(canvas); t.wrapS = t.wrapT = THREE.RepeatWrapping;
    return { texture: t, roughness: 0.82, metalness: 0, repeat: [1.5, 1.5] };
  }

  // ── EXPOSED BEAMS ────────────────────────────────────────────────────────────
  if (mat.id === 'exposed_beam' || mat.id === 'beam_white_c') {
    const gcB = mat.grainColor || '#a07040';
    ctx.fillStyle = '#e8e4dc'; ctx.fillRect(0, 0, S, S); // plain ceiling between beams
    const beamW = 46, spacing = 128;
    for (let x = -beamW; x < S + spacing; x += spacing) {
      ctx.fillStyle = jitter(base, 16); ctx.globalAlpha = 1;
      ctx.fillRect(x, 0, beamW, S);
      for (let j = 0; j < 26; j++) {
        const yy = (j/26)*S;
        ctx.strokeStyle = jitter(gcB, 16); ctx.globalAlpha = 0.06+Math.random()*0.10; ctx.lineWidth = 0.4+Math.random()*0.8;
        ctx.beginPath(); ctx.moveTo(x, yy); ctx.lineTo(x+beamW, yy+(Math.random()-0.5)*6); ctx.stroke();
      }
      ctx.globalAlpha = 1;
      ctx.fillStyle = 'rgba(255,255,255,0.14)'; ctx.fillRect(x, 0, 4, S);
      ctx.fillStyle = 'rgba(0,0,0,0.24)'; ctx.fillRect(x+beamW-4, 0, 4, S);
      ctx.fillStyle = 'rgba(0,0,0,0.30)'; ctx.fillRect(x-4, 0, 4, S); ctx.fillRect(x+beamW, 0, 4, S);
    }
    const t = new THREE.CanvasTexture(canvas); t.wrapS = t.wrapT = THREE.RepeatWrapping;
    return { texture: t, roughness: 0.70, metalness: 0, repeat: [1.5, 1.5] };
  }

  // ── PRESSED TIN ──────────────────────────────────────────────────────────────
  if (mat.id === 'tin_ceil') {
    const ts = 64, g = 2;
    ctx.fillStyle = base; ctx.fillRect(0, 0, S, S);
    for (let row = 0; row < S/ts+1; row++) {
      for (let col = 0; col < S/ts+1; col++) {
        const x = col*ts+g, y = row*ts+g, w = ts-g*2, h = ts-g*2;
        const cx2 = x+w/2, cy2 = y+h/2;
        // Stamped diamond boss, embossed highlight/shadow
        const emboss = ctx.createRadialGradient(cx2-w*0.18, cy2-h*0.18, 0, cx2, cy2, w*0.62);
        emboss.addColorStop(0, 'rgba(255,255,255,0.55)');
        emboss.addColorStop(0.45, 'rgba(255,255,255,0.10)');
        emboss.addColorStop(0.75, 'rgba(0,0,0,0.16)');
        emboss.addColorStop(1, 'rgba(0,0,0,0.34)');
        ctx.fillStyle = jitter(base, 4); ctx.globalAlpha = 1; ctx.fillRect(x, y, w, h);
        ctx.fillStyle = emboss; ctx.globalAlpha = 1;
        ctx.beginPath();
        ctx.moveTo(cx2, y+3); ctx.lineTo(x+w-3, cy2); ctx.lineTo(cx2, y+h-3); ctx.lineTo(x+3, cy2);
        ctx.closePath(); ctx.fill();
        ctx.strokeStyle = 'rgba(0,0,0,0.30)'; ctx.lineWidth = 1.4;
        ctx.strokeRect(x+0.5, y+0.5, w-1, h-1);
      }
    }
    ctx.globalAlpha = 1;
    const t = new THREE.CanvasTexture(canvas); t.wrapS = t.wrapT = THREE.RepeatWrapping;
    return { texture: t, roughness: 0.28, metalness: 0.72, repeat: [2, 2] };
  }

  // ── TONGUE & GROOVE ──────────────────────────────────────────────────────────
  if (mat.id === 'plank_ceil') {
    const gcT = mat.grainColor || '#b8a080';
    const pw = 46, nc = Math.ceil(S/pw)+2;
    for (let i = 0; i < nc; i++) {
      ctx.fillStyle = jitter(base, 10); ctx.globalAlpha = 1;
      ctx.fillRect(i*pw, 0, pw-3, S);
      ctx.fillStyle = 'rgba(255,255,255,0.16)'; ctx.fillRect(i*pw, 0, 2, S);
      // Crisp painted-plank groove — cleaner edge than raw open wood grain
      ctx.fillStyle = gcT; ctx.globalAlpha = 0.30; ctx.fillRect(i*pw+pw-5, 0, 2, S);
      ctx.globalAlpha = 0.14; ctx.fillRect(i*pw+pw-3, 0, 2, S);
    }
    ctx.globalAlpha = 1;
    const t = new THREE.CanvasTexture(canvas); t.wrapS = t.wrapT = THREE.RepeatWrapping;
    return { texture: t, roughness: 0.62, metalness: 0, repeat: [3, 3] };
  }

  // ── MIRROR PANEL ─────────────────────────────────────────────────────────────
  if (mat.id === 'mirror_ceil') {
    const ps = 128, g2 = 4;
    ctx.fillStyle = '#0c0c10'; ctx.fillRect(0, 0, S, S);
    for (let row = 0; row < S/ps+1; row++) {
      for (let col = 0; col < S/ps+1; col++) {
        const x = col*ps+g2, y = row*ps+g2, w = ps-g2*2, h = ps-g2*2;
        ctx.fillStyle = base; ctx.globalAlpha = 1; ctx.fillRect(x, y, w, h);
        const refl = ctx.createLinearGradient(x, y, x+w, y+h);
        refl.addColorStop(0, 'rgba(255,255,255,0.55)');
        refl.addColorStop(0.35, 'rgba(255,255,255,0.08)');
        refl.addColorStop(0.6, 'rgba(0,0,0,0.10)');
        refl.addColorStop(1, 'rgba(255,255,255,0.20)');
        ctx.fillStyle = refl; ctx.fillRect(x, y, w, h);
        ctx.strokeStyle = 'rgba(0,0,0,0.5)'; ctx.lineWidth = 1;
        ctx.strokeRect(x+0.5, y+0.5, w-1, h-1);
      }
    }
    ctx.globalAlpha = 1;
    const t = new THREE.CanvasTexture(canvas); t.wrapS = t.wrapT = THREE.RepeatWrapping;
    return { texture: t, roughness: 0.03, metalness: 0.10, repeat: [1.5, 1.5] };
  }

  // ── ACOUSTIC DROP TILE ───────────────────────────────────────────────────────
  if (mat.id === 'acoustic_tile') {
    const ts2 = 60, g3 = 3;
    ctx.fillStyle = mat.groutColor || '#b0aca4'; ctx.fillRect(0, 0, S, S);
    for (let row = 0; row < S/ts2+1; row++) {
      for (let col = 0; col < S/ts2+1; col++) {
        const x = col*ts2+g3, y = row*ts2+g3, w = ts2-g3*2, h = ts2-g3*2;
        ctx.fillStyle = jitter(base, 5); ctx.globalAlpha = 1; ctx.fillRect(x, y, w, h);
        // Fine acoustic perforation stipple
        for (let i = 0; i < 90; i++) {
          ctx.fillStyle = 'rgba(0,0,0,0.10)'; ctx.globalAlpha = 1;
          ctx.beginPath(); ctx.arc(x+2+Math.random()*(w-4), y+2+Math.random()*(h-4), 0.5, 0, Math.PI*2); ctx.fill();
        }
        ctx.fillStyle = 'rgba(255,255,255,0.10)'; ctx.fillRect(x, y, w, 2);
        ctx.fillStyle = 'rgba(0,0,0,0.12)'; ctx.fillRect(x, y+h-2, w, 2);
      }
    }
    ctx.globalAlpha = 1;
    const t = new THREE.CanvasTexture(canvas); t.wrapS = t.wrapT = THREE.RepeatWrapping;
    return { texture: t, roughness: 0.92, metalness: 0, repeat: [2, 2] };
  }

  // ── SLATTED WOOD CEILING ─────────────────────────────────────────────────────
  if (mat.id === 'slat_ceil') {
    const gcS = mat.grainColor || '#9a6c3e';
    const slW = 34, gap = 8;
    ctx.fillStyle = '#141010'; ctx.fillRect(0, 0, S, S);
    for (let col = 0; col < S/(slW+gap)+1; col++) {
      const x = col*(slW+gap);
      ctx.fillStyle = jitter(base, 14); ctx.globalAlpha = 1;
      ctx.fillRect(x, 0, slW, S);
      for (let i = 0; i < 22; i++) {
        const yy = (i/22)*S;
        ctx.strokeStyle = jitter(gcS, 16); ctx.globalAlpha = 0.06+Math.random()*0.08; ctx.lineWidth = 0.4+Math.random()*0.7;
        ctx.beginPath(); ctx.moveTo(x, yy); ctx.lineTo(x+slW, yy+(Math.random()-0.5)*5); ctx.stroke();
      }
      ctx.globalAlpha = 1;
      ctx.fillStyle = 'rgba(255,255,255,0.14)'; ctx.fillRect(x, 0, 3, S);
      ctx.fillStyle = 'rgba(0,0,0,0.20)'; ctx.fillRect(x+slW-3, 0, 3, S);
    }
    const t = new THREE.CanvasTexture(canvas); t.wrapS = t.wrapT = THREE.RepeatWrapping;
    return { texture: t, roughness: 0.66, metalness: 0, repeat: [1.5, 1.5] };
  }

  // ── PRESSED COPPER ───────────────────────────────────────────────────────────
  if (mat.id === 'copper_ceil') {
    const ts3 = 58, g4 = 2;
    const patina = mat.groutColor || '#7c4726';
    ctx.fillStyle = base; ctx.fillRect(0, 0, S, S);
    for (let row = 0; row < S/ts3+1; row++) {
      for (let col = 0; col < S/ts3+1; col++) {
        const x = col*ts3+g4, y = row*ts3+g4, w = ts3-g4*2, h = ts3-g4*2;
        const cx2 = x+w/2, cy2 = y+h/2;
        ctx.fillStyle = jitter(base, 10); ctx.globalAlpha = 1; ctx.fillRect(x, y, w, h);
        const emboss = ctx.createRadialGradient(cx2-w*0.2, cy2-h*0.2, 0, cx2, cy2, w*0.6);
        emboss.addColorStop(0, 'rgba(255,220,180,0.5)');
        emboss.addColorStop(0.5, 'rgba(255,255,255,0.06)');
        emboss.addColorStop(1, 'rgba(0,0,0,0.30)');
        ctx.fillStyle = emboss; ctx.beginPath(); ctx.arc(cx2, cy2, w*0.42, 0, Math.PI*2); ctx.fill();
        // Weathered patina speckle
        if (Math.random() < 0.35) {
          ctx.fillStyle = jitter(patina, 14); ctx.globalAlpha = 0.20 + Math.random()*0.25;
          ctx.beginPath(); ctx.ellipse(x+Math.random()*w, y+Math.random()*h, w*0.18, h*0.14, Math.random()*Math.PI, 0, Math.PI*2); ctx.fill();
        }
        ctx.globalAlpha = 1;
        ctx.strokeStyle = 'rgba(0,0,0,0.28)'; ctx.lineWidth = 1.2;
        ctx.strokeRect(x+0.5, y+0.5, w-1, h-1);
      }
    }
    ctx.globalAlpha = 1;
    const t = new THREE.CanvasTexture(canvas); t.wrapS = t.wrapT = THREE.RepeatWrapping;
    return { texture: t, roughness: 0.32, metalness: 0.68, repeat: [2, 2] };
  }

  // ── VENETIAN CEILING ─────────────────────────────────────────────────────────
  if (mat.id === 'venetian_ceil') {
    for (let i = 0; i < 55; i++) {
      const x = Math.random()*S, y = Math.random()*S, r = 35+Math.random()*120;
      const v = (Math.random()-0.5)*14;
      const grd = ctx.createRadialGradient(x, y, 0, x, y, r);
      grd.addColorStop(0, `rgba(${Math.max(0,br+v)},${Math.max(0,bg+v)},${Math.max(0,bb+v)},0.18)`);
      grd.addColorStop(1, 'rgba(0,0,0,0)');
      ctx.fillStyle = grd; ctx.fillRect(x-r, y-r, r*2, r*2);
    }
    for (let i = 0; i < 70; i++) {
      ctx.strokeStyle = jitter(base, 9); ctx.globalAlpha = 0.05+Math.random()*0.07; ctx.lineWidth = 5+Math.random()*12;
      const x0=Math.random()*S, y0=Math.random()*S, ang=Math.random()*Math.PI*2, len=50+Math.random()*140;
      ctx.beginPath(); ctx.moveTo(x0,y0);
      ctx.quadraticCurveTo(x0+Math.cos(ang+0.4)*len*0.5, y0+Math.sin(ang+0.4)*len*0.5, x0+Math.cos(ang)*len, y0+Math.sin(ang)*len);
      ctx.stroke();
    }
    ctx.globalAlpha = 1;
    const t = new THREE.CanvasTexture(canvas); t.wrapS = t.wrapT = THREE.RepeatWrapping;
    return { texture: t, roughness: 0.40, metalness: 0.02, repeat: [1.5, 1.5] };
  }

  // ── GOLD LEAF ────────────────────────────────────────────────────────────────
  if (mat.id === 'gold_leaf') {
    const leaf = 46;
    ctx.fillStyle = base; ctx.fillRect(0, 0, S, S);
    for (let row = 0; row < S/leaf+1; row++) {
      for (let col = 0; col < S/leaf+1; col++) {
        const x = col*leaf, y = row*leaf;
        ctx.fillStyle = jitter(base, 14); ctx.globalAlpha = 0.5+Math.random()*0.5;
        ctx.fillRect(x, y, leaf, leaf);
        ctx.globalAlpha = 1;
        // Individual leaf-square seam — a hallmark of hand-applied gold leaf
        ctx.strokeStyle = 'rgba(0,0,0,0.10)'; ctx.lineWidth = 0.6;
        ctx.strokeRect(x+0.5, y+0.5, leaf-1, leaf-1);
      }
    }
    for (let i = 0; i < 8; i++) {
      const x = Math.random()*S, y = Math.random()*S, r = 60+Math.random()*140;
      const g = ctx.createRadialGradient(x, y, 0, x, y, r);
      g.addColorStop(0, 'rgba(255,245,200,0.30)'); g.addColorStop(1, 'rgba(255,245,200,0)');
      ctx.fillStyle = g; ctx.globalAlpha = 1; ctx.fillRect(x-r, y-r, r*2, r*2);
    }
    ctx.globalAlpha = 1;
    const t = new THREE.CanvasTexture(canvas); t.wrapS = t.wrapT = THREE.RepeatWrapping;
    return { texture: t, roughness: 0.22, metalness: 0.85, repeat: [1.5, 1.5] };
  }

  // ── EXPOSED CONCRETE SLAB ──────────────────────────────────────────────────
  // Raw concrete soffit: soft blotches + fine grain + faint form-panel seams.
  if (mat.id === 'concrete_ceil') {
    for (let i = 0; i < 650; i++) { ctx.fillStyle = `rgba(0,0,0,${Math.random() * 0.03})`; ctx.fillRect(Math.random() * S, Math.random() * S, 16 + Math.random() * 46, 16 + Math.random() * 46); }
    for (let i = 0; i < 320; i++) { ctx.fillStyle = `rgba(255,255,255,${Math.random() * 0.02})`; ctx.fillRect(Math.random() * S, Math.random() * S, 10 + Math.random() * 26, 10 + Math.random() * 26); }
    for (let i = 0; i < 5000; i++) { const v = (Math.random() - 0.5) * 10; ctx.fillStyle = rgbStr(br + v, bg + v, bb + v); ctx.globalAlpha = 0.05; ctx.fillRect(Math.random() * S, Math.random() * S, 1, 1); }
    ctx.globalAlpha = 1;
    ctx.strokeStyle = 'rgba(0,0,0,0.06)'; ctx.lineWidth = 1.2;
    for (let x = S / 3; x < S; x += S / 3) { ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, S); ctx.stroke(); }
    for (let y = S / 3; y < S; y += S / 3) { ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(S, y); ctx.stroke(); }
    const t = new THREE.CanvasTexture(canvas); t.wrapS = t.wrapT = THREE.RepeatWrapping;
    return { texture: t, roughness: 0.92, metalness: 0, repeat: [1.5, 1.5] };
  }

  // ── MICROCEMENT CEILING ────────────────────────────────────────────────────
  if (mat.id === 'microcement_c') {
    for (let i = 0; i < 55; i++) { const x = Math.random() * S, y = Math.random() * S, w = 60 + Math.random() * 120, h = 40 + Math.random() * 80, v = (Math.random() - 0.5) * 12; const grd = ctx.createLinearGradient(x, y, x + w, y + h); grd.addColorStop(0, `rgba(${br + v},${bg + v},${bb + v},0.12)`); grd.addColorStop(1, `rgba(${br - v},${bg - v},${bb - v},0.08)`); ctx.fillStyle = grd; ctx.beginPath(); ctx.ellipse(x + w / 2, y + h / 2, w / 2, h / 2, Math.random() * Math.PI, 0, Math.PI * 2); ctx.fill(); }
    for (let i = 0; i < 5000; i++) { const v = (Math.random() - 0.5) * 8; ctx.fillStyle = rgbStr(br + v, bg + v, bb + v); ctx.globalAlpha = 0.04; ctx.fillRect(Math.random() * S, Math.random() * S, 1, 1); }
    ctx.globalAlpha = 1;
    const t = new THREE.CanvasTexture(canvas); t.wrapS = t.wrapT = THREE.RepeatWrapping;
    return { texture: t, roughness: 0.40, metalness: 0.02, repeat: [1.2, 1.2] };
  }

  // ── TADELAKT CEILING ───────────────────────────────────────────────────────
  if (mat.id === 'tadelakt_c') {
    for (let i = 0; i < 80; i++) { const x = Math.random() * S, y = Math.random() * S, rr = 50 + Math.random() * 130, v = (Math.random() - 0.5) * 15; const grd = ctx.createRadialGradient(x, y, 0, x, y, rr); grd.addColorStop(0, `rgba(${br + v | 0},${bg + v | 0},${bb + v | 0},0.15)`); grd.addColorStop(1, 'rgba(0,0,0,0)'); ctx.fillStyle = grd; ctx.fillRect(x - rr, y - rr, rr * 2, rr * 2); }
    for (let i = 0; i < 40; i++) { ctx.strokeStyle = jitter(base, 10); ctx.globalAlpha = 0.05 + Math.random() * 0.05; ctx.lineWidth = 8 + Math.random() * 20; const x0 = Math.random() * S, y0 = Math.random() * S, ang = (Math.random() - 0.5) * 1.3, len = 80 + Math.random() * 150; ctx.beginPath(); ctx.moveTo(x0, y0); ctx.quadraticCurveTo(x0 + Math.cos(ang + 0.2) * len * 0.5, y0 + Math.sin(ang + 0.2) * len * 0.5, x0 + Math.cos(ang) * len, y0 + Math.sin(ang) * len); ctx.stroke(); }
    ctx.globalAlpha = 1;
    for (let i = 0; i < 8; i++) { const rx = Math.random() * S, ry = Math.random() * S, rr = 40 + Math.random() * 80; const g = ctx.createRadialGradient(rx, ry, 0, rx, ry, rr); g.addColorStop(0, 'rgba(255,255,255,0.06)'); g.addColorStop(1, 'rgba(255,255,255,0)'); ctx.fillStyle = g; ctx.fillRect(rx - rr, ry - rr, rr * 2, rr * 2); }
    const t = new THREE.CanvasTexture(canvas); t.wrapS = t.wrapT = THREE.RepeatWrapping;
    return { texture: t, roughness: 0.26, metalness: 0.03, repeat: [1, 1] };
  }

  // ── FLUTED PANEL CEILING ───────────────────────────────────────────────────
  if (mat.id === 'fluted_c') {
    const fw = 18;
    for (let x = 0; x < S; x += fw) { const rg = ctx.createLinearGradient(x, 0, x + fw, 0); rg.addColorStop(0, 'rgba(255,255,255,0.20)'); rg.addColorStop(0.35, 'rgba(255,255,255,0.04)'); rg.addColorStop(0.65, 'rgba(0,0,0,0.04)'); rg.addColorStop(1, 'rgba(0,0,0,0.20)'); ctx.fillStyle = rg; ctx.fillRect(x, 0, fw, S); ctx.fillStyle = 'rgba(0,0,0,0.26)'; ctx.fillRect(x + fw - 2, 0, 2, S); ctx.fillStyle = 'rgba(255,255,255,0.18)'; ctx.fillRect(x, 0, 2, S); }
    const t = new THREE.CanvasTexture(canvas); t.wrapS = t.wrapT = THREE.RepeatWrapping;
    return { texture: t, roughness: 0.55, metalness: 0, repeat: [0.5, 1] };
  }

  // ── HERRINGBONE WOOD CEILING ───────────────────────────────────────────────
  if (mat.id === 'herring_c') {
    const bw = 26, bh = 13, gc = mat.grainColor || '#8a5c38';
    ctx.fillStyle = base; ctx.fillRect(0, 0, S, S);
    for (let y = -S; y < S * 2; y += bh * 4) { for (let x = -S; x < S * 2; x += bw * 2) { for (const [tx, ty, rot] of [[x + bw * .5, y + bh * .5, -Math.PI / 4], [x + bw * 1.5, y + bh * 2.5, Math.PI / 4]]) { ctx.fillStyle = jitter(base, 16); ctx.save(); ctx.translate(tx, ty); ctx.rotate(rot); ctx.fillRect(-bw / 2, -bh / 2, bw - 1, bh - 1); ctx.fillStyle = 'rgba(255,255,255,0.14)'; ctx.fillRect(-bw / 2, -bh / 2, bw - 1, 1.5); ctx.fillStyle = 'rgba(0,0,0,0.16)'; ctx.fillRect(-bw / 2, bh / 2 - 2, bw - 1, 1.5); ctx.restore(); } } }
    ctx.strokeStyle = gc; for (let j = 0; j < 50; j++) { const y = (j / 50) * S; ctx.globalAlpha = 0.05; ctx.lineWidth = 0.4; ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(S, y + (Math.random() - 0.5) * 8); ctx.stroke(); }
    ctx.globalAlpha = 1;
    const t = new THREE.CanvasTexture(canvas); t.wrapS = t.wrapT = THREE.RepeatWrapping;
    return { texture: t, roughness: 0.62, metalness: 0, repeat: [2, 2] };
  }

  // ── BAMBOO / REED CEILING ──────────────────────────────────────────────────
  if (mat.id === 'bamboo_c') {
    const sh = 18, gc = mat.grainColor || '#c0aa50';
    for (let r = 0; r < S / sh + 1; r++) { ctx.fillStyle = jitter(base, 12); ctx.fillRect(0, r * sh, S, sh - 2); ctx.fillStyle = 'rgba(255,255,255,0.18)'; ctx.fillRect(0, r * sh, S, 2); ctx.fillStyle = gc; ctx.globalAlpha = 0.32; ctx.fillRect(0, r * sh + sh - 2, S, 2); ctx.globalAlpha = 1; }
    ctx.strokeStyle = gc; for (let x = 55; x < S; x += 55 + Math.random() * 25) { ctx.globalAlpha = 0.14; ctx.lineWidth = 1.2; ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, S); ctx.stroke(); }
    ctx.globalAlpha = 1;
    const t = new THREE.CanvasTexture(canvas); t.wrapS = t.wrapT = THREE.RepeatWrapping;
    return { texture: t, roughness: 0.72, metalness: 0, repeat: [2, 2] };
  }

  return null;
}
